import React, { useState, useEffect, useRef } from "react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";
import { saveProfile as dbSaveProfile, loadProfile, saveImpression as dbSaveImpression, loadImpressions, deleteImpression as dbDeleteImpression, updateImpression as dbUpdateImpression, saveConnection, loadConnections, saveScan, createMenuScan, insertMenuItems, saveRanking, loadDrinkFlavorsBatch, saveDrinkFlavor, normalizeDrinkKey, loadFoodFlavor, saveFoodFlavor, writeSelectionSignal, writeSessionEvent, writeInteractionEvent, getUserId, uploadProfilePhoto } from './supabaseStorage';
import DRINK_FLAVOR_SEEDS from './drinkFlavorSeeds';
import { computeExtractionHash } from './extractionHash';
import { pairingFit, NEUTRAL_FOOD_VECTOR } from './foodFlavor';
import supabase from './supabaseStorage';
import logoImage from './Assets/logo.png';

const C = {
  cream:"#F5F0E4", linen:"#FAF7F0", sand:"#EDE0CC",
  aqua:"#00B4C8", aquaD:"#008FA0",
  coral:"#CC2200", gold:"#E8B84B",
  ink:"#1C1F26", mist:"#A8BFC2",
  white:"#FFFFFF", border:"#EDE8DF",
};

const WINE_DB = [
  {id:"w01",name:"Sancerre",grape:"Sauvignon Blanc",region:"Loire Valley",style:"crisp",body:"light",price:22,aromas:["citrus","mineral","white flowers","gooseberry","flint"],foods:["oysters","goat cheese","asparagus","sushi","salad"],desc:"Razor-sharp and mineral. The benchmark Sauvignon Blanc."},
  {id:"w02",name:"Pouilly-Fumé",grape:"Sauvignon Blanc",region:"Loire Valley",style:"crisp",body:"light",price:20,aromas:["smoky flint","citrus","white peach","elderflower"],foods:["shellfish","smoked salmon","soft cheese","light pasta"],desc:"Smoky and electric. Flint and citrus in perfect tension."},
  {id:"w03",name:"Grüner Veltliner",grape:"Grüner Veltliner",region:"Wachau, Austria",style:"crisp",body:"medium",price:18,aromas:["white pepper","lime","herbs","green apple"],foods:["schnitzel","asparagus","fish","vegetables"],desc:"Austria's great white: peppery, mineral, endlessly food-friendly."},
  {id:"w04",name:"Albariño",grape:"Albariño",region:"Rías Baixas, Spain",style:"crisp",body:"light",price:15,aromas:["peach","apricot","citrus","floral","saline"],foods:["seafood","paella","mussels","octopus","ceviche"],desc:"Spain's greatest white. Ocean air and stone fruit."},
  {id:"w05",name:"White Burgundy",grape:"Chardonnay",region:"Mâcon, Burgundy",style:"medium",body:"medium",price:16,aromas:["apple","butter","vanilla","toast","hazelnut"],foods:["roast chicken","lobster","cream sauces","mushroom risotto"],desc:"Burgundy Chardonnay at its most accessible."},
  {id:"w06",name:"Riesling Spätlese",grape:"Riesling",region:"Mosel, Germany",style:"off-dry",body:"light",price:17,aromas:["petrol","peach","apricot","slate","lime blossom"],foods:["spicy thai","indian","pork","duck","blue cheese"],desc:"Off-dry magic. Sweet but electric with acidity."},
  {id:"w07",name:"Albariño Martin Codax",grape:"Albariño",region:"Rías Baixas, Spain",style:"crisp",body:"light",price:15,aromas:["peach","apricot","citrus","saline","white flowers"],foods:["seafood","paella","mussels","ceviche"],desc:"Spain's ocean-kissed white. Pure and vibrant."},
  {id:"w08",name:"Chablis Premier Cru",grape:"Chardonnay",region:"Chablis, Burgundy",style:"crisp",body:"medium",price:24,aromas:["oyster shell","lemon","green apple","chalk","flint"],foods:["oysters","shellfish","sushi","mild fish"],desc:"Pure mineral tension. The world's most food-friendly Chardonnay."},
  {id:"w09",name:"Gewürztraminer",grape:"Gewürztraminer",region:"Alsace, France",style:"off-dry",body:"medium",price:22,aromas:["lychee","rose petal","ginger","peach","baking spice"],foods:["spicy thai","indian curry","Chinese duck","pork belly","soft cheese"],desc:"The most dramatic of all white grapes. Lychee, rose, and spice."},
  {id:"w10",name:"Viognier",grape:"Viognier",region:"Condrieu, Rhône Valley",style:"medium",body:"full",price:35,aromas:["apricot","peach blossom","honeysuckle","violet","vanilla"],foods:["lobster","scallops","roast chicken","Thai green curry"],desc:"The most perfumed white in the world. Rich and intoxicating."},
  {id:"w11",name:"Pinot Grigio",grape:"Pinot Grigio",region:"Friuli, Italy",style:"crisp",body:"light",price:16,aromas:["green apple","lemon","almond","pear","white flowers"],foods:["seafood","light pasta","bruschetta","prosciutto","salads"],desc:"Italy's most popular white at its finest. Crisp and clean."},
  {id:"w12",name:"Moscato d'Asti",grape:"Moscato",region:"Piedmont, Italy",style:"off-dry",body:"light",price:18,aromas:["peach","apricot","orange blossom","honey","white flowers"],foods:["fruit tarts","strawberries","light desserts","biscotti"],desc:"Italy's most joyful wine. Gently sparkling and barely sweet."},
  {id:"w13",name:"Chenin Blanc Vouvray",grape:"Chenin Blanc",region:"Vouvray, Loire Valley",style:"off-dry",body:"medium",price:20,aromas:["apple","quince","honey","chamomile","beeswax","ginger"],foods:["roast pork","pâté","goat cheese","chicken"],desc:"One of wine's most versatile grapes. Ages magnificently."},
  {id:"w14",name:"Torrontés",grape:"Torrontés",region:"Salta, Argentina",style:"crisp",body:"light",price:15,aromas:["rose petal","lychee","peach","citrus","lavender"],foods:["seafood ceviche","grilled fish","spicy Asian food","mild cheese"],desc:"Argentina's unique aromatic white. Explosively floral but bone dry."},
  {id:"w15",name:"Marlborough Sauvignon Blanc",grape:"Sauvignon Blanc",region:"Marlborough, New Zealand",style:"crisp",body:"light",price:18,aromas:["passion fruit","grapefruit","cut grass","gooseberry","kiwi"],foods:["oysters","grilled fish","goat cheese","asparagus"],desc:"The benchmark for New World Sauvignon Blanc."},
  {id:"w16",name:"Meursault",grape:"Chardonnay",region:"Meursault, Burgundy",style:"medium",body:"full",price:45,aromas:["hazelnut","butter","toasted oak","white peach","cream"],foods:["roast chicken","lobster","grilled fish","cream sauces"],desc:"The pinnacle of oaked Burgundy Chardonnay. Hazelnut and butter."},
  {id:"w17",name:"Rombauer Chardonnay",grape:"Chardonnay",region:"Carneros, California",style:"medium",body:"full",price:30,aromas:["ripe apple","lemon","vanilla","light oak","cream","white peach"],foods:["lobster","crab","grilled fish","cream sauces","roast chicken"],desc:"California's most beloved Chardonnay. Rich yet refreshing."},
  {id:"w18",name:"Kim Crawford Sauvignon Blanc",grape:"Sauvignon Blanc",region:"Marlborough, New Zealand",style:"crisp",body:"light",price:18,aromas:["passion fruit","grapefruit","cut grass","lemon","kiwi"],foods:["oysters","grilled fish","goat cheese","green salads"],desc:"New Zealand's most iconic export. Electric passion fruit."},
  {id:"w19",name:"Sonoma-Cutrer Chardonnay",grape:"Chardonnay",region:"Russian River Valley, California",style:"medium",body:"full",price:28,aromas:["ripe apple","lemon citrus","vanilla","light oak","cream"],foods:["lobster","crab","grilled fish","cream sauces","roast chicken"],desc:"Sonoma's most consistent Chardonnay. Crisp meets rich."},
  {id:"r01",name:"Provence Rosé",grape:"Grenache/Cinsault",region:"Provence, France",style:"crisp",body:"light",price:18,aromas:["strawberry","rose petals","peach","dried herbs"],foods:["salad niçoise","grilled fish","charcuterie","pizza"],desc:"The ur-rosé. Pale, dry, and impossibly chic."},
  {id:"r02",name:"Bandol Rosé",grape:"Mourvèdre",region:"Bandol, Provence",style:"medium",body:"medium",price:24,aromas:["red cherry","peach","herbs","mineral"],foods:["lamb","bouillabaisse","grilled meats","cheese"],desc:"The aristocrat of rosé. Structure and substance."},
  {id:"r03",name:"Whispering Angel Rosé",grape:"Grenache/Vermentino/Rolle",region:"Provence, France",style:"crisp",body:"light",price:25,aromas:["strawberry","grapefruit","rose petals","white peach","mineral"],foods:["salad niçoise","grilled fish","sushi","light tapas"],desc:"The most recognisable Provence Rosé. Pale, bone-dry, elegant."},
  {id:"r04",name:"Miraval Rosé",grape:"Grenache/Cinsault/Syrah",region:"Provence, France",style:"crisp",body:"light",price:22,aromas:["peach","white flowers","grapefruit","strawberry","mineral"],foods:["grilled fish","sushi","light salads","Mediterranean dishes"],desc:"The celebrity Provence Rosé — and genuinely excellent."},
  {id:"r05",name:"Sancerre Rosé",grape:"Pinot Noir",region:"Loire Valley, France",style:"crisp",body:"light",price:28,aromas:["red cherry","raspberry","mineral","citrus","herbs","rose"],foods:["grilled fish","charcuterie","goat cheese","salads"],desc:"The most sophisticated rosé in France. Mineral and elegant."},
  {id:"rd01",name:"Barolo",grape:"Nebbiolo",region:"Piedmont, Italy",style:"bold",body:"full",price:32,aromas:["tar","roses","cherry","tobacco","licorice"],foods:["beef","lamb","truffles","aged cheese","mushroom"],desc:"The King of Italian wine. Needs time; rewards patience."},
  {id:"rd02",name:"Barossa Shiraz",grape:"Shiraz",region:"Barossa Valley, Australia",style:"bold",body:"full",price:22,aromas:["blackberry","plum","chocolate","coffee","leather"],foods:["barbecue","beef","lamb","game","aged cheddar"],desc:"Full-throttle Australian Shiraz. Opulent and unabashed."},
  {id:"rd03",name:"Rioja Crianza",grape:"Tempranillo",region:"Rioja, Spain",style:"medium",body:"medium",price:17,aromas:["red fruit","vanilla","tobacco","leather","oak"],foods:["lamb","pork","tapas","manchego","chorizo"],desc:"Spain's reliable ambassador. Elegant oak-aged Tempranillo."},
  {id:"rd04",name:"Pinot Noir, Willamette",grape:"Pinot Noir",region:"Oregon, USA",style:"medium",body:"medium",price:28,aromas:["cherry","raspberry","earth","forest floor","cola"],foods:["salmon","duck","pork","mushrooms","brie"],desc:"Oregon's crown jewel. Silk and earth in perfect dialogue."},
  {id:"rd05",name:"Côtes du Rhône",grape:"GSM Blend",region:"Rhône Valley",style:"medium",body:"medium",price:14,aromas:["red berries","pepper","garrigue","lavender"],foods:["pork","chicken","lamb","pizza","grilled veggies"],desc:"The perfect bistro red. Grenache-led, spicy, accessible."},
  {id:"rd06",name:"Malbec Reserva",grape:"Malbec",region:"Mendoza, Argentina",style:"bold",body:"full",price:18,aromas:["plum","violet","dark chocolate","coffee"],foods:["beef","steak","lamb","barbecue","blue cheese"],desc:"Argentina's international star. Plummy and velvety."},
  {id:"rd07",name:"Chianti Classico",grape:"Sangiovese",region:"Tuscany, Italy",style:"medium",body:"medium",price:20,aromas:["sour cherry","leather","dried herbs","tobacco"],foods:["pizza","pasta","tomato sauces","prosciutto","lasagna"],desc:"The soul of Tuscany. Made for the table."},
  {id:"rd08",name:"Châteauneuf-du-Pape",grape:"Grenache/Syrah/Mourvèdre",region:"Rhône Valley, France",style:"bold",body:"full",price:45,aromas:["black cherry","leather","lavender","garrigue","dark plum"],foods:["lamb","game","duck","beef","aged cheese"],desc:"The grandest wine of the Southern Rhône."},
  {id:"rd09",name:"Caymus Napa Cabernet",grape:"Cabernet Sauvignon",region:"Rutherford, Napa Valley",style:"bold",body:"full",price:80,aromas:["blackberry","cassis","dark cherry","cocoa","vanilla","mocha"],foods:["grilled ribeye","lamb chops","beef tenderloin","aged cheddar"],desc:"California's most consistent Cab icon since 1972."},
  {id:"rd10",name:"Silver Oak Alexander Valley Cab",grape:"Cabernet Sauvignon",region:"Alexander Valley, Sonoma",style:"bold",body:"full",price:65,aromas:["dark cherry","blackberry","cedarwood","vanilla","fennel"],foods:["grilled ribeye","roasted lamb","hearty stews","aged hard cheese"],desc:"A restaurant list staple for 50 years. Cedar, dark cherry, graphite."},
  {id:"rd11",name:"Jordan Cabernet Sauvignon",grape:"Cabernet Sauvignon",region:"Alexander Valley, Sonoma",style:"bold",body:"full",price:55,aromas:["black cherry","cassis","tobacco","cedar","herbs","vanilla"],foods:["roast lamb","beef","venison","aged cheese","mushroom dishes"],desc:"The Alexander Valley benchmark for four decades."},
  {id:"rd12",name:"Duckhorn Napa Merlot",grape:"Merlot",region:"Napa Valley, California",style:"bold",body:"full",price:55,aromas:["black cherry","plum","cocoa","mocha","cedar","herbs"],foods:["roast duck","lamb","beef","mushroom risotto","aged cheese"],desc:"The wine that saved Merlot's reputation."},
  {id:"rd13",name:"The Prisoner Red Blend",grape:"Red Blend",region:"Napa Valley, California",style:"bold",body:"full",price:45,aromas:["dark fruit","blackberry","leather","vanilla","plum","spice"],foods:["braised lamb","BBQ ribs","hearty pasta","aged gouda"],desc:"The cult Napa Red Blend. Luscious and irresistible."},
  {id:"rd14",name:"Pinot Noir Sonoma Coast",grape:"Pinot Noir",region:"Sonoma Coast, California",style:"medium",body:"medium",price:40,aromas:["red cherry","earth","ocean breeze","raspberry","herbs","cola"],foods:["salmon","duck","pork","mushrooms","aged brie"],desc:"Wind-swept coastal Pinot at its most elegant."},
  {id:"rd15",name:"Paso Robles Zinfandel",grape:"Zinfandel",region:"Paso Robles, California",style:"bold",body:"full",price:22,aromas:["blackberry jam","black pepper","vanilla","dark cherry","chocolate"],foods:["barbecue","pizza","grilled meats","aged cheddar"],desc:"Old vine Paso Robles Zinfandel. Jammy, peppery, generous."},
  {id:"rd16",name:"Catena Alta Malbec",grape:"Malbec",region:"Mendoza, Argentina",style:"bold",body:"full",price:35,aromas:["dark plum","violet","dark chocolate","tobacco","mineral"],foods:["beef asado","grilled lamb","venison","aged cheese"],desc:"Argentina's high-altitude icon. Dark plum and violet intensity."},
  {id:"rd17",name:"Don Melchor Cabernet",grape:"Cabernet Sauvignon",region:"Maipo Valley, Chile",style:"bold",body:"full",price:95,aromas:["blackcurrant","cedar","tobacco","dark cherry","graphite"],foods:["beef","lamb","venison","aged cheese","truffle"],desc:"Chile's greatest Cab. Rivals Napa with concentration and cedar."},
  {id:"rd18",name:"Ribera del Duero Reserva",grape:"Tempranillo",region:"Ribera del Duero, Spain",style:"bold",body:"full",price:35,aromas:["dark cherry","vanilla","tobacco","leather","cedar","plum"],foods:["roast lamb","suckling pig","beef","aged manchego","game"],desc:"Spain's most powerful Tempranillo. Dark cherry and structured tannins."},
  {id:"rd19",name:"Priorat Garnacha",grape:"Garnacha/Cariñena",region:"Priorat, Catalonia, Spain",style:"bold",body:"full",price:55,aromas:["black cherry","mineral","licorice","dark fruit","earth","violet"],foods:["roast lamb","venison","beef","aged cheese"],desc:"One of Spain's two DOCa wines. Extraordinary mineral concentration."},
  {id:"rd20",name:"Amarone della Valpolicella",grape:"Corvina/Molinara/Rondinella",region:"Veneto, Italy",style:"bold",body:"full",price:65,aromas:["dried cherry","chocolate","tobacco","leather","fig","spice"],foods:["game","braised beef","aged cheese","venison","truffles"],desc:"Made from dried grapes. Dense, powerful, long-lived."},
  {id:"s01",name:"Champagne Brut NV",grape:"Chardonnay/Pinot Noir",region:"Champagne, France",style:"crisp",body:"light",price:35,aromas:["brioche","citrus","green apple","chalk","cream"],foods:["oysters","fried chicken","sushi","caviar"],desc:"The world's greatest aperitif. Occasion or no occasion."},
  {id:"s02",name:"Prosecco DOC",grape:"Glera",region:"Veneto, Italy",style:"off-dry",body:"light",price:12,aromas:["pear","apple","peach","flowers","cream"],foods:["aperitivo","prosciutto","melon","bruschetta"],desc:"Italy's answer to every aperitivo moment."},
  {id:"s03",name:"Crémant d'Alsace",grape:"Pinot Blanc/Auxerrois",region:"Alsace, France",style:"crisp",body:"light",price:16,aromas:["apple","pear","flowers","bread","lemon"],foods:["smoked salmon","foie gras","light starters","oysters"],desc:"Champagne's elegant cousin at a fraction of the price."},
  {id:"s04",name:"Blanc de Blancs Champagne",grape:"Chardonnay",region:"Côte des Blancs, Champagne",style:"crisp",body:"light",price:55,aromas:["lemon curd","green apple","chalk","cream","brioche","oyster shell"],foods:["oysters","caviar","sushi","grilled fish"],desc:"Pure Chardonnay Champagne. The purest expression of chalk minerality."},
  {id:"s05",name:"Schramsberg Blanc de Blancs",grape:"Chardonnay",region:"North Coast, California",style:"crisp",body:"light",price:45,aromas:["green apple","chalk","brioche","lemon","cream"],foods:["oysters","light starters","smoked salmon","sushi"],desc:"America's most celebrated sparkling wine. Served at White House state dinners."},
  {id:"s06",name:"Domaine Carneros Sparkling",grape:"Pinot Noir/Chardonnay",region:"Carneros, California",style:"crisp",body:"light",price:35,aromas:["cherry","lemon","brioche","cream","mineral"],foods:["oysters","light appetizers","sushi","celebration starters"],desc:"Taittinger's California château. The most Champagne-like setting in Napa."},
  {id:"s07",name:"Pét-Nat",grape:"Various",region:"Loire Valley, France",style:"off-dry",body:"light",price:22,aromas:["apple","pear","yeast","lemon","herbs","wild fruit"],foods:["oysters","charcuterie","light starters","soft cheese"],desc:"The original sparkling wine. Wild, lively, and full of character."},
  {id:"s08",name:"Lambrusco Secco",grape:"Lambrusco",region:"Emilia-Romagna, Italy",style:"crisp",body:"light",price:16,aromas:["cherry","raspberry","violet","herbs","earth","dark fruit"],foods:["charcuterie","pizza","piadina","aged parmesan","grilled meats"],desc:"Forget the sweet version — dry Lambrusco is a revelation."},
];

const FOOD_QUICK = ["Oysters","Salmon","Beef","Lamb","Pasta","Risotto","Cheese","Sushi","Charcuterie","Chicken","Duck","Seafood"];
const STYLE_OPTS = ["Crisp & Fresh","Medium & Balanced","Bold & Rich","Sparkling","Rosé"];

const EVENTS = [
  {id:"e1",title:"Blind Tasting & Mingle",venue:"Osteria Morini",city:"New York",date:"Apr 19, 2026",time:"7:00 PM",price:65,spots:4,maxSpots:14,theme:"Italian Reds",wines:["Barolo","Chianti Classico","Bandol Rosé","Champagne Brut NV"]},
  {id:"e2",title:"Rosé & Rooftop",venue:"The Harbour Club",city:"Miami",date:"Apr 26, 2026",time:"6:30 PM",price:75,spots:12,maxSpots:20,theme:"Rosé & White",wines:["Provence Rosé","Bandol Rosé","Albariño","Sancerre"]},
  {id:"e3",title:"Bubbles & Bites",venue:"Maison Marcel",city:"Los Angeles",date:"May 3, 2026",time:"7:30 PM",price:80,spots:8,maxSpots:16,theme:"Sparkling",wines:["Champagne Brut NV","Chablis Premier Cru","Crémant d'Alsace","Pouilly-Fumé"]},
];

const HISTORY_INIT = [
  {id:"h1",wine:"Chablis Premier Cru",wineId:"w08",venue:"Le Bernardin",city:"New York",date:"Mar 15, 2026",with:"Solo",rating:5,food:"Oysters",notes:"Razor mineral, the cleanest thing I've tasted this year."},
  {id:"h2",wine:"Sancerre",wineId:"w01",venue:"Frenchette",city:"New York",date:"Mar 8, 2026",with:"Isabelle M.",rating:5,food:"Goat cheese salad",notes:"Couldn't stop refilling. She ordered it too."},
  {id:"h3",wine:"Pinot Noir, Willamette",wineId:"rd04",venue:"Ox & Finch",city:"Glasgow",date:"Feb 20, 2026",with:"Business dinner",rating:4,food:"Salmon",notes:"Silky, earthy. Held the table."},
  {id:"h4",wine:"Provence Rosé",wineId:"r01",venue:"The Harbour Club",city:"Miami",date:"Feb 7, 2026",with:"Weekend trip",rating:4,food:"Tuna tartare",notes:"Sunset view, cold glass. Perfect."},
  {id:"h5",wine:"Barolo",wineId:"rd01",venue:"Maialino",city:"New York",date:"Jan 28, 2026",with:"James K.",rating:5,food:"Braised beef",notes:"The king. Every sip felt like an event."},
];

const PROFILES = [
  {id:"p1",name:"Isabelle",age:29,city:"New York",job:"Architect",photo:"👩🏻",winePersonality:"The Mineralist",favoriteWine:"Chablis Premier Cru",lastPour:"Sancerre at Frenchette",
    prompts:[{q:"My wine must-have is",a:"A glass of something crisp before I even look at the menu."},{q:"I'll know we're compatible if",a:"You order white without being asked if you want red."},{q:"The wine that changed me",a:"A 2018 Meursault in Lyon. I cried a little."}],
    aromas:["mineral","citrus","floral"],compatibility:92,
    chatHistory:[
      {from:"them",text:"I saw we both love Chablis. What draws you to the mineral side?",time:"2h ago"},
      {from:"me",text:"The restraint. No oak trying to impress you. Just chalk and lemon.",time:"2h ago"},
      {from:"them",text:"Exactly. Something honest about it. Have you been to Chablis itself?",time:"1h ago"},
    ]},
  {id:"p2",name:"Marco",age:33,city:"New York",job:"Sommelier",photo:"👨🏽",winePersonality:"The Earthy Romantic",favoriteWine:"Pinot Noir, Willamette",lastPour:"Bourgogne Rouge at Le Coucou",
    prompts:[{q:"My controversial wine take",a:"Prosecco is better than most Champagnes people actually buy."},{q:"My ideal wine evening",a:"Outdoor table, natural light, one bottle, two glasses, nowhere to be."},{q:"I'll know we're compatible if",a:"You ask what's in the glass before you ask the price."}],
    aromas:["earth","cherry","forest floor"],compatibility:87,chatHistory:[]},
  {id:"p3",name:"Sofia",age:31,city:"Miami",job:"Creative Director",photo:"👩🏼",winePersonality:"The Ibiza Rosé",favoriteWine:"Provence Rosé",lastPour:"Bandol Rosé at The Harbour Club",
    prompts:[{q:"My wine ritual",a:"First sip always with eyes closed. Always."},{q:"The strangest wine I loved",a:"An orange Ramato from Friuli. Looked like apple juice, tasted like time."},{q:"My wine must-have is",a:"Something pink, something cold, somewhere with a view."}],
    aromas:["strawberry","rose","herbs"],compatibility:81,chatHistory:[]},
];

function buildPalate(history) {
  const ac={},fc={},sc={};
  history.forEach(h=>{
    const w=WINE_DB.find(x=>x.id===h.wineId);
    if(!w) return;
    w.aromas.forEach(a=>{ac[a]=(ac[a]||0)+h.rating;});
    w.foods.forEach(f=>{fc[f]=(fc[f]||0)+h.rating;});
    sc[w.style]=(sc[w.style]||0)+h.rating;
  });
  return {
    topAromas:Object.entries(ac).sort((a,b)=>b[1]-a[1]).slice(0,6).map(e=>e[0]),
    topFoods:Object.entries(fc).sort((a,b)=>b[1]-a[1]).slice(0,5).map(e=>e[0]),
    preferredStyle:Object.entries(sc).sort((a,b)=>b[1]-a[1])[0]?.[0]||"medium",
    totalPours:history.length,
  };
}

function scoreWine(wine,palate,food,style) {
  let s=0;
  wine.aromas.forEach(a=>{if(palate.topAromas.includes(a))s+=3;});
  wine.foods.forEach(f=>{if(palate.topFoods.includes(f))s+=2;});
  if(wine.style===palate.preferredStyle)s+=2;
  if(food)wine.foods.forEach(f=>{if(food.toLowerCase().includes(f)||f.includes(food.toLowerCase()))s+=4;});
  if(style){
    const m={"Crisp & Fresh":"crisp","Medium & Balanced":"medium","Bold & Rich":"bold"};
    if(m[style]&&wine.style===m[style])s+=3;
    if(style==="Sparkling"&&wine.id.startsWith("s"))s+=5;
    if(style==="Rosé"&&wine.id.startsWith("r"))s+=5;
  }
  return s;
}

async function callClaude(messages,system,maxTokens=600,model="claude-sonnet-4-6") {
  const {data:{session}}=await supabase.auth.getSession();
  const r=await fetch("/api/claude",{
    method:"POST",
    headers:{
      "Content-Type":"application/json",
      Authorization:`Bearer ${session?.access_token}`,
    },
    body:JSON.stringify({model,max_tokens:maxTokens,system,messages}),
  });
  const d=await r.json();
  if(d.error){console.error("Claude API error:",d.error);throw new Error(d.error.message||d.error);}
  return d.content?.[0]?.text||"";
}
// ── PALATE QUIZ ─────────────────────────────────────────────
// Progress save/resume — bump QUIZ_VERSION whenever questions/options change so stale saves are ignored.
const QUIZ_VERSION = 2;
const QUIZ_PROGRESS_KEY = 'swishd_quiz_progress';
const QUIZ_PROGRESS_MAX_AGE = 14 * 24 * 60 * 60 * 1000;
function saveQuizProgress(step, selections) {
  try { localStorage.setItem(QUIZ_PROGRESS_KEY, JSON.stringify({ v: QUIZ_VERSION, step, selections, savedAt: Date.now() })); }
  catch(e) { console.warn('[swishd] quiz progress save failed', e); }
}
function clearQuizProgress() {
  try { localStorage.removeItem(QUIZ_PROGRESS_KEY); }
  catch(e) { console.warn('[swishd] quiz progress clear failed', e); }
}
// Returns saved progress only if it's current-version, fresh, past Q1, and every index fits its question.
function loadQuizProgress() {
  try {
    const p = JSON.parse(localStorage.getItem(QUIZ_PROGRESS_KEY));
    if (!p || p.v !== QUIZ_VERSION || typeof p.savedAt !== 'number' || Date.now() - p.savedAt >= QUIZ_PROGRESS_MAX_AGE) return null;
    if (!Number.isInteger(p.step) || p.step <= 0 || p.step >= QUIZ_QUESTIONS.length) return null;
    if (!p.selections || typeof p.selections !== 'object' || Array.isArray(p.selections)) return null;
    const valid = Object.entries(p.selections).every(([qId, idxs]) => {
      const q = QUIZ_QUESTIONS.find(q => String(q.id) === qId);
      return q && Array.isArray(idxs) && idxs.every(i => Number.isInteger(i) && i >= 0 && i < q.options.length);
    });
    return valid ? p : null;
  } catch(e) { return null; }
}
const QUIZ_QUESTIONS = [
  {
    id: 1,
    text: "How do you take your coffee?",
    options: [
      { label: "Black, no sugar", emoji: "☕", score: { bitter:2, bold:1, dry:1 } },
      { label: "Black with cream, no sugar", emoji: "🥛", score: { bitter:1, rich:1 } },
      { label: "A little sugar, no cream", emoji: "🍶", score: { sweet:1, bitter:1 } },
      { label: "Cream and sugar", emoji: "☕", score: { sweet:2, rich:1 }, personality: { warm:1 } },
      { label: "Sweet and creamy — the more the better", emoji: "🍮", score: { sweet:3, rich:1 }, personality: { warm:1 } },
      { label: "I'm a tea person", emoji: "🍵", score: { herbal:1 }, personality: { calm:1 } },
    ],
  },
  {
    id: 2,
    multi: true,
    subtext: "Select all that apply",
    text: "Pick the snacks you'd actually reach for:",
    options: [
      { label: "Dark chocolate (70%+)", emoji: "🍫", score: { bitter:2, rich:1 } },
      { label: "Grapefruit", emoji: "🍊", score: { acidic:2, bitter:1 } },
      { label: "Black olives", emoji: "🫒", score: { herbal:1, dry:1, smoky:1 } },
      { label: "Salt & vinegar chips", emoji: "🥔", score: { acidic:1, dry:1 } },
      { label: "Milk chocolate", emoji: "🍫", score: { sweet:2, rich:1 }, personality: { warm:1 } },
      { label: "Sweet gummy candy", emoji: "🍬", score: { sweet:3, fruity:1 } },
    ],
  },
  {
    id: 3,
    text: "Spicy food — where do you land?",
    options: [
      { label: "Can't handle heat", emoji: "😮‍💨", score: { light:2 }, personality: { calm:1 } },
      { label: "Mild is my limit", emoji: "🌶️", score: { light:1 } },
      { label: "I like a kick", emoji: "🌶️", score: { bold:1, smoky:1 } },
      { label: "Bring the fire", emoji: "🔥", score: { bold:2, smoky:1 } },
    ],
  },
  {
    id: 4,
    text: "How sweet do you like your drinks?",
    options: [
      { label: "Very sweet", emoji: "🍭", score: { sweet:4, fruity:1 }, sweetTier: "very" },
      { label: "A little sweet", emoji: "🍑", score: { sweet:2 }, sweetTier: "little" },
      { label: "Barely sweet", emoji: "🍏", score: { dry:1, acidic:1 }, sweetTier: "barely" },
      { label: "Not sweet at all", emoji: "☕", score: { dry:2, bitter:1 }, sweetTier: "none" },
    ],
  },
  {
    id: 5,
    multi: true,
    subtext: "Select all that apply",
    text: "When you're out, what do you usually order?",
    options: [
      { label: "Cocktail", emoji: "🍸", score: { bold:1, sweet:1 } },
      { label: "Beer", emoji: "🍺", score: { bitter:1, bold:1 } },
      { label: "Wine", emoji: "🍷", score: { dry:1, acidic:1 } },
      { label: "Something fruity/tropical", emoji: "🍹", score: { sweet:2, fruity:2 } },
      { label: "A shot", emoji: "🥃", score: { bold:2 }, personality: { daring:1 } },
      { label: "Whatever's easy — I'm not picky", emoji: "🤷", score: { light:1 }, exclusive: true },
    ],
  },
  {
    id: 6,
    text: "How do you feel about sour or tart flavors — think lemon juice, green apple, or a tangy vinaigrette:",
    options: [
      { label: "Love it — bright and tart is my thing", emoji: "🍋", score: { acidic:3 } },
      { label: "A little tartness adds life to a drink", emoji: "🌊", score: { acidic:1, light:1 } },
      { label: "I prefer smooth and mellow over sharp", emoji: "🧈", score: { rich:1, sweet:1 } },
      { label: "Hard pass — too mouth-puckering for me", emoji: "🚫", score: { sweet:1, rich:1 } },
    ],
  },
  {
    id: 7,
    text: "You found a drink you genuinely love. What happens next?",
    options: [
      { label: "I order it every time — why mess with a good thing", emoji: "🔁", score: {}, personality: { calm:1 } },
      { label: "I'll have it again, but I like mixing it up too", emoji: "🙂", score: {}, personality: { open:1 } },
      { label: "I already want to try something else — variety's the fun part", emoji: "🎲", score: {}, personality: { open:2, daring:1 } },
      { label: "Finding my \"usual\" feels boring; if it's not new, I'm not that interested", emoji: "🚀", score: {}, personality: { open:3, daring:2 } },
    ],
  },
  {
    id: 8,
    text: "Your ideal drink is…",
    options: [
      { label: "Light and easy, sip all night", emoji: "🍃", score: { light:3 } },
      { label: "Somewhere in the middle", emoji: "⚖️", score: { light:1, rich:1 } },
      { label: "Rich and full, a sipper you savor", emoji: "🍷", score: { rich:3, bold:1 } },
    ],
  },
  {
    id: 9,
    multi: true,
    subtext: "Select all that apply",
    text: "A drink hits your lips — what do you WANT to taste first?",
    options: [
      { label: "Fruit", emoji: "🍇", score: { fruity:2, sweet:1 } },
      { label: "Something sweet", emoji: "🍯", score: { sweet:3 } },
      { label: "Something sharp or zingy", emoji: "⚡", score: { acidic:2 } },
      { label: "Herbs or \"green\" freshness", emoji: "🌿", score: { herbal:3, dry:1 } },
      { label: "Something smoky or toasty", emoji: "🔥", score: { smoky:2, bold:1 } },
      { label: "Something clean and simple", emoji: "💧", score: { light:2, dry:1 } },
    ],
  },
  {
    id: 10,
    multi: true,
    weight: 1.6,
    subtext: "Select all that apply",
    text: "What's the dealbreaker when a drink goes wrong?",
    options: [
      { label: "Bitter aftertaste that won't quit", emoji: "😖", score: { sweet:2, light:1 } },
      { label: "So sweet it's sickly", emoji: "🍬", score: { dry:2, bitter:1 } },
      { label: "Sour enough to make you wince", emoji: "🍋", score: { sweet:1, rich:1 } },
      { label: "Boozy burn on the way down", emoji: "🥴", score: { light:2 } },
      { label: "Feels heavy or bloating", emoji: "🫃", score: { rich:1 } },
      { label: "Something's \"off\" — can't place it", emoji: "😬", score: { light:1, dry:1 } },
      { label: "Nothing really — I'll drink almost anything", emoji: "🤷", score: {}, personality: { open:2, daring:1 }, exclusive: true },
    ],
  },
  {
    id: 11,
    weight: 0.6,
    text: "How adventurous are you with new drinks?",
    options: [
      { label: "I stick to what I know", emoji: "🏠", score: {}, personality: { calm:1 } },
      { label: "I'll try something if it sounds good", emoji: "🙂", score: {}, personality: { open:1 } },
      { label: "I love trying weird/new stuff", emoji: "🎲", score: {}, personality: { open:2, daring:1 } },
      { label: "Surprise me every time", emoji: "🎉", score: {}, personality: { open:2, daring:3 } },
    ],
  },
  {
    id: 12,
    text: "Think of the last time something tasted TOO strong for you — what was it?",
    options: [
      { label: "Coffee or dark chocolate, way too bitter", emoji: "☕", score: { sweet:2, light:1 } },
      { label: "A grapefruit or sour candy, way too sharp", emoji: "🍋", score: { sweet:1, rich:1 } },
      { label: "A spicy dish that overwhelmed everything else", emoji: "🌶️", score: { light:2 } },
      { label: "Black licorice or strong herbs, too intense", emoji: "🌿", score: { sweet:1, dry:1 } },
      { label: "Honestly, nothing's ever been \"too much\" for me", emoji: "💪", score: {}, personality: { daring:2, open:1 }, exclusive: true },
    ],
  },
  {
    id: 13,
    text: "How do you feel about strong/boozy drinks?",
    options: [
      { label: "Too much for me", emoji: "😅", score: { light:2, sweet:1 } },
      { label: "A little goes a long way", emoji: "🙂", score: { light:1 } },
      { label: "I enjoy a strong pour", emoji: "🥃", score: { bold:1, rich:1 } },
      { label: "The stronger the better", emoji: "🔥", score: { bold:2, smoky:1 } },
    ],
  },
  {
    id: 14,
    multi: true,
    weight: 0.5,
    subtext: "Select all that apply",
    text: "What actually gets you to try something new?",
    options: [
      { label: "It looks good — I'm a sucker for presentation", emoji: "✨", score: { sweet:1, fruity:1 } },
      { label: "Someone I trust recommends it", emoji: "🤝", score: {}, personality: { warm:1 } },
      { label: "It sounds strange enough to be interesting", emoji: "🌀", score: {}, personality: { open:2, daring:1 } },
      { label: "It's a proven classic, not a gimmick", emoji: "🏛️", score: { dry:1, rich:1 }, personality: { calm:1 } },
      { label: "Honestly, whatever's cheapest works", emoji: "💰", score: { light:1 } },
    ],
  },
  {
    id: 15,
    multi: true,
    subtext: "Select all that apply",
    text: "\"The perfect drink makes me feel…\"",
    options: [
      { label: "Cozy and relaxed", emoji: "🛋️", score: { rich:2 }, personality: { calm:1 }, feel: "cozy" },
      { label: "Excited and social", emoji: "🎉", score: { bold:2, sweet:1 }, feel: "excited" },
      { label: "Fancy and special", emoji: "✨", score: { dry:1, rich:1 }, feel: "fancy" },
      { label: "Adventurous and curious", emoji: "🚀", score: { smoky:2, bold:1 }, personality: { daring:3 }, feel: "adventurous" },
      { label: "Refreshed and light", emoji: "🍃", score: { light:2, acidic:1 }, feel: "refreshed" },
    ],
  },
];

const QUIZ_PROFILES = {
  sweet: {
    key: "sweet",
    title: "Sweet Indulger",
    emoji: "🍭",
    description: "You love smooth, fruity, easy-to-drink flavors. If it tastes like dessert or fruit — you're in.",
    subtypes: [
      { name: "Candy Lover", match: (s) => s.sweet >= 5 },
      { name: "Tropical Dreamer", match: (s) => s.fruity >= 3 },
      { name: "Dessert Drinker", match: () => true },
    ],
    recommendations: ["Moscato", "Riesling", "Fruit Cocktails"],
  },
  crisp: {
    key: "crisp",
    title: "Crisp Explorer",
    emoji: "🍋",
    description: "You're all about freshness and brightness. You love drinks that feel clean, zesty, and alive.",
    subtypes: [
      { name: "Citrus Seeker", match: (s) => s.acidic >= 4 },
      { name: "Sparkling Socialite", match: (s) => s.light >= 3 },
      { name: "Light Sipper", match: () => true },
    ],
    recommendations: ["Sauvignon Blanc", "Champagne", "Gin & Tonic"],
  },
  botanical: {
    key: "botanical",
    title: "Botanical Enthusiast",
    emoji: "🌿",
    description: "You're drawn to layered, aromatic flavors — herbs, florals, and complex botanicals.",
    subtypes: [
      { name: "Citrus Botanist", match: (s) => s.acidic >= 3 },
      { name: "Herbal Purist", match: (s) => s.herbal >= 5 },
      { name: "Floral Explorer", match: () => true },
    ],
    recommendations: ["Gin & Tonic", "Dry Vermouth", "Herbal Cocktails"],
  },
  bold: {
    key: "bold",
    title: "Bold Traditionalist",
    emoji: "🔥",
    description: "You appreciate strong, classic drinks with depth and character. You want flavor that makes a statement.",
    subtypes: [
      { name: "Bitter Fan", match: (s) => s.bitter >= 4 },
      { name: "Classic Connoisseur", match: (s) => s.bold >= 4 },
      { name: "Structure Seeker", match: () => true },
    ],
    recommendations: ["Cabernet Sauvignon", "Whiskey", "Negroni"],
  },
  rich: {
    key: "rich",
    title: "Rich Connoisseur",
    emoji: "🌑",
    description: "You love indulgent, deep flavors — think chocolate, coffee, and luxurious textures.",
    subtypes: [
      { name: "Chocolate Lover", match: (s) => s.rich >= 4 },
      { name: "Velvet Drinker", match: (s) => s.bold >= 2 },
      { name: "Barrel Aged Fan", match: () => true },
    ],
    recommendations: ["Port", "Bourbon", "Stout"],
  },
  adventurous: {
    key: "adventurous",
    title: "Adventurous Taster",
    emoji: "🚀",
    description: "You seek unique, bold, and sometimes wild flavor experiences. The weirder the better.",
    subtypes: [
      { name: "Smoke Chaser", match: (s) => s.smoky >= 3 },
      { name: "Flavor Hunter", match: (s) => s.bold >= 3 },
      { name: "Funk Seeker", match: () => true },
    ],
    recommendations: ["Mezcal", "Natural Wine", "Peated Scotch"],
  },
};

const QUIZ_TYPE_MAP = {
  sweet:       { red:0.2, white:0.7, sparkling:0.6, rose:0.5 },
  crisp:       { red:0.2, white:0.8, sparkling:0.9, rose:0.4 },
  botanical:   { red:0.3, white:0.6, sparkling:0.5, rose:0.3 },
  bold:        { red:0.9, white:0.2, sparkling:0.1, rose:0.2 },
  rich:        { red:0.8, white:0.3, sparkling:0.1, rose:0.2 },
  adventurous: { red:0.6, white:0.5, sparkling:0.3, rose:0.3 },
};

const QUIZ_VARIETAL_MAP = {
  sweet:       ["Moscato", "Riesling", "Gewürztraminer"],
  crisp:       ["Sauvignon Blanc", "Pinot Grigio", "Champagne"],
  botanical:   ["Grüner Veltliner", "Dry Vermouth", "Albariño"],
  bold:        ["Cabernet Sauvignon", "Syrah", "Malbec"],
  rich:        ["Merlot", "Port", "Zinfandel"],
  adventurous: ["Pét-Nat", "Mezcal", "Gamay"],
};

function quizDetermineSubtype(profileKey, scores) {
  // Subtypes aren't part of the current design (see Swishd_Quiz_Redesign_Spec.md).
  // Kept as a safe no-op so existing callers don't break.
  return "";
}

function quizToFingerprint(scores, profileKey) {
  const rawAxes = {
    sweetness: (scores.sweet||0) + (scores.fruity||0),
    body:      (scores.rich ||0) + (scores.bold ||0),
    acidity:   (scores.acidic||0) + (scores.light||0),
    tannin:    (scores.bitter||0) + (scores.dry  ||0),
  };
  const maxAxis = Math.max(...Object.values(rawAxes)) || 1;
  return {
    version: 1,
    axes: {
      sweetness: Math.min(Math.round((rawAxes.sweetness/maxAxis)*10),10),
      body:      Math.min(Math.round((rawAxes.body     /maxAxis)*10),10),
      acidity:   Math.min(Math.round((rawAxes.acidity  /maxAxis)*10),10),
      tannin:    Math.min(Math.round((rawAxes.tannin   /maxAxis)*10),10),
    },
    types:     QUIZ_TYPE_MAP[profileKey]     || QUIZ_TYPE_MAP.bold,
    varietals: QUIZ_VARIETAL_MAP[profileKey] || QUIZ_VARIETAL_MAP.bold,
    source:    "quiz",
    updated_at: new Date().toISOString(),
  };
}

// Derive running trait totals from the raw per-question selections.
// Multi-select questions AVERAGE their selected options (sum/count) so a
// question contributes ~one option's weight regardless of how many are tapped.
// Single-select questions have count 1 and are therefore unaffected.
function computeQuizScores(selections) {
  const flavor = {};
  QUIZ_QUESTIONS.forEach(q => {
    const sel = selections[q.id];
    if (!sel || sel.length === 0) return;
    const count = sel.length;
    const w = q.weight || 1;
    sel.forEach(idx => {
      const opt = q.options[idx];
      if (!opt || !opt.score) return;
      Object.keys(opt.score).forEach(k => {
        flavor[k] = (flavor[k] || 0) + (opt.score[k] / count) * w;
      });
    });
  });
  return flavor;
}

// Six-axis radar values. Same share normalization as quizDetermineProfile, then the
// highest axis is scaled to 100 so the shape always fills the chart.
function radarValuesFromScores(scores) {
  const s = scores || {};
  const total = Object.values(s).reduce((a, b) => a + b, 0) || 1;
  const sh = k => (s[k] || 0) / total;
  const axes = [
    { trait: "Sweet",  raw: sh('sweet')  * 1.0 + sh('fruity') * 0.9 },
    { trait: "Acidic", raw: sh('acidic') * 1.0 + sh('light')  * 0.55 },
    { trait: "Bitter", raw: sh('bitter') * 0.9 + sh('smoky')  * 1.2 },
    { trait: "Bold",   raw: sh('bold')   * 1.0 },
    { trait: "Rich",   raw: sh('rich')   * 1.0 },
    { trait: "Herbal", raw: sh('herbal') * 1.4 + sh('dry')    * 0.6 },
  ];
  const max = Math.max(...axes.map(a => a.raw)) || 1;
  return axes.map(a => ({ trait: a.trait, value: (a.raw / max) * 100 }));
}

// Shared radar presentation: spokes, rings, vertex dots, side-anchored labels
// (top two axes bold in brand red) and a caption.
function PalateRadar({ data, height = 240, style }) {
  const top = [...data].sort((a, b) => b.value - a.value).slice(0, 2).map(d => d.trait);
  const renderTick = ({ x, y, payload }) => {
    const rad = -(payload.coordinate || 0) * Math.PI / 180;
    const dx = Math.cos(rad), dy = Math.sin(rad), gap = 6;
    const anchor = dx > 0.1 ? "start" : dx < -0.1 ? "end" : "middle";
    const baseline = dy > 0.5 ? "hanging" : dy < -0.5 ? "auto" : "middle";
    const hi = top.includes(payload.value);
    return (
      <text x={x + dx * gap} y={y + dy * gap} textAnchor={anchor} dominantBaseline={baseline}
        style={{ fontFamily: "'Jost',sans-serif", fontSize: 13, fontWeight: hi ? 700 : 500, fill: hi ? C.coral : "#5C6668" }}>
        {payload.value}
      </text>
    );
  };
  return (
    <div style={{ width: "100%", ...style }}>
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} outerRadius="68%">
            <PolarGrid stroke="#D9CFBF" strokeWidth={1} radialLines={true}/>
            <PolarAngleAxis dataKey="trait" tick={renderTick} tickLine={false}/>
            <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false}/>
            <Radar dataKey="value" stroke={C.coral} fill={C.coral} fillOpacity={0.18}
              dot={{ r: 3, fill: C.coral, stroke: C.coral, strokeWidth: 0 }} isAnimationActive={false}/>
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, fontStyle: "italic", color: "#5C6668", textAlign: "center", marginTop: 4 }}>
        The farther out, the more you.
      </div>
    </div>
  );
}

function computeQuizPersonality(selections) {
  const personality = {};
  QUIZ_QUESTIONS.forEach(q => {
    const sel = selections[q.id];
    if (!sel || sel.length === 0) return;
    const count = sel.length;
    const w = q.weight || 1;
    sel.forEach(idx => {
      const opt = q.options[idx];
      if (!opt || !opt.personality) return;
      Object.keys(opt.personality).forEach(k => {
        personality[k] = (personality[k] || 0) + (opt.personality[k] / count) * w;
      });
    });
  });
  return personality;
}

const QUIZ_TRAIT_TO_PROFILE = {
  sweet:  { profile: 'sweet',       weight: 1.0 },
  fruity: { profile: 'sweet',       weight: 0.9 },
  acidic: { profile: 'crisp',       weight: 1.0 },
  light:  { profile: 'crisp',       weight: 0.55 },
  herbal: { profile: 'botanical',   weight: 1.4 },
  dry:    { profile: 'botanical',   weight: 0.6 },
  bold:   { profile: 'bold',        weight: 1.0 },
  bitter: { profile: 'bold',        weight: 0.9 },
  rich:   { profile: 'rich',        weight: 1.0 },
  smoky:  { profile: 'adventurous', weight: 1.2 },
};

const QUIZ_FEEL_TO_PROFILE = {
  cozy: 'rich', excited: 'sweet', fancy: 'botanical',
  adventurous: 'adventurous', refreshed: 'crisp',
};

const QUIZ_PERSONALITY_TO_PROFILE = {
  daring: 'adventurous', warm: 'sweet', calm: 'botanical', open: 'adventurous',
};

function quizDetermineProfile(selections) {
  const flavor = computeQuizScores(selections);
  const personality = computeQuizPersonality(selections);
  const total = Object.values(flavor).reduce((a, b) => a + b, 0) || 1;

  const profileScores = { sweet: 0, crisp: 0, botanical: 0, bold: 0, rich: 0, adventurous: 0 };
  Object.keys(flavor).forEach(trait => {
    const map = QUIZ_TRAIT_TO_PROFILE[trait];
    if (map) profileScores[map.profile] += (flavor[trait] / total) * map.weight;
  });

  const ranked = Object.keys(profileScores).sort((a, b) => profileScores[b] - profileScores[a]);
  const winner = ranked[0];
  const margin = profileScores[ranked[0]] - profileScores[ranked[1]];

  const daring = personality.daring || 0;
  const intensity = ((flavor.bold || 0) + (flavor.bitter || 0) + (flavor.smoky || 0)) / total;
  if (daring >= 2.5 && intensity >= 0.30 && (winner === 'bold' || winner === 'adventurous')) {
    return 'adventurous';
  }

  if (margin < 0.06) {
    const top2 = ranked.slice(0, 2);

    const q15 = selections[15];
    if (q15 && q15.length) {
      const q15Options = QUIZ_QUESTIONS.find(q => q.id === 15).options;
      const feelMatches = q15
        .map(idx => QUIZ_FEEL_TO_PROFILE[q15Options[idx]?.feel])
        .filter(mapped => mapped && top2.includes(mapped));
      if (feelMatches.length) {
        return feelMatches.reduce((best, p) => (profileScores[p] > profileScores[best] ? p : best));
      }
    }

    const topPersonality = Object.keys(personality)
      .sort((a, b) => (personality[b] || 0) - (personality[a] || 0))[0];
    const mappedPersonality = QUIZ_PERSONALITY_TO_PROFILE[topPersonality];
    if (mappedPersonality && top2.includes(mappedPersonality)) return mappedPersonality;

    const q4 = selections[4];
    if (q4 && q4.length) {
      const tier = QUIZ_QUESTIONS.find(q => q.id === 4).options[q4[0]]?.sweetTier;
      if ((tier === 'very' || tier === 'little') && top2.includes('sweet')) return 'sweet';
      if ((tier === 'barely' || tier === 'none') && top2.includes('crisp')) return 'crisp';
    }
  }

  return winner;
}
// ── PALATE VECTOR (Step 4) ────────────────────────────────────
// Deterministic derivation from existing palate state into numeric flavor axes.
// Returns values in [0,1] for: sweet, bitter, acidic, smoky, herbal, boozy, dry, fruity.
// Increment 2 will use these to compute match_score against drink flavor blueprints.
function palateToVector(palate) {
  const v = { sweet:0.5, bitter:0.5, acidic:0.5, smoky:0.5, herbal:0.5, boozy:0.5, dry:0.5, fruity:0.5 };
  if (!palate) return v;
  // Style nudges
  const style = (palate.preferredStyle || '').toLowerCase();
  if (style === 'crisp')      { v.acidic = 0.85; v.dry = 0.75; v.sweet = 0.2;  v.bitter = 0.45; }
  else if (style === 'medium'){ v.acidic = 0.6;  v.dry = 0.6;  v.sweet = 0.4;  v.bitter = 0.55; }
  else if (style === 'bold')  { v.bitter = 0.8;  v.boozy = 0.75; v.dry = 0.65; v.sweet = 0.25; }
  else if (style === 'off-dry'){ v.sweet = 0.7;  v.acidic = 0.55; v.dry = 0.3; }
  // Aroma tags nudge in expected directions
  const aromas = (palate.topAromas || []).map(a => a.toLowerCase());
  const bump = (axis, delta) => { v[axis] = Math.min(1, Math.max(0, v[axis] + delta)); };
  for (const a of aromas) {
    if (/citrus|lemon|lime|grapefruit/.test(a))                bump('acidic', 0.1);
    if (/mineral|flint|chalk|saline/.test(a))                  { bump('dry', 0.1); bump('acidic', 0.05); }
    if (/floral|rose|elderflower|jasmine/.test(a))             bump('herbal', 0.05);
    if (/herb|sage|thyme|bay|fennel/.test(a))                  bump('herbal', 0.12);
    if (/pepper|spice|clove|cinnamon/.test(a))                 bump('smoky', 0.05);
    if (/smoke|tobacco|leather|tar/.test(a))                   bump('smoky', 0.15);
    if (/cherry|berry|plum|fig|currant|raspberry/.test(a))     bump('fruity', 0.15);
    if (/peach|apricot|pear|apple|melon/.test(a))              { bump('fruity', 0.12); bump('sweet', 0.05); }
    if (/honey|caramel|vanilla|toffee/.test(a))                bump('sweet', 0.1);
    if (/oak|toast|butter|cream/.test(a))                      bump('boozy', 0.05);
  }
  // Round to 2 decimals to keep payloads readable in logs
  Object.keys(v).forEach(k => { v[k] = Math.round(v[k] * 100) / 100; });
  return v;
}

// ── COSINE SIMILARITY (Step 6) ────────────────────────────────
// Both inputs are objects with the same numeric keys (palate vector / drink vector).
// Returns a score in [0,1] where 1.0 is a perfect match.
const FLAVOR_AXES = ['sweet','bitter','acidic','smoky','herbal','boozy','dry','fruity'];
function cosineSimilarity(a, b) {
  let dot = 0, magA = 0, magB = 0;
  for (const k of FLAVOR_AXES) {
    const av = (typeof a?.[k] === 'number') ? a[k] : 0.5;
    const bv = (typeof b?.[k] === 'number') ? b[k] : 0.5;
    dot += av * bv;
    magA += av * av;
    magB += bv * bv;
  }
  if (magA === 0 || magB === 0) return 0;
  return dot / (Math.sqrt(magA) * Math.sqrt(magB));
}

// AI-generated flavor vector for unknown drinks (cache miss).
// Returns null if AI call fails or response is malformed; ranking falls back to neutral 0.5 vector for that drink.
async function aiGenerateFlavorVectors(unknownDrinks) {
  if (!unknownDrinks || unknownDrinks.length === 0) return {};
  const list = unknownDrinks.map(d => `- ${d.name} (${d.category || 'unknown'})`).join('\n');
  try {
    const raw = await callClaude(
      [{
        role: 'user',
        content:
          `For each drink below, output a flavor vector with these 8 axes (each 0.0 to 1.0):\n` +
          `sweet, bitter, acidic, smoky, herbal, boozy, dry, fruity\n\n` +
          `Drinks:\n${list}\n\n` +
          `Output a JSON object keyed by drink name. Example:\n` +
          `{"Negroni": {"sweet":0.3,"bitter":0.9,"acidic":0.4,"smoky":0.1,"herbal":0.85,"boozy":0.75,"dry":0.55,"fruity":0.35}}\n\n` +
          `Rules:\n- Output ONLY a JSON object, no prose, no fences.\n- Use objective sensory profile, not personal preference.\n- All 8 axes are required for every drink.`
      }],
      `You are a flavor profile service. You output a JSON object mapping drink names to numeric flavor vectors. No prose. No code fences.`,
      1200
    );
    const cleaned = raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '').trim();
    const obj = JSON.parse(cleaned);
    if (obj && typeof obj === 'object') return obj;
  } catch (e) {
    console.error('aiGenerateFlavorVectors failed', e);
  }
  return {};
}

// ── WINE GLASS GLYPH ──────────────────────────────────────
function WineGlassGlyph({capH,W,sw,color}) {
  const col=color||C.coral;
  const ascend=capH*0.20, H=capH+ascend, cx=W/2, pad=W*0.06;
  const topEnds=ascend, botEnds=ascend+capH*0.28, botTrough=ascend+capH*0.44;
  const botCtrlY=botEnds+(botTrough-botEnds)*0.85;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} fill="none"
      style={{display:"inline-block",verticalAlign:"bottom",overflow:"visible",flexShrink:0,transform:"skewX(-12deg)",transformOrigin:"50% 100%"}}>
      <path d={`M ${pad} ${topEnds} Q ${cx} 0 ${W-pad} ${topEnds}`} stroke={col} strokeWidth={sw} strokeLinecap="round" fill="none"/>
      <path d={`M ${pad} ${botEnds} Q ${cx} ${botCtrlY} ${W-pad} ${botEnds}`} stroke={col} strokeWidth={sw} strokeLinecap="round" fill="none"/>
      <line x1={cx} y1={botTrough} x2={cx} y2={H} stroke={col} strokeWidth={sw*0.48} strokeLinecap="round"/>
    </svg>
  );
}

// ── LOGO — all lowercase ───────────────────────────────────
function SwishdLogo({size="md",light=false}) {
  const cfg={xl:{r:66,W:32,sw:2.0},lg:{r:40,W:20,sw:1.6},md:{r:26,W:14,sw:1.3},sm:{r:20,W:11,sw:1.1},xs:{r:15,W:8,sw:0.9}};
  const cf=cfg[size]||cfg.md;
  const ink=light?"rgba(250,247,240,0.85)":C.ink;
  const f="'Cormorant Garamond',Georgia,serif";
  return (
    <div style={{display:"inline-flex",alignItems:"baseline",lineHeight:1}}>
      <span style={{fontFamily:f,fontSize:cf.r,fontWeight:400,color:ink,lineHeight:1}}>s</span>
      <span style={{fontFamily:f,fontSize:cf.r,fontWeight:400,color:ink,lineHeight:1}}>w</span>
      <WineGlassGlyph capH={cf.r*0.82} W={cf.W} sw={cf.sw}/>
      <span style={{fontFamily:f,fontSize:cf.r,fontWeight:400,color:ink,lineHeight:1}}>sh</span>
      <span style={{fontFamily:f,fontSize:cf.r,fontWeight:300,fontStyle:"italic",color:ink,lineHeight:1}}>'</span>
      <span style={{fontFamily:f,fontSize:cf.r,fontWeight:400,color:ink,lineHeight:1}}>d</span>
    </div>
  );
}

// ── SHARED STYLES ─────────────────────────────────────────
const ss={
  card:{background:"#FFFFFF",border:"1px solid #EDE8DF",borderRadius:14,boxShadow:"0 2px 16px rgba(28,31,38,0.05)"},
  cardHi:{background:"#FFFFFF",border:"1.5px solid rgba(204,34,0,0.2)",borderRadius:14,boxShadow:"0 4px 20px rgba(204,34,0,0.08)"},
  btn:{cursor:"pointer",border:"none",fontFamily:"'Cormorant Garamond',Georgia,serif",transition:"all 0.2s",display:"block",width:"100%",textAlign:"center"},
  btnDark:{background:"#1C1F26",color:"#FAF7F0",padding:"16px",fontSize:17,fontStyle:"italic",borderRadius:3},
  btnCoral:{background:"#CC2200",color:"white",padding:"16px",fontSize:17,fontStyle:"italic",borderRadius:3,border:"none",cursor:"pointer",fontFamily:"'Cormorant Garamond',Georgia,serif",transition:"all 0.2s",display:"block",width:"100%",textAlign:"center"},
  btnOut:{background:"transparent",color:"#1C1F26",border:"1.5px solid #EDE8DF",padding:"15px",fontSize:16,fontStyle:"italic",borderRadius:3},
  chip:{display:"inline-flex",alignItems:"center",padding:"8px 16px",border:"1.5px solid #EDE8DF",borderRadius:40,fontFamily:"'Jost',sans-serif",fontSize:12,cursor:"pointer",color:"#A8BFC2",background:"#FAFAF8",transition:"all 0.15s"},
  chipOn:{background:"#1C1F26",border:"1.5px solid #1C1F26",color:"white"},
  sl:{fontFamily:"'Jost',sans-serif",fontSize:10,letterSpacing:"3px",color:"#A8BFC2",textTransform:"uppercase",marginBottom:12,display:"block"},
  navBtn:{background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"6px 8px"},
  input:{background:"#FFFFFF",border:"1.5px solid #EDE8DF",color:"#1C1F26",padding:"13px 16px",fontFamily:"'Jost',sans-serif",fontSize:14,width:"100%",outline:"none",borderRadius:8},
  tag:{fontFamily:"'Jost',sans-serif",fontSize:9,letterSpacing:"2px",textTransform:"uppercase",display:"inline-block",padding:"3px 9px",borderRadius:3},
};

const FONTS=`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400;1,500&family=Jost:wght@300;400;500&display=swap');`;
const RESET=`*{box-sizing:border-box;margin:0;padding:0;}::-webkit-scrollbar{width:3px;}::-webkit-scrollbar-thumb{background:#CC220044;}.fade{animation:fi 0.35s ease both;}@keyframes fi{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}textarea{font-family:'Jost',sans-serif;font-size:13px;background:#FAF7F0;border:1.5px solid #EDE8DF;border-radius:8px;padding:12px;width:100%;outline:none;resize:vertical;color:#1C1F26;min-height:90px;}textarea::placeholder{color:#A8BFC2;}textarea:focus,input:focus{border-color:#CC220044 !important;outline:none;}@keyframes swishdWave{0%,60%,100%{opacity:0.25}30%{opacity:1}}.dots{display:inline-flex;gap:2px;margin-left:2px}.dots>i{font-style:normal;animation:swishdWave 1.2s ease-in-out infinite}.dots>i:nth-child(2){animation-delay:0.2s}.dots>i:nth-child(3){animation-delay:0.4s}`;

// ── SHARED CTA COMPONENTS ─────────────────────────────────
// CTA_STD_V1_2026_06_10
const _BTN_BASE = {
  height: 52, padding: '0 20px', borderRadius: 26,
  fontFamily: 'Jost, sans-serif', fontWeight: 300, fontSize: 17,
  letterSpacing: '1px', textAlign: 'center', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const _BTN = {
  primary:   { ..._BTN_BASE, background: '#c23b0e', color: '#fff', border: 'none' },
  secondary: { ..._BTN_BASE, background: 'transparent', color: '#c23b0e', border: '1.5px solid #c23b0e' },
};
function Btn({ variant = 'primary', tone = 'coral', style, children, ...props }) {
  if (variant === 'inline') {
    return (
      <button {...props} style={{
        fontFamily: 'Jost, sans-serif', fontWeight: 300, fontSize: 14, letterSpacing: '0.5px',
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color: tone === 'gray' ? '#6b6b6b' : '#c23b0e', ...style }}>
        {children}
      </button>
    );
  }
  return <button {...props} style={{ ...(_BTN[variant] || _BTN.primary), ...style }}>{children}</button>;
}
function IconBtn({ size = 40, label, style, children, ...props }) {
  return (
    <button {...props}
      onMouseDown={e => (e.currentTarget.style.transform = 'scale(.9)')}
      onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
      onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
      style={{ width: size, height: size, display: 'inline-flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none',
        cursor: 'pointer', transition: 'transform .12s, color .12s', ...style }}>
      {children}
      {label && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: 10, marginTop: 2 }}>{label}</span>}
    </button>
  );
}

// ── CHAT ──────────────────────────────────────────────────
function ChatScreen({profile,onBack,palate}) {
  const [msgs,setMsgs]=useState(profile.chatHistory||[]);
  const [input,setInput]=useState("");
  const [typing,setTyping]=useState(false);
  const bottomRef=useRef(null);
  useEffect(()=>{bottomRef.current?.scrollIntoView({behavior:"smooth"});},[msgs]);

  const starters=["What's the most memorable wine you've ever had?","Red, white, or something unexpected?","Best wine bar you've discovered recently?","Do you prefer tastings or intimate dinners?"];

  const send=async()=>{
    if(!input.trim())return;
    const m={from:"me",text:input,time:"just now"};
    setMsgs(p=>[...p,m]); setInput(""); setTyping(true);
    try {
      const shared=palate?profile.aromas.filter(a=>palate.topAromas.includes(a)):[];
      const reply=await callClaude(
        [{role:"user",content:input}],
        `You are ${profile.name}, ${profile.age}, ${profile.job}. Wine personality: ${profile.winePersonality}. Fave wine: ${profile.favoriteWine}. Shared aromas: ${shared.join(", ")||"none yet"}. Reply in 1-2 sentences, warm, witty, wine-curious.`,
        120
      );
      setMsgs(p=>[...p,{from:"them",text:reply,time:"just now"}]);
    } catch { setMsgs(p=>[...p,{from:"them",text:"(lost signal — try again)",time:""}]); }
    setTyping(false);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100vh",background:C.linen,maxWidth:430,margin:"0 auto"}}>

      {/* HEADER */}
      <div style={{padding:"14px 20px 12px",display:"flex",alignItems:"center",gap:12,borderBottom:`1px solid ${C.border}`,background:"rgba(250,247,240,0.96)",backdropFilter:"blur(12px)",flexShrink:0}}>
        <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"uppercase"}}>← Back</button>
        <div style={{width:36,height:36,borderRadius:"50%",background:C.border,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{profile.photo}</div>
        <div style={{flex:1}}>
          <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:16,fontWeight:400}}>{profile.name}</div>
          <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.coral}}>{profile.compatibility}% swish'd match</div>
        </div>
      </div>

      {/* SCROLLABLE CHAT AREA — profile card at top, then messages */}
      <div style={{flex:1,overflowY:"auto",padding:"20px 16px",display:"flex",flexDirection:"column",gap:10}}>

        {/* PROFILE MINI CARD */}
        <div style={{background:"#FFFFFF",border:"1px solid #EDE8DF",borderRadius:14,boxShadow:"0 2px 16px rgba(28,31,38,0.05)",marginBottom:6,overflow:"hidden"}}>
          <div style={{padding:"16px 18px 12px",borderBottom:`1px solid ${C.border}`,display:"flex",gap:12,alignItems:"center"}}>
            <div style={{width:52,height:52,borderRadius:"50%",background:C.border,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,flexShrink:0}}>{profile.photo}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:18,fontWeight:400,marginBottom:1}}>{profile.name}, {profile.age}</div>
              <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.mist}}>{profile.job} · {profile.city}</div>
              <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.coral,marginTop:2}}>{profile.winePersonality}</div>
            </div>
          </div>
          <div style={{padding:"10px 18px",borderBottom:`1px solid ${C.border}`,background:"rgba(0,180,200,0.04)"}}>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:"#1f2226",letterSpacing:2,textTransform:"uppercase",marginBottom:3}}>Last poured</div>
            <div style={{fontSize:14,fontWeight:400}}>{profile.favoriteWine}</div>
          </div>
          <div style={{padding:"10px 18px"}}>
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {profile.aromas.map(a=><span key={a} style={{fontFamily:"'Jost',sans-serif",fontSize:9,letterSpacing:"2px",textTransform:"uppercase",padding:"3px 9px",borderRadius:3,background:"rgba(204,34,0,0.07)",color:C.coral,border:"1px solid rgba(204,34,0,0.2)"}}>{a}</span>)}
            </div>
          </div>
        </div>

        {/* CONVERSATION STARTERS — only when no messages */}
        {msgs.length===0&&(
          <div style={{textAlign:"center",paddingTop:8}}>
            <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic",fontSize:14,color:C.mist,marginBottom:12}}>Start the conversation</div>
            {starters.map((s,i)=>(
              <div key={i} onClick={()=>setInput(s)} style={{background:"#FFFFFF",border:"1px solid #EDE8DF",borderRadius:14,padding:"10px 14px",marginBottom:8,cursor:"pointer",fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic",fontSize:14,color:C.ink}}>{s}</div>
            ))}
          </div>
        )}

        {/* CHAT HISTORY */}
        {msgs.map((m,i)=>(
          <div key={i} style={{display:"flex",justifyContent:m.from==="me"?"flex-end":"flex-start"}}>
            <div style={{maxWidth:"78%",padding:"11px 14px",borderRadius:m.from==="me"?"14px 14px 4px 14px":"14px 14px 14px 4px",background:m.from==="me"?C.ink:"#FFFFFF",color:m.from==="me"?C.linen:C.ink,border:m.from==="me"?"none":`1px solid ${C.border}`,fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:15,lineHeight:1.5}}>
              {m.text}
              <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:m.from==="me"?"rgba(255,255,255,0.4)":C.mist,marginTop:4}}>{m.time}</div>
            </div>
          </div>
        ))}
        {typing&&<div style={{display:"flex"}}><div style={{padding:"11px 16px",borderRadius:"14px 14px 14px 4px",background:"#FFFFFF",border:`1px solid ${C.border}`,fontFamily:"'Jost',sans-serif",fontSize:13,color:C.mist,letterSpacing:4}}>···</div></div>}
        <div ref={bottomRef}/>
      </div>

      {/* INPUT BAR */}
      <div style={{padding:"12px 16px 28px",borderTop:`1px solid ${C.border}`,background:"rgba(250,247,240,0.97)",backdropFilter:"blur(12px)",display:"flex",gap:10,flexShrink:0}}>
        <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder="" style={{background:"#FFFFFF",border:"1.5px solid #EDE8DF",color:"#1C1F26",padding:"13px 16px",fontFamily:"'Jost',sans-serif",fontSize:14,flex:1,outline:"none",borderRadius:24}}/>
        <button onClick={send} style={{background:C.coral,color:"white",border:"none",borderRadius:"50%",width:44,height:44,cursor:"pointer",fontSize:18,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>→</button>
      </div>
    </div>
  );
}

// ── PAYMENT SCREEN COMPONENT ────────────────────── PAYMENT_REDESIGN_V1
function PaymentScreen({ev,onBack,onDone,ss,C,WineGlassGlyph}) {
  const [cardName,setCardName]=useState("");
  const [cardNum,setCardNum]=useState("");
  const [expiry,setExpiry]=useState("");
  const [cvv,setCvv]=useState("");
  const [done,setDone]=useState(false);
  const [qty,setQty]=useState(1);
  const [payMethod,setPayMethod]=useState("apple");
  return (
    <div className="fade" style={{padding:"24px 20px"}}>
      {done?(
        <div style={{textAlign:"center",paddingTop:40}}>
          <div style={{display:"flex",justifyContent:"center",gap:8,marginBottom:24}}>
            <WineGlassGlyph capH={32} W={20} sw={1.8} color={C.coral}/>
            <WineGlassGlyph capH={32} W={20} sw={1.8} color={C.coral}/>
          </div>
          <h2 style={{fontFamily:"'Jost',sans-serif",fontSize:26,fontWeight:400,fontStyle:"normal",textTransform:"uppercase",letterSpacing:2,marginBottom:8}}>You're swish'd in.</h2>
          <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:13,color:"#1C1F26",lineHeight:1.8,marginBottom:6}}>{ev.title}</p>
          <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.aqua,marginBottom:4}}>{ev.venue} · {ev.city}</p>
          <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.aqua,marginBottom:28}}>{ev.date} · {ev.time}</p>
          <div style={{...ss.card,padding:"14px 16px",marginBottom:20,background:"rgba(204,34,0,0.04)",border:`1px solid rgba(204,34,0,0.15)`}}>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.coral,marginBottom:4}}>Confirmation sent</div>
            <div style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.mist}}>Check your email.</div>
          </div>
          <button style={{...ss.btn,...ss.btnCoral}} onClick={onDone}>Back to events</button>
        </div>
      ):(
        <>
          <span style={ss.sl}>Reserve your spot</span>
          <h2 style={{fontSize:20,fontWeight:400,fontStyle:"italic",marginBottom:4}}>{ev.title}</h2>
          <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.aqua,marginBottom:4}}>{ev.venue} · {ev.city}</p>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.aqua}}>{ev.date} · {ev.time}</p>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist}}>Qty</span>
              <input type="number" min={1} value={qty} onChange={e=>setQty(Math.max(1,parseInt(e.target.value)||1))} style={{...ss.input,width:64,padding:"6px 8px",height:"auto",fontSize:13}}/>
            </div>
          </div>
          <div style={{...ss.card,padding:"14px 16px",marginBottom:20,background:"rgba(232,184,75,0.04)",border:"1px solid rgba(232,184,75,0.2)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:"#1C1F26"}}>Total Payment</div>
              <div style={{fontSize:20,color:"#1C1F26",fontWeight:400}}>${ev.price*qty}</div>
            </div>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:"#1C1F26",marginTop:4,lineHeight:1.6}}>{ev.spots} spots remaining · This is a non-refundable ticket.</div>
          </div>
          <span style={ss.sl}>Payment details</span>
          <div style={{display:"flex",gap:8,marginBottom:16}}>
            {[["apple","Apple Pay"],["paypal","PayPal"],["card","Credit Cards"]].map(([m,label])=>(
              <button key={m} onClick={()=>setPayMethod(m)} style={{flex:1,padding:"10px 4px",borderRadius:8,fontFamily:"'Jost',sans-serif",fontSize:11,cursor:"pointer",border:payMethod===m?"2px solid #1C1F26":"1.5px solid #1C1F26",background:"#FFFFFF",color:"#1C1F26",fontWeight:payMethod===m?500:300}}>{m==="apple" ? (<><svg viewBox="0 0 384 512" width="11" height="13" style={{verticalAlign:"middle",marginRight:3,marginBottom:2}} fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>Pay</>) : label}</button>
            ))}
          </div>
          {payMethod==="card"&&(
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
              <div>
                <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>Name on card</div>
                <input style={{...ss.input}} placeholder="Isabella Martin" value={cardName} onChange={e=>setCardName(e.target.value)}/>
              </div>
              <div>
                <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>Card number</div>
                <input style={{...ss.input}} placeholder="4242 4242 4242 4242" value={cardNum} onChange={e=>setCardNum(e.target.value)} maxLength={19}/>
              </div>
              <div style={{display:"flex",gap:10}}>
                <div style={{flex:1}}>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>Expiry</div>
                  <input style={{...ss.input}} placeholder="MM / YY" value={expiry} onChange={e=>setExpiry(e.target.value)} maxLength={7}/>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>CVV</div>
                  <input style={{...ss.input}} placeholder="···" value={cvv} onChange={e=>setCvv(e.target.value)} maxLength={4}/>
                </div>
              </div>
            </div>
          )}
          <Btn style={{width:"100%",marginBottom:10}} onClick={()=>setDone(true)}>Confirm Reservation</Btn>
          <p style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,textAlign:"center",lineHeight:1.6}}>🔒 Secured · Non-refundable</p>
        </>
      )}
    </div>
  );
}
function ProfileTab({history,palate,ss,C,lookingFor,setLookingFor,profileData,setProfileData,profileSaved,saveProfile,quizResult,startQuiz}) {
  const [photoUrl,setPhotoUrl]=useState(null);
  const [editingName,setEditingName]=useState(false);
  const [nameInput,setNameInput]=useState("");
  // Two-state component: 'edit' (the full form) and 'collapsed' (Hinge-style saved view).
  const [profileView,setProfileView]=useState(profileSaved?'collapsed':'edit');
  const [profTab,setProfTab]=useState('palate');
  const fileRef=useRef();
  const handlePhoto=async e=>{
    const f=e.target.files[0]; if(!f) return;
    const url=await uploadProfilePhoto(f);
    if(!url) return;
    setPhotoUrl(url);
    upd('photo_url',url);
  };
  const upd=(k,v)=>setProfileData(p=>({...p,[k]:v}));
  // Sync the displayed avatar from persisted profile once it loads.
  useEffect(()=>{ if(profileData.photo_url) setPhotoUrl(profileData.photo_url); },[profileData.photo_url]);
  // Returning user whose profile finishes loading after this tab mounts → collapse.
  // Does not fight the edit pencil: only fires when profileSaved *changes* to true.
  useEffect(()=>{ if(profileSaved) setProfileView('collapsed'); },[profileSaved]);

  // Resolve the palate result to display. Priority: fresh in-session quiz →
  // reconstruct from persisted per-question selections (survives reload) → none.
  // Note: the persisted palate_fingerprint has only 4 axes + no title/subtype, so we
  // rebuild the full result (title/subtype/descriptor/6-axis scores) from quiz_selections.
  const palateResult = (()=>{
    if(quizResult) return { profile:quizResult.profile, subtype:quizResult.subtype, scores:quizResult.scores };
    const sel = profileData.quiz_selections;
    if(sel && Object.keys(sel).length){
      const scores = computeQuizScores(sel);
      const profileKey = quizDetermineProfile(sel);
      return { profile: QUIZ_PROFILES[profileKey], subtype: quizDetermineSubtype(profileKey, scores), scores };
    }
    return null;
  })();
  const fpAxes = profileData.palate_fingerprint?.axes;

  // ── COLLAPSED / SAVED VIEW (Hinge-style) ──────────────────────
  if(profileView==='collapsed') return (
    <div className="fade" style={{padding:"24px 20px 40px"}}>
      {/* PHOTO + edit pencil */}
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:6}}>
        <div style={{position:"relative",width:110,height:110}}>
          <div style={{width:110,height:110,borderRadius:"50%",background:C.border,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",border:`2px solid ${C.border}`}}>
            {(photoUrl||profileData.photo_url)
              ? <img src={photoUrl||profileData.photo_url} alt="profile" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              : <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke={C.mist} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
            }
          </div>
          <div onClick={()=>setProfileView('edit')} title="Edit profile" style={{position:"absolute",right:0,bottom:2,width:32,height:32,borderRadius:"50%",background:C.coral,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",border:"2px solid #fff"}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9"/>
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
            </svg>
          </div>
        </div>
        <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:24,color:C.ink,marginTop:12,textAlign:"center"}}>{profileData.user_name||"User Name"}</div>
      </div>

      {/* TABS */}
      <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,marginTop:14,marginBottom:22}}>
        {[{k:'palate',label:'My Palate Fingerprint'},{k:'safety',label:'Safety'},{k:'swishd',label:"My Swish'd"}].map(t=>(
          <div key={t.k} onClick={()=>setProfTab(t.k)} style={{flex:1,textAlign:"center",padding:"10px 4px",cursor:"pointer",fontFamily:"'Jost',sans-serif",fontSize:10.5,letterSpacing:0.3,lineHeight:1.3,color:profTab===t.k?C.coral:C.mist,fontWeight:profTab===t.k?600:400,borderBottom:profTab===t.k?`2px solid ${C.coral}`:"2px solid transparent",marginBottom:-1}}>{t.label}</div>
        ))}
      </div>

      {/* TAB: My Palate Fingerprint */}
      {profTab==='palate'&&(
        palateResult ? (
          <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
            <div style={{fontSize:52,marginBottom:6}}>{palateResult.profile.emoji}</div>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,letterSpacing:2,textTransform:"uppercase",color:C.mist,marginBottom:8}}>Your palate profile</div>
            <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:30,fontWeight:400,color:C.ink,marginBottom:4,textAlign:"center"}}>{palateResult.profile.title}</div>
            {palateResult.subtype&&<div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.coral,letterSpacing:1,marginBottom:18,textTransform:"uppercase"}}>{palateResult.subtype}</div>}
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:C.mist,lineHeight:1.7,textAlign:"center",marginBottom:24,maxWidth:300}}>{palateResult.profile.description}</div>
            <PalateRadar data={radarValuesFromScores(palateResult.scores)}/>
          </div>
        ) : fpAxes ? (
          // Defensive fallback: a fingerprint exists but selections don't (legacy row) —
          // render the 4-axis fingerprint so we never show the blank state incorrectly.
          <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
            <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:28,fontWeight:400,color:C.ink,marginBottom:18,textAlign:"center"}}>Your Palate Fingerprint</div>
            <PalateRadar data={[
              {trait:"Sweetness",value:Math.min(Math.max((fpAxes.sweetness||0)*10,0),100)},
              {trait:"Body",     value:Math.min(Math.max((fpAxes.body||0)*10,0),100)},
              {trait:"Acidity",  value:Math.min(Math.max((fpAxes.acidity||0)*10,0),100)},
              {trait:"Tannin",   value:Math.min(Math.max((fpAxes.tannin||0)*10,0),100)},
            ]}/>
          </div>
        ) : (
          <div style={{textAlign:"center",padding:"16px 8px"}}>
            <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:26,fontWeight:400,color:C.ink,marginBottom:10}}>Find Your Flavor Crowd</div>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:C.mist,lineHeight:1.7,marginBottom:24,maxWidth:320,marginLeft:"auto",marginRight:"auto"}}>Take the Palate Fingerprint Quiz to unlock personalized drink picks feature and meet nearby swishers with similar palate fingerprint.</div>
            <Btn variant="primary" onClick={startQuiz} style={{width:240,display:"block",margin:"0 auto"}}>Start Palate Fingerprint</Btn>
          </div>
        )
      )}

      {/* TAB: Safety */}
      {profTab==='safety'&&(
        <div style={{textAlign:"center",padding:"48px 16px",fontFamily:"'Jost',sans-serif",fontSize:13,color:C.mist,lineHeight:1.7}}>Safety features coming soon</div>
      )}

      {/* TAB: My Swish'd */}
      {profTab==='swishd'&&(
        <div style={{textAlign:"center",padding:"48px 16px",fontFamily:"'Jost',sans-serif",fontSize:13,color:C.mist,lineHeight:1.7}}>More photos and profile settings coming soon</div>
      )}
    </div>
  );

  // ── EDIT / FORM VIEW (existing layout, unchanged) ─────────────
  return (
    <div className="fade" style={{padding:"24px 20px"}}>
      <span style={ss.sl}>Your palate</span>

      {/* PHOTO */}
      <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:24}}>
        <div onClick={()=>fileRef.current.click()} style={{width:72,height:72,borderRadius:"50%",background:C.border,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",overflow:"hidden",flexShrink:0,border:`2px dashed ${C.mist}`}}>
          {photoUrl
            ? <img src={photoUrl} alt="profile" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            : <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.mist} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
          }
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" style={{display:"none"}} onChange={handlePhoto}/>
        <div>
          {editingName ? (
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
              <input
                autoFocus
                value={nameInput}
                onChange={e=>setNameInput(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"){upd("user_name",nameInput);setEditingName(false);}}}
                style={{...ss.input,fontSize:16,fontStyle:"italic",padding:"4px 8px",height:"auto"}}
              />
              <button onClick={()=>{upd("user_name",nameInput);setEditingName(false);}} style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.coral,background:"none",border:"none",cursor:"pointer",padding:0}}>Save</button>
            </div>
          ) : (
            <div
              onClick={()=>{setNameInput(profileData.user_name||"");setEditingName(true);}}
              style={{fontSize:16,fontWeight:400,fontStyle:"italic",marginBottom:2,color:C.coral,cursor:"pointer"}}
            >
              {profileData.user_name || "User Name"}
            </div>
          )}
          <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.mist,marginBottom:3}}>Tap photo to upload</div>
          <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.coral}}>{history.length} pours logged</div>
        </div>
      </div>

      {/* NAME */}
      <div style={{marginBottom:12}}>
        <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>Name</div>
        <input style={{...ss.input,width:"100%",boxSizing:"border-box"}} placeholder="Your full name" value={profileData.name} onChange={e=>upd("name",e.target.value)}/>
      </div>

      {/* DATE OF BIRTH + OCCUPATION — UI_POLISH_V1_2026_06_01 — 2-col grid */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div>
          <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>
            Date of birth <span style={{fontSize:9,color:C.mist,letterSpacing:0,textTransform:"none",marginLeft:4}}>· locked after save</span>
          </div>
          <input type="date" style={{...ss.input,color:C.ink,width:"100%",boxSizing:"border-box",fontFamily:"'Jost',sans-serif",fontSize:14,padding:"10px 12px"}} max={new Date(new Date().setFullYear(new Date().getFullYear()-18)).toISOString().split("T")[0]} value={profileData.dob} onChange={e=>upd("dob",e.target.value)} disabled={profileSaved&&!!profileData.dob}/>
        </div>
        <div>
          <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>Occupation</div>
          <input style={{...ss.input,width:"100%",boxSizing:"border-box"}} placeholder="e.g. Product designer" value={profileData.occupation} onChange={e=>upd("occupation",e.target.value)}/>
        </div>
      </div>

      {/* GATE_AUDIT_V1_2026_06_01 — two-col dropdown grid */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div>
          <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>Swish'd personality</div>
          <select value={profileData.personality} onChange={e=>upd("personality",e.target.value)} style={{...ss.input,width:"100%",boxSizing:"border-box",color:C.ink,appearance:"none",backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A8BFC2' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 10px center",paddingRight:28}}>
            <option value="">Select…</option>
            <option value="wine">Wine</option>
            <option value="cocktails">Cocktails</option>
            <option value="champagne">Champagne</option>
            <option value="spirits">Spirits</option>
            <option value="mix">Mix</option>
          </select>
        </div>
        <div>
          <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>You're looking for</div>
          <select value={lookingFor} onChange={e=>setLookingFor(e.target.value)} style={{...ss.input,width:"100%",boxSizing:"border-box",color:C.ink,appearance:"none",backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A8BFC2' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 10px center",paddingRight:28}}>
            <option value="woman">Woman</option>
            <option value="man">Man</option>
          </select>
        </div>
      </div>

      {/* LOCATION */}
      <div style={{marginBottom:18}}>
        <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>Location</div>
        <div style={{display:"flex",gap:8,marginBottom:8}}>
          <input style={{...ss.input,flex:1}} placeholder="City, zip code, or neighbourhood…" value={profileData.location} onChange={e=>upd("location",e.target.value)}/>
          {/* CTA_STD_V1_2026_06_10 */}
          <Btn
            variant="secondary"
            onClick={()=>{
              if(navigator.geolocation){
                navigator.geolocation.getCurrentPosition(pos=>{
                  const {latitude:lat,longitude:lng}=pos.coords;
                  fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
                    .then(r=>r.json())
                    .then(d=>{
                      const addr=d.address;
                      const loc=addr.city||addr.town||addr.village||addr.county||"";
                      const state=addr.state||"";
                      const zip=addr.postcode||"";
                      const country=addr.country||"";
                      upd("location",[loc,state,zip,country].filter(Boolean).join(", "));
                    }).catch(()=>upd("location","Location found"));
                },()=>alert("Location access denied. Please type your city manually."));
              }
            }}
            style={{whiteSpace:"nowrap",flexShrink:0}}>
            📍 My Location
          </Btn>
        </div>
        {profileData.location&&(
          <div style={{borderRadius:10,overflow:"hidden",border:"1px solid rgba(0,180,200,0.2)"}}>
            <div style={{background:"rgba(0,180,200,0.05)",padding:"8px 14px",display:"flex",alignItems:"center",gap:8,borderBottom:"1px solid rgba(0,180,200,0.15)"}}>
              <span style={{fontSize:14}}>📍</span>
              <span style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:C.aquaD,fontWeight:500}}>{profileData.location}</span>
            </div>
            <iframe
              key={profileData.location}
              title="location-map"
              width="100%"
              height="200"
              style={{border:0,display:"block"}}
              src={`https://www.openstreetmap.org/export/embed.html?query=${encodeURIComponent(profileData.location)}&layer=mapnik`}
              allowFullScreen/>
          </div>
        )}
        <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,marginTop:6}}>Accepts city, neighbourhood, or zip code. Visible to nearby Swishers. Updatable anytime.</div>
      </div>

      {/* GATE_AUDIT_V1_2026_06_01 — pill CTA, 240px centered */}
      {/* CTA_STD_V1_2026_06_10 */}
      <Btn variant="primary" onClick={async()=>{ if(await saveProfile()) setProfileView('collapsed'); }} style={{width:240,display:"block",margin:"8px auto 0"}}>
        {profileSaved?"profile saved ✓":"save profile"}
      </Btn>
    </div>
  );
}
// ── Sign-in gate for auth-required actions (limited mode) ─────────
function SignInGate({ action = "use this", onSignIn }) {
  return (
    <div style={{
      background:"#f5f1ea", border:"1.5px solid #d4cfc7", borderRadius:16,
      padding:"28px 24px", textAlign:"center", maxWidth:360,
      margin:"24px auto", boxSizing:"border-box",
    }}>
      <p style={{
        fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic", fontWeight:500,
        fontSize:22, color:"#1f2226", margin:"0 0 8px", lineHeight:1.3,
      }}>Sign in to {action}.</p>
      <p style={{
        fontFamily:"'Jost',sans-serif", fontWeight:300, fontSize:14,
        color:"#9aa0a6", margin:"0 0 24px", lineHeight:1.6,
      }}>Create your palate to snap menus, get pairings, and save your pours.</p>
      <button onClick={onSignIn} style={{
        height:52, padding:"0 32px", borderRadius:32, border:"none",
        background:"#c23b0e", color:"#fff", fontFamily:"'Cormorant Garamond',serif",
        fontStyle:"italic", fontWeight:500, fontSize:18, cursor:"pointer",
      }}>Sign in</button>
    </div>
  );
}

function SignupScreen({mode='signup'}) {
  const [step,setStep]=useState('email');
  const [contact,setContact]=useState('');
  const [digits,setDigits]=useState(['','','','','','']);
  const [error,setError]=useState('');
  const [loading,setLoading]=useState(false);
  const inputRefs=useRef([]);

  async function handleEmailSubmit(e) {
    e.preventDefault(); setError('');
    if(!/\S+@\S+\.\S+/.test(contact.trim())){setError('Please enter a valid email address.');return;}
    setLoading(true);
    try {
      const {error:err}=await supabase.auth.signInWithOtp({email:contact.trim()});
      if(err) throw err;
      setStep('otp');
    } catch(err){setError(err.message||'Something went wrong. Try again.');}
    setLoading(false);
  }

  async function handleVerify(e) {
    e.preventDefault(); setError('');
    setLoading(true);
    try {
      const {data,error:err}=await supabase.auth.verifyOtp({email:contact.trim(),token:digits.join(''),type:'email'});
      if(err) throw err;
      // data.session populated → onAuthStateChange fires → setCurrentUser → setScreen('main')
    } catch(err){setError('Invalid or expired code. Please try again.');}
    setLoading(false);
  }

  async function handleResend(){
    setError(''); setDigits(['','','','','','']);
    await supabase.auth.signInWithOtp({email:contact.trim()});
  }

  function handleDigitChange(idx,val){
    const digit=val.replace(/\D/g,'').slice(-1);
    const next=[...digits]; next[idx]=digit; setDigits(next);
    if(digit&&idx<5) inputRefs.current[idx+1]?.focus();
  }
  function handleDigitKeyDown(idx,e){
    if(e.key==='Backspace'&&!digits[idx]&&idx>0){
      const next=[...digits]; next[idx-1]=''; setDigits(next);
      inputRefs.current[idx-1]?.focus();
    }
  }
  function handlePaste(e){
    const text=e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if(text.length>0){
      setDigits(Array.from({length:6},(_,i)=>text[i]||''));
      inputRefs.current[Math.min(text.length,5)]?.focus();
      e.preventDefault();
    }
  }

  const allFilled=digits.every(d=>d!=='');
  const slotStyle={width:44,height:52,borderRadius:12,border:'1.5px solid #d4cfc7',background:'#f5f1ea',
    fontFamily:"'Jost',sans-serif",fontSize:22,fontWeight:400,color:'#1f2226',textAlign:'center',
    outline:'none',boxSizing:'border-box'};

  return (
    <div style={{minHeight:'100vh',background:'#EFEDE3',display:'flex',flexDirection:'column',alignItems:'center',paddingTop:80,paddingLeft:24,paddingRight:24,paddingBottom:32,maxWidth:430,margin:'0 auto',boxSizing:'border-box'}}>
      <style>{FONTS+RESET}</style>
      {step==='email' ? (
        <>
          <p style={{fontFamily:"'Cormorant Garamond',serif",fontSize:26,fontStyle:'italic',fontWeight:500,color:'#1f2226',textAlign:'center',margin:'0 0 8px',lineHeight:1.3}}>{mode==='signin' ? "Confirm that it's you" : "Sign up"}</p>
          <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:14,color:'#9aa0a6',textAlign:'center',margin:'0 0 48px',lineHeight:1.6}}>Enter your email</p>
          <form onSubmit={handleEmailSubmit} style={{width:'100%',maxWidth:320,display:'flex',flexDirection:'column',gap:12}}>
            <input type="email" placeholder="your@email.com" value={contact} onChange={e=>setContact(e.target.value)}
              style={{width:'100%',height:52,borderRadius:26,border:'1.5px solid #d4cfc7',background:'#f5f1ea',fontFamily:"'Jost',sans-serif",fontSize:15,color:'#1f2226',padding:'0 20px',boxSizing:'border-box',outline:'none'}}/>
            {error&&<p style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:'#c23b0e',margin:0,textAlign:'center'}}>{error}</p>}
            <button type="submit" disabled={loading||!contact.trim()}
              style={{height:56,borderRadius:32,border:'none',background:contact.trim()?'#c23b0e':'#d4b4aa',color:'#fff',fontFamily:"'Cormorant Garamond',serif",fontStyle:'italic',fontWeight:500,fontSize:18,cursor:contact.trim()?'pointer':'default',marginTop:4}}>
              {loading?'Sending…':'Continue'}
            </button>
          </form>
        </>
      ) : (
        <>
          <p style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,fontStyle:'italic',fontWeight:500,color:'#1f2226',textAlign:'center',margin:'0 0 8px',lineHeight:1.3}}>Enter your verification code</p>
          <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:14,color:'#9aa0a6',textAlign:'center',margin:'0 0 40px',lineHeight:1.6}}>We sent a 6-digit code to <strong style={{color:'#1f2226'}}>{contact}</strong></p>
          <form onSubmit={handleVerify} style={{width:'100%',maxWidth:320,display:'flex',flexDirection:'column',alignItems:'center',gap:24}}>
            <div style={{display:'flex',gap:8}} onPaste={handlePaste}>
              {digits.map((d,i)=>(
                <input key={i} ref={el=>inputRefs.current[i]=el} type="text" inputMode="numeric"
                  maxLength={1} value={d} onChange={e=>handleDigitChange(i,e.target.value)}
                  onKeyDown={e=>handleDigitKeyDown(i,e)} style={slotStyle}/>
              ))}
            </div>
            {error&&<p style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:'#c23b0e',margin:0,textAlign:'center'}}>{error}</p>}
            <button type="submit" disabled={loading||!allFilled}
              style={{width:'100%',maxWidth:320,height:56,borderRadius:32,border:'none',background:allFilled?'#c23b0e':'#d4b4aa',color:'#fff',fontFamily:"'Cormorant Garamond',serif",fontStyle:'italic',fontWeight:500,fontSize:18,cursor:allFilled?'pointer':'default'}}>
              {loading?'Verifying…':'Verify'}
            </button>
          </form>
          <button onClick={handleResend}
            style={{marginTop:24,fontFamily:"'Jost',sans-serif",fontSize:13,color:'#c23b0e',background:'none',border:'none',cursor:'pointer',letterSpacing:'0.04em',textDecoration:'underline'}}>
            Resend code
          </button>
        </>
      )}
    </div>
  );
}

export default function SwishdApp() {
  const [screen,setScreen]=useState('welcome');
  // GATE_AUDIT_V1_2026_06_01 — default tab is match; discover removed
  const [tab,setTab]=useState("match");
  const [currentUser,setCurrentUser]=useState(null);
  const [authLoading,setAuthLoading]=useState(true);
  // SIGNUP_GATE_V1_2026_06_01
  const [signupModalOpen,setSignupModalOpen]=useState(false);
  const [pendingAction,setPendingAction]=useState(null);
  const [signupStage,setSignupStage]=useState('email');
  const [signupEmail,setSignupEmail]=useState('');
  const [signupCode,setSignupCode]=useState('');
  const [signupError,setSignupError]=useState('');
  const [signupLoading,setSignupLoading]=useState(false);

  // ── SITE_GATE_V1_2026_09_24 — site-wide password gate, sits in front of all routing
  const [siteUnlocked,setSiteUnlocked]=useState(()=>{
    try{ return localStorage.getItem('swishd_gate_ok')==='1'; }catch(e){ return false; }
  });
  const [gateInput,setGateInput]=useState('');
  const [gateError,setGateError]=useState('');
  const [gateChecking,setGateChecking]=useState(false);

  const submitGate=async(e)=>{
    if(e) e.preventDefault();
    if(gateChecking||!gateInput) return;
    setGateChecking(true);
    setGateError('');
    try{
      const r=await fetch('/api/gate-check',{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({password:gateInput}),
      });
      const d=await r.json().catch(()=>({ok:false}));
      if(d&&d.ok){
        try{ localStorage.setItem('swishd_gate_ok','1'); }catch(err){}
        setGateInput('');
        setSiteUnlocked(true);
      }else{
        setGateError('Incorrect password.');
      }
    }catch(err){
      setGateError('Incorrect password.');
    }finally{
      setGateChecking(false);
    }
  };

  // ── Auth listener + guest routing — SIGNUP_GATE_V1_2026_06_01
  useEffect(()=>{
    let mounted=true;
    (async()=>{
      const {data:{session}}=await supabase.auth.getSession();
      if(!mounted)return;
      setCurrentUser(session?.user||null);
      setAuthLoading(false);
      // SIGNUP_GATE_V1_2026_06_01 — allow guest browsing after age gate
      const ageConfirmed=localStorage.getItem('swishd_age_confirmed')==='1';
      if(session||ageConfirmed) setScreen('main');
    })();
    const {data:{subscription}}=supabase.auth.onAuthStateChange((_,session)=>{
      if(!mounted)return;
      setCurrentUser(session?.user||null);
      setAuthLoading(false);
      if(session) setScreen('main');
    });
    return ()=>{mounted=false;subscription.unsubscribe();};
  },[]);

  const goSignIn = () => setScreen(localStorage.getItem('swishd_age_confirmed') ? 'signup' : 'age_gate');
  // SIGNUP_GATE_V1_2026_06_01 — wraps any action that requires auth
  const requireAuth = (action, actionLabel) => {
    if (currentUser) {
      action();
    } else {
      setPendingAction({run:action,label:actionLabel});
      setSignupStage('email');
      setSignupEmail('');
      setSignupCode('');
      setSignupError('');
      setSignupModalOpen(true);
    }
  };

  // ── Load impressions from Supabase when user logs in
  useEffect(()=>{
    if(!currentUser)return;
    supabase.from('impressions').select('*').eq('user_id',currentUser.id).order('created_at',{ascending:false})
      .then(({data})=>{if(data&&data.length>0)setImpressions(data.map(d=>({id:d.id,wine:d.wine||"",venue:d.venue||"",date:d.drink_date||"",with:d.paired_with||"",notes:d.notes||"",details:d.details||"",rating:d.rating||0})));});
  },[currentUser]);
  const [history,setHistory]=useState(HISTORY_INIT);
  const [palate,setPalate]=useState(null);
  const [scanText,setScanText]=useState("");
  const [scanResult,setScanResult]=useState(null);
  const [scanning,setScanning]=useState(false);
  const [agentMsgs,setAgentMsgs]=useState([]);
  const [agentInput,setAgentInput]=useState("");
  const [agentLoading,setAgentLoading]=useState(false);
  const [scanMenuText,setScanMenuText]=useState("");
  // ── Scan mode (menu | bottle) ─────────────────────────
  const [scanMode,setScanMode]=useState('menu');
  // Bottle scan output — single object, not a list
  const [bottleData,setBottleData]=useState(null);
  // ── Extraction-flow state ─────
  const [extractedItems,setExtractedItems]=useState([]);   // [{name, category, price, producer, drank, notes, match_score?, rank?, reason?, menu_item_id?, ...}, ...]
  // The menu_scans.id for the current scan (menu OR bottle). Used to link saved impressions and persist rankings.
  const [currentScanId,setCurrentScanId]=useState(null);
  // ── Food re-ranking (Increment 5) ─────
  const [foodModalOpen,setFoodModalOpen]=useState(false);
  const [foodModalStage,setFoodModalStage]=useState('ask'); // 'ask' (Yes/No) or 'input' (text field)
  const [foodPromptShown,setFoodPromptShown]=useState(false); // ensure modal opens only once per scan session
  const [foodInput,setFoodInput]=useState("");
  const [foodActive,setFoodActive]=useState(""); // the food we're currently re-ranked against ("" = none)
  const [foodReranking,setFoodReranking]=useState(false);
  // When true, the current rerank had no good pairing signal on the menu.
  // UI shows "Limited pairing match — sorted by your palate" caption.
  const [limitedPairing,setLimitedPairing]=useState(false);
  // ── Redirect flow (Select #2 → My Story) ─────
  // Track distinct drinks ever ticked this scan session, whether food modal was resolved, and whether redirect already fired.
  const [sessionSelectedIds,setSessionSelectedIds]=useState(()=>new Set());
  // Per-card "show reason" toggle. Top match (#1) is always shown. Other cards reveal on tap.
  // Reset on each scan via setExtractedItems flow (the _id of each card is regenerated per scan).
  const [expandedReasonIds,setExpandedReasonIds]=useState(()=>new Set());
  const [foodModalResolved,setFoodModalResolved]=useState(false);
  const [redirectFired,setRedirectFired]=useState(false);
  // ── Summary screen state ─────
  // After the second distinct tick (post-modal-resolved), the scan tab transitions to a summary screen
  // showing the latest selected drink + paired food (if any). Updates with each subsequent tick.
  // 'ranking' = normal ranked list view, 'summary' = the closure screen.
  const [scanPhase,setScanPhase]=useState('ranking');
  const [summaryDrink,setSummaryDrink]=useState(null);
  // Track the impression_id of the first-ticked drink in this scan session (so we can delete
  // it on food rerank — only one specific row, not all entries on the menu).
  const [firstSelectedImpressionId,setFirstSelectedImpressionId]=useState(null);

  // Interaction events: fire one *_viewed event per scanPhase transition.
  // Skipped for 'ranking' on initial mount (when no scan exists yet) to avoid noise.
  useEffect(()=>{
    if (scanPhase === 'ranking' && extractedItems.length === 0) return;
    const eventName = {
      ranking: 'palate_ranking_viewed',
      pairing_prompt: 'food_pairing_prompt_viewed',
      food_input: 'food_input_viewed',
      apply_confirm: 'update_ranking_card_viewed',
      summary: 'summary_viewed',
    }[scanPhase];
    if (!eventName) return;
    writeInteractionEvent({
      eventName,
      scanId: currentScanId,
      properties: { food_active: foodActive || null },
    }).catch(()=>{});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanPhase]);
  // ── Food OCR (single-dish scan inside the food modal) ─────
  const [foodOcrLoading,setFoodOcrLoading]=useState(false);
  const [foodOcrProgress,setFoodOcrProgress]=useState(0);
  const [foodOcrError,setFoodOcrError]=useState("");
  const foodCameraRef = useRef(null);
  const foodGalleryRef = useRef(null);
  const [scanRawText,setScanRawText]=useState("");          // exact text we sent to AI (for logging)
  const [scanSaving,setScanSaving]=useState(false);
  const [ranking,setRanking]=useState(false);              // true while we compute rankings
  // ── OCR state ─────────────────────────────────────────
  const [ocrLoading,setOcrLoading]=useState(false);
  const [ocrProgress,setOcrProgress]=useState(0);
  const [ocrPreviewURL,setOcrPreviewURL]=useState("");
  const [ocrError,setOcrError]=useState("");
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [cameraPermission,setCameraPermission]=useState(()=>localStorage.getItem("swishd_camera_ok")==="true");
  const [showCameraPrompt,setShowCameraPrompt]=useState(false);
  const [food,setFood]=useState("");
  const [wineStyle,setWineStyle]=useState("");
  const [picks,setPicks]=useState([]);
  const [aiInsight,setAiInsight]=useState("");
  const [loadingAI,setLoadingAI]=useState(false);
  const [selectedWine,setSelectedWine]=useState(null);
  const [addWine,setAddWine]=useState(null);
  const [diaryNote,setDiaryNote]=useState("");
  const [diaryRating,setDiaryRating]=useState(5);
  const [diaryWith,setDiaryWith]=useState("");
  const [profIdx,setProfIdx]=useState(0);
  const [connections,setConnections]=useState(()=>{
    const now=Date.now();
    const day=24*60*60*1000;
    return [
      {profile:PROFILES[0],connectedAt:now-day*0,  lastInteraction:now-day*0},
      {profile:PROFILES[1],connectedAt:now-day*5,  lastInteraction:now-day*5},
      {profile:PROFILES[2],connectedAt:now-day*12, lastInteraction:now-day*12},
    ];
  });
  const [chatProfile,setChatProfile]=useState(null);
  const [toast,setToast]=useState(null);
  const [quizStep,setQuizStep]=useState(0);
  const [quizScores,setQuizScores]=useState({});
  const [quizSelections,setQuizSelections]=useState({});
  const [quizResult,setQuizResult]=useState(null);
const [quizSaving,setQuizSaving]=useState(false);
  const [quizResumeOffer,setQuizResumeOffer]=useState(null);
  // Offer resume each time the quiz screen opens.
  // Skipped when the quiz was opened already resumed (step > 0) by the quiz-required modal.
  useEffect(()=>{ if(screen==='palate-quiz'&&quizStep===0) setQuizResumeOffer(loadQuizProgress()); },[screen]);
  const [quizRequiredOpen,setQuizRequiredOpen]=useState(false);
  const startQuizFresh=()=>{ setPalateIntroCancelled(false); setQuizStep(0); setQuizScores({}); setQuizSelections({}); setQuizResult(null); setScreen('palate-quiz'); };
  // Quiz-required modal CTA: resumes saved progress directly, otherwise starts fresh.
  const openQuizFromGate=()=>{
    setQuizRequiredOpen(false);
    const p=loadQuizProgress();
    if(!p){ startQuizFresh(); return; }
    setQuizSelections(p.selections); setQuizScores(computeQuizScores(p.selections)); setQuizStep(p.step); setQuizResult(null); setQuizResumeOffer(null);
    setScreen('palate-quiz');
  };
  // Persist progress on every step/selection change. Skipped while the resume offer is pending
  // (so the entry reset can't clobber the save), after completion, and for an untouched quiz.
  useEffect(()=>{
    if(screen!=='palate-quiz'||quizResumeOffer||quizResult) return;
    if(quizStep===0&&Object.keys(quizSelections).length===0) return;
    saveQuizProgress(quizStep,quizSelections);
  },[screen,quizStep,quizSelections,quizResumeOffer,quizResult]);
  const [under21,setUnder21]=useState(false);
  const [authMode,setAuthMode]=useState('signup');
  const [palateIntroCancelled,setPalateIntroCancelled]=useState(false);
const [lookingFor,setLookingFor]=useState("woman");

  // ── Impressions (diary entries saved from scan or diary page)
  const [impressions,setImpressions]=useState([]);
  // ── Inline edit state (My Story cards) ─────
  // When non-null, this impression's card is expanded with editable fields.
  // Holds the working copy; on Save we patch the row in DB and refresh local state.
  const [editingImpId,setEditingImpId]=useState(null);
  const [editingDraft,setEditingDraft]=useState(null);
  const [editingSaving,setEditingSaving]=useState(false);
  const [impWine,setImpWine]=useState("");
  const [impVenue,setImpVenue]=useState("");
  const [impDate,setImpDate]=useState("");
  const [impWith,setImpWith]=useState("");
const [impMood,setImpMood]=useState({});
  const [moodPromptIdx,setMoodPromptIdx]=useState(null);
  const [moodModalIdx,setMoodModalIdx]=useState(null);
  const [moodOther,setMoodOther]=useState("");
  const [moodOtherOpen,setMoodOtherOpen]=useState(false);
  const [impNotes,setImpNotes]=useState("");
  const [impDetails,setImpDetails]=useState("");
  const [impRating,setImpRating]=useState(0);

  const saveImpression=async()=>{
    if(!impWine.trim()&&!impVenue.trim())return;
    const entry={
      id:`imp${Date.now()}`,
      wine:impWine,venue:impVenue,
      date:impDate||new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),
      with:impWith,notes:impNotes,details:impDetails,rating:impRating,
    };
    setImpressions(prev=>[entry,...prev]);
    // Persist to Supabase
    const saved=await dbSaveImpression(entry);
    if(saved) setImpressions(prev=>prev.map(p=>p.id===entry.id?{...p,id:saved.id}:p));
    setImpWine("");setImpVenue("");setImpDate("");setImpWith("");setImpMood({});setImpNotes("");setImpDetails("");setImpRating(0);
    showToast("Saved to Impressions ✓",C.coral);
    setTab("diary");
  };

  // ── Profile state
  const [profileData,setProfileData]=useState({name:"",user_name:"",dob:"",occupation:"",personality:"",location:"",photo_url:"",palate_fingerprint:null,quiz_selections:null});
  const [profileSaved,setProfileSaved]=useState(false);

  // A completed fingerprint can live in the in-session quiz result, in profileData (loaded on
  // startup), or only in the DB (e.g. user signed in mid-session) — check them in that order.
  const hasPalateFingerprint=async()=>{
    if(quizResult?.fingerprint||profileData.palate_fingerprint) return true;
    if(!currentUser) return false;
    try{
      const {data}=await supabase.from('swishd_profiles').select('palate_fingerprint').eq('user_id',currentUser.id).maybeSingle();
      if(data?.palate_fingerprint){ setProfileData(p=>({...p,palate_fingerprint:data.palate_fingerprint})); return true; }
    }catch(e){ console.warn('[quiz gate] fingerprint lookup failed',e); }
    return false;
  };

  // Returns false (and opens the quiz-required modal) when no fingerprint exists.
  const saveProfile=async()=>{
    if(!(await hasPalateFingerprint())){ setQuizRequiredOpen(true); return false; }
    setProfileSaved(true);
    dbSaveProfile(profileData, lookingFor).then(ok=>{
      if(!ok) console.error('Profile save failed');
    });
    showToast("Profile saved ✓",C.coral);
    return true;
  };

  useEffect(()=>{setPalate(buildPalate(history));},[history]);

  // Load persisted data from Supabase on startup
  useEffect(()=>{
    loadImpressions().then(data=>{ if(data&&data.length>0) setImpressions(data); });
    loadConnections().then(data=>{ if(data&&data.length>0) setConnections(data); });
    loadProfile().then(data=>{
      if(data){
        setProfileData({name:data.name||"",user_name:data.user_name||"",dob:data.dob||"",occupation:data.occupation||"",personality:data.personality||"",location:data.location||"",photo_url:data.photo_url||"",palate_fingerprint:data.palate_fingerprint||null,quiz_selections:data.quiz_selections||null});
        if(data.looking_for) setLookingFor(data.looking_for);
        if(data.palate_fingerprint) setProfileSaved(true);
      }
    });
  },[]);

  // ── SEED INGESTION (Step 5) ─────────────────────────────────────────
  // On first run for this browser, ingest the hand-tuned drink flavor blueprints
  // so the cache is hot for canonical drinks (Negroni, Cabernet, Chardonnay, etc).
  // Idempotent: checks the DB for what's already there; only writes the missing ones.
  // Marks a localStorage flag with the seed batch length so we re-run when seeds grow.
  useEffect(() => {
    const SEED_FLAG = 'swishd_seeds_v1';
    const expectedTag = `len=${DRINK_FLAVOR_SEEDS.length}`;
    if (localStorage.getItem(SEED_FLAG) === expectedTag) return;
    (async () => {
      try {
        const seedNames = DRINK_FLAVOR_SEEDS.map(s => s.name);
        const cache = await loadDrinkFlavorsBatch(seedNames);
        const missing = DRINK_FLAVOR_SEEDS.filter(s => !cache[normalizeDrinkKey(s.name)]);
        for (const s of missing) {
          await saveDrinkFlavor({ name: s.name, category: s.category, vector: s.vector, source: 'curated' });
        }
        localStorage.setItem(SEED_FLAG, expectedTag);
        if (missing.length > 0) console.log(`[swishd] seeded ${missing.length} drink flavor blueprints`);
      } catch (e) {
        console.error('seed ingestion failed:', e);
      }
    })();
  }, []);

  // VIEWPORT_LOCK_V2_2026_05_28 — lock body bounce while on onboarding screens (Landing/age_gate/signup)
  useEffect(() => {
    if (screen === 'welcome' || screen === 'age_gate' || screen === 'signup') {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
    };
  }, [screen]);


  const showToast=(msg,col=C.ink)=>{setToast({msg,col});setTimeout(()=>setToast(null),2600);};

  // ── OCR: Image → cleaned menu text ────────────────────────────────
  // Light pre-processing per Step 3 of the scan spec:
  //  • drop section headers (DRINK MENU, WINE LIST, COCKTAILS, etc.)
  //  • drop pure noise lines (page numbers, single chars, dashes)
  //  • preserve line breaks, normalize whitespace
  // Common OCR misreads in price tokens. Applied per-token so we don't mangle drink names.
  // Examples: "S14" → "$14", "l4" → "14", "I4" → "14", "$I6" → "$16", "O" in digit context → "0".
  const fixOcrPriceToken = (tok) => {
    // Token looks price-ish if it contains a digit or starts with $/S
    if (!/[\d$Sl I]/i.test(tok)) return tok;
    // Step 1: leading S followed by digits → $
    let t = tok.replace(/^S(\d)/, '$$$1');
    // Step 2: stray $/letter then digits in middle (e.g. "$I6", "$l4")
    t = t.replace(/\$([IlO])(\d)/gi, (_, ch, d) => {
      const fix = ch === 'O' ? '0' : '1';
      return '$' + fix + d;
    });
    // Step 3: lone digit-shaped letters wedged between digits (e.g. "1l", "2O")
    t = t.replace(/(\d)([IlO])(\d?)/g, (_, a, ch, b) => {
      const fix = ch === 'O' ? '0' : '1';
      return a + fix + b;
    });
    return t;
  };

  const cleanMenuText = (raw) => {
    const NOISE_HEADERS = /^(drink menu|drinks menu|wine list|wine menu|cocktail menu|beverage menu|by the glass|by the bottle|menu|cocktails?|wines?|beers?|spirits?|reds?|whites?|ros[eé]s?|sparkling|champagne|on tap|signature|classics?|seasonal)$/i;
    // FIX C: browser chrome and screenshot artifacts. Status bar, URL bar, tab counts.
    // Example garbage from screenshots: "12:56 NEE 71", "he thevoxagency.com M", "+", "<", ">"
    const BROWSER_CHROME_RE = /^(\d{1,2}:\d{2}.*\d{1,3}|\w{1,3}\s+(https?:\/\/|www\.|[a-z]+\.com).*|\d{1,2}[%]?\s*$|^[+<>•\-]+\s*$)/i;
    const _afterClean_DEBUG = raw
      // FIX 1: Bullets / dot-leaders become line breaks BEFORE splitting on newlines.
      // Common OCR garbage: •, ●, ▪, ▫, ◦, ‣, ⁃, plus em-dash used as a bullet leader.
      .replace(/[•●▪▫◦‣⁃·]+/g, '\n')
      // Bullet-style hyphens at the START of a line (preserve in-name hyphens like Côtes-du-Rhône)
      .replace(/(^|\n)\s*[-*+]\s+/g, '$1')
      .split(/\r?\n/)
      .map(l => {
        let s = l.replace(/\s+/g, " ").trim();
        // FIX A: OCR-misread bullets. Tesseract often reads •, ●, ◦ as a stray lowercase
        // letter (e, o, eo, c) followed by a space then the actual content. Detect a
        // single 1-2 char lowercase prefix followed by a capitalized word and strip it.
        // Conservative: only strips if the rest of the line looks like a real drink (starts capital).
        s = s.replace(/^([a-z]{1,2})\s+([A-Z])/, '$2');
        // FIX 1 cont: strip non-alphanumeric symbols EXCEPT the ones that carry meaning:
        //   $ (price), . (decimal/abbrev), , (separator), — - – (separator), & ' (drink names),
        //   accents (é è à á í ó ú ñ ç), / (slash like "Cab/Merlot")
        // Tilde, backtick, asterisk, underscore, brackets, pipes etc. → drop.
        s = s.replace(/[~`*_<>{}|\\\[\]()=#@^"]/g, '');
        // FIX 2: normalize OCR errors in price tokens, per-token to avoid mangling words.
        s = s.split(' ').map(fixOcrPriceToken).join(' ');
        return s.trim();
      })
      .filter(l => {
        if (!l) return false;
        // PRICE_LINE_WHITELIST: standalone numeric prices (e.g. "24", "25", "108") and glass/bottle
        // formats ("14/63", "16 / 72") are valid drink data. Whitelist BEFORE the length/digit filters
        // would otherwise discard them as noise.
        const STANDALONE_PRICE_RE = /^\s*\$?\s*\d{1,4}(?:[.,]\d{2})?\s*$/;
        const WINE_PRICE_PATTERN = /^\s*\d{1,3}\s*\/\s*\d{1,3}\s*$/;
        if (STANDALONE_PRICE_RE.test(l)) return true;
        if (WINE_PRICE_PATTERN.test(l)) return true;
        if (l.length < 3) return false;                // single chars / "—"
        if (/^[\d\W_]+$/.test(l)) return false;         // pure numbers/punct (page nums)
        if (NOISE_HEADERS.test(l)) return false;        // section headers
        if (BROWSER_CHROME_RE.test(l)) return false;    // browser status/url bars
        return true;
      })
      .join("\n");
    return _afterClean_DEBUG;
  };

  // Decide whether OCR output is too scrambled to be useful.
  // Two-gate check, mirrors how ID verification works:
  //   Gate 1 (basic): at least 30% of lines have a 3+ letter run and are mostly alphabetic
  //   Gate 2 (semantic): at least 2 lines contain a wine/drink-name shape — a capitalized
  //                       word 3-30 chars long, optionally followed by more capitalized words
  // Both must pass. Either failure → garbage.
  const isGarbageOCR = (cleanedText) => {
    const lines = (cleanedText || '').split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return true;

    // Gate 1
    const wordLikeLines = lines.filter(l => {
      const alphaRun = /[A-Za-z]{3,}/.test(l);
      if (!alphaRun) return false;
      const alphaCount = (l.match(/[A-Za-z]/g) || []).length;
      return alphaCount / l.length >= 0.5;
    });
    if (wordLikeLines.length / lines.length < 0.3) return true;

    // Gate 2 — wine-name shape: a Capitalized word 3-30 chars, with at least one more
    // capitalized or recognizable word on the same line. Catches "Pinot Noir", "Sancerre,",
    // "Cabernet Sauvignon", etc. Rejects "I V s e t r 4" style noise even if Gate 1 passed.
    const WINE_SHAPE = /\b[A-Z][a-zA-Z'’é]{2,29}(?:\s+[A-Z][a-zA-Z'’é-]{1,29})*/;
    const wineShapedLines = lines.filter(l => WINE_SHAPE.test(l));
    if (wineShapedLines.length < 2) return true;

    return false;
  };

  // ── IMAGE PREPROCESSING FOR OCR (OCR_PREPROCESS_V1_2026_05_20) ──────────
  // Many real-world menus use textured backgrounds, gray-on-cream type, decorative
  // fonts. Tesseract's confidence drops sharply on these, triggering the LOW_CONFIDENCE
  // rejection even though the text is human-readable. We preprocess the image to a
  // binarized form (Otsu's method, auto-threshold) before handing it to Tesseract.
  //
  // Effect: gray-on-cream → black-on-white, faint diagonal textures → washed to white,
  // bold display fonts → preserved at full contrast. Typically lifts confidence by 20-40 points.
  const preprocessImageForOCR = async (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        try {
          // Upscale if the image is small — Tesseract needs ~300 DPI equivalent.
          // A 600px-wide phone photo of a menu often comes out to ~100 DPI; upscale 2x.
          const targetMaxWidth = 1800;
          let w = img.naturalWidth;
          let h = img.naturalHeight;
          if (w < targetMaxWidth) {
            const scale = Math.min(targetMaxWidth / w, 3);
            w = Math.round(w * scale);
            h = Math.round(h * scale);
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          // Smoothing helps when upscaling
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, w, h);

          // Convert to grayscale + collect histogram for Otsu threshold
          const imgData = ctx.getImageData(0, 0, w, h);
          const px = imgData.data;
          const hist = new Array(256).fill(0);
          const total = px.length / 4;
          for (let i = 0; i < px.length; i += 4) {
            const g = (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) | 0;
            px[i] = px[i + 1] = px[i + 2] = g;
            hist[g]++;
          }

          // Otsu's method — find threshold that maximizes inter-class variance
          let sum = 0;
          for (let t = 0; t < 256; t++) sum += t * hist[t];
          let sumB = 0, wB = 0, maxVar = 0, threshold = 128;
          for (let t = 0; t < 256; t++) {
            wB += hist[t];
            if (wB === 0) continue;
            const wF = total - wB;
            if (wF === 0) break;
            sumB += t * hist[t];
            const mB = sumB / wB;
            const mF = (sum - sumB) / wF;
            const between = wB * wF * (mB - mF) * (mB - mF);
            if (between > maxVar) { maxVar = between; threshold = t; }
          }

          // Detect orientation: if more pixels are dark than light, image is dark-on-light
          // (typical menu) — keep polarity. If light-on-dark (bar menu chalkboard), invert.
          let darkCount = 0;
          for (let i = 0; i < 256; i++) if (i < threshold) darkCount += hist[i];
          const lightCount = total - darkCount;
          const invertNeeded = darkCount > lightCount;
          // Wait — we want text to be dark on light background, which means MOST pixels
          // should be light (background) and a minority dark (text). If darkCount > lightCount,
          // image is currently inverted (light text on dark background) — flip it.

          // Apply binarization
          for (let i = 0; i < px.length; i += 4) {
            const isLight = px[i] >= threshold;
            // Final value: invertNeeded toggles polarity so output is always dark-text-on-light
            const out = invertNeeded ? (isLight ? 0 : 255) : (isLight ? 255 : 0);
            px[i] = px[i + 1] = px[i + 2] = out;
            // alpha stays
          }
          ctx.putImageData(imgData, 0, 0);

          canvas.toBlob((blob) => {
            URL.revokeObjectURL(url);
            // If somehow blob is null, fall back to the original file
            resolve(blob || file);
          }, 'image/png');
        } catch (e) {
          console.warn('[swishd] preprocessImageForOCR failed; using original:', e);
          URL.revokeObjectURL(url);
          resolve(file);
        }
      };
      img.onerror = () => {
        console.warn('[swishd] preprocessImageForOCR: image load failed; using original');
        URL.revokeObjectURL(url);
        resolve(file);
      };
      img.src = url;
    });
  };

  const runOCR = async (file) => {
    if (!file) return;
    setOcrError("");
    setOcrLoading(true);
    setOcrProgress(0);

    // Reject non-image files immediately. Tesseract throws a confusing error on PDFs etc.
    if (!file.type || !file.type.startsWith('image/')) {
      setOcrError('NOT_IMAGE');
      setOcrLoading(false);
      return;
    }

    // Show preview thumbnail (use the ORIGINAL, not the binarized version)
    const previewURL = URL.createObjectURL(file);
    setOcrPreviewURL(previewURL);

    try {
      // Preprocess: binarize + upscale. Falls back to original on error.
      const processedFile = await preprocessImageForOCR(file);
      console.log('[swishd] OCR_PREPROCESS_V1: image preprocessed (binarized + auto-threshold)');

      // Lazy-load tesseract.js so it doesn't bloat the initial bundle
      const Tesseract = (await import("tesseract.js")).default;
      const { data } = await Tesseract.recognize(processedFile, "eng", {
        logger: (m) => {
          if (m.status === "recognizing text" && typeof m.progress === "number") {
            setOcrProgress(Math.round(m.progress * 100));
          }
        },
      });
      const cleaned = cleanMenuText(data?.text || "");
      // OCR trace: surface raw Tesseract output and post-clean text for debugging.
      console.log('[swishd] OCR raw Tesseract text (first 1200 chars):', (data?.text || '').slice(0, 1200));
      console.log('[swishd] OCR post-clean text (first 1200 chars):', cleaned.slice(0, 1200));
      console.log('[swishd] OCR confidence:', data?.confidence);
      // Auto-Retake threshold lowered from 55 to 40 now that preprocessing handles
      // most low-contrast cases. With preprocessing, "low confidence" usually means the
      // image is truly unreadable (blurred, folded, glared). 40 still catches those.
      const ocrConfidence = typeof data?.confidence === 'number' ? data.confidence : 100;
      if (!cleaned) {
        setOcrError("EMPTY"); // sentinel for "no text at all"
      } else if (ocrConfidence < 40) {
        setOcrError("LOW_CONFIDENCE"); // sentinel for "characters readable but mostly guessed"
        setScanText(cleaned); // still surface the cleaned text so user sees what came out
      } else if (isGarbageOCR(cleaned)) {
        setOcrError("GARBAGE"); // sentinel for "scrambled output"
        // We still drop the cleaned text into scanText so the user can see what came out and decide
        setScanText(cleaned);
      } else {
        setScanText(cleaned);
      }
    } catch (e) {
      console.error("OCR failed", e);
      setOcrError("FAILED"); // sentinel for "tesseract threw"
    } finally {
      setOcrLoading(false);
    }
  };

  // ── AI-DIRECT MENU EXTRACTION (AI_DIRECT_V1_2026_05_20) ────────────────
  // Architecture: Tesseract OCR → minimal cleanup → AI (Haiku) → structured items.
  // No buffer loop, no splitOnSeparator, no varietal regex, no localExtract fallback.
  // The AI parses menu structure directly from multi-line raw text.
  //
  // Output shape per item: { name, category, price, producer, vintage, drank, notes, _id }
  // Output shape preserved from prior implementation so ranking / UI / persistence work unchanged.

  const EXTRACTION_MODEL = 'claude-haiku-4-5-20251001';

  // Minimal text cleanup. Strip browser chrome, drop pure-symbol/single-char noise.
  // Keep multi-line structure intact — the AI uses line position as a structural cue
  // (varietal header above producer line above price line, etc.).
  const minimalCleanup = (raw) => {
    const BROWSER_CHROME_RE = /^(https?:\/\/|www\.|\d{1,2}:\d{2}\s*(am|pm)?|battery|wifi|signal)/i;
    return (raw || '')
      .split(/\r?\n/)
      .map(l => l.replace(/\s+/g, ' ').trim())
      .filter(l => {
        if (!l) return false;
        if (l.length < 2) return false;                 // single chars / em-dash alone
        if (/^[\W_]+$/.test(l)) return false;           // pure punctuation
        if (BROWSER_CHROME_RE.test(l)) return false;    // browser status bars
        return true;
      })
      .join('\n');
  };

  const runScan = async () => {
    if (!scanText.trim()) return;
    if (scanMode === 'bottle') {
      return runBottleExtraction();
    }
    setScanning(true);
    setScanMenuText(scanText);
    setAgentMsgs([]);
    setExtractedItems([]);
    setCurrentScanId(null);
    setSessionSelectedIds(new Set());
    setExpandedReasonIds(new Set());
    setFoodModalResolved(false);
    setRedirectFired(false);
    setFoodPromptShown(false);
    setFoodActive('');
    setScanPhase('ranking');
    setSummaryDrink(null);
    setFirstSelectedImpressionId(null);
    setLimitedPairing(false);

    const cleanedText = minimalCleanup(scanText);
    setScanRawText(cleanedText);
    console.log('[swishd] AI_DIRECT_V1 cleaned input (first 1200 chars):', cleanedText.slice(0, 1200));

    let items = [];
    let aiRaw = '';
    let parseFailed = false;

    try {
      const systemPrompt =
        `You are a strict menu extraction service for a wine and cocktail app. ` +
        `You parse OCR'd menu text (potentially noisy, with merged lines, OCR misreads, and varied layouts) ` +
        `into structured JSON. You return ONLY a JSON array. No prose. No markdown fences. No commentary.`;

      const userPrompt =
        `Extract every drink from this OCR'd menu text. Return a JSON array of objects.\n\n` +
        `MENU TEXT (multi-line, raw):\n` +
        `===\n${cleanedText}\n===\n\n` +
        `OBJECT SCHEMA — each drink is one object with these fields:\n` +
        `  "name"     : string. The display name. For wines, format is "Varietal — Producer" (use em-dash). For cocktails/spirits, the drink name only.\n` +
        `  "category" : "wine" | "cocktail" | "spirit" | "unknown"\n` +
        `  "producer" : string or null. For WINES, the producer + bottling (e.g. "Laroche La Chevalière"). For COCKTAILS, the ingredient list as a comma-separated string in lowercase, with major brand names preserved (e.g. "Casamigos Reposado Tequila, Caribbean pineapple & poblano chile liqueurs, caramelized pineapple juice, habanero-infused agave, lime, mango chile sal de flor"). For SPIRITS, the distillery or brand if separable; otherwise null.\n` +
        `  "vintage"  : integer or null. The 4-digit year. If OCR shows 2-digit (e.g. "23" or "'23"), interpret as 20XX where XX = the 2 digits. Range 1980-2030.\n` +
        `  "price"    : number or null. Glass price preferred. For "X / Y" format (glass/bottle), use X. Never use a 2-digit number that is clearly a vintage (e.g. ", 23") as price.\n` +
        `  "varietal" : string or null. The grape varietal in title case (Chardonnay, Sancerre, Sauvignon Blanc, etc.). Null for cocktails/spirits. For wines where the menu lists a region as the heading (e.g. "Sancerre" means Sauvignon Blanc from Sancerre), use the heading as varietal.\n\n` +
        `MENU PATTERNS YOU MUST HANDLE:\n\n` +
        `PATTERN A — Vertical wine block (varietal header / producer / price):\n` +
        `  Input:\n` +
        `    CHARDONNAY\n` +
        `    LAROCHE LA CHEVALIÈRE, PAYS D'OC, FRA, 23\n` +
        `    16 / 72\n` +
        `    CHARDONNAY\n` +
        `    PRESQU'ILE WINERY, SANTA BARBARA COUNTY, 23\n` +
        `    22 / 99\n` +
        `  Output:\n` +
        `    {"name":"Chardonnay — Laroche La Chevalière","category":"wine","producer":"Laroche La Chevalière","vintage":2023,"price":16,"varietal":"Chardonnay"}\n` +
        `    {"name":"Chardonnay — Presqu'ile Winery","category":"wine","producer":"Presqu'ile Winery","vintage":2023,"price":22,"varietal":"Chardonnay"}\n` +
        `  Critical: two lines with the same varietal but different producers are DIFFERENT drinks. NEVER merge them.\n\n` +
        `PATTERN B — Section heading above varietal:\n` +
        `  Input:\n` +
        `    ROSE & WHITE\n` +
        `    ROSÉ\n` +
        `    CHÂTEAU D'ESCLANS WHISPERING ANGEL, CÔTES DE PROVENCE, FRA, 23\n` +
        `    15 / 63\n` +
        `  Output:\n` +
        `    {"name":"Rosé — Château d'Esclans Whispering Angel","category":"wine","producer":"Château d'Esclans Whispering Angel","vintage":2023,"price":15,"varietal":"Rosé"}\n` +
        `  "ROSE & WHITE" is a page heading — skip it entirely. The varietal is the next line ("ROSÉ").\n\n` +
        `PATTERN C — Inline cocktail (name + ingredients + price on one line):\n` +
        `  Input: "Negroni — gin, Campari, sweet vermouth $14"\n` +
        `  Output: {"name":"Negroni","category":"cocktail","producer":"gin, Campari, sweet vermouth","vintage":null,"price":14,"varietal":null}\n` +
        `  The text after the dash is INGREDIENTS, not separate drinks. The name is just "Negroni". Put the ingredient list in "producer".\n\n` +
        `PATTERN D — Stacked cocktail (name line, then ingredients line, then price line):\n` +
        `  Input:\n` +
        `    Morning Glory\n` +
        `    gin, lemon, honey, sparkling wine\n` +
        `    14\n` +
        `  Output: {"name":"Morning Glory","category":"cocktail","producer":"gin, lemon, honey, sparkling wine","vintage":null,"price":14,"varietal":null}\n\n` +
        `PATTERN E — All-caps stacked cocktail (Vine 75 style):\n` +
        `  Input:\n` +
        `    APHRODITE\n` +
        `    gin, elderflower, rose, lemon, prosecco 17\n` +
        `  Output: {"name":"Aphrodite","category":"cocktail","producer":"gin, elderflower, rose, lemon, prosecco","vintage":null,"price":17,"varietal":null}\n` +
        `  Title-case the name. Price is the trailing number. Ingredients (without the price) go in "producer".\n\n` +
        `PATTERN F — Creative name + style subtitle (Inspired Cocktails style):\n` +
        `  Input:\n` +
        `    COZUMEL\n` +
        `    SPICY MARGARITA\n` +
        `    Casamigos Reposado Tequila, Caribbean pineapple & poblano chile liqueurs,\n` +
        `    caramelized pineapple juice, habanero-infused agave, lime, mango chile sal de flor\n` +
        `    24\n` +
        `    CANNES\n` +
        `    CHAMPAGNE SOUR\n` +
        `    Grey Goose Essences Strawberry & Lemongrass Vodka, French rhubarb,\n` +
        `    lemon, strawberry, Cham-feign Rosé foam, berries\n` +
        `    24\n` +
        `  Output:\n` +
        `    {"name":"Cozumel — Spicy Margarita","category":"cocktail","producer":"Casamigos Reposado Tequila, Caribbean pineapple & poblano chile liqueurs, caramelized pineapple juice, habanero-infused agave, lime, mango chile sal de flor","vintage":null,"price":24,"varietal":null}\n` +
        `    {"name":"Cannes — Champagne Sour","category":"cocktail","producer":"Grey Goose Essences Strawberry & Lemongrass Vodka, French rhubarb, lemon, strawberry, Cham-feign Rosé foam, berries","vintage":null,"price":24,"varietal":null}\n` +
        `  Recognition: each drink is a BLOCK with this exact shape — (1) a short ALL-CAPS creative name on one line, (2) an ALL-CAPS style descriptor on the next line (e.g. "SPICY MARGARITA", "CHAMPAGNE SOUR", "WHITE NEGRONI", "A REIMAGINED CLASSIC", "VERDE BLOODY MARY", "PASSIONFRUIT CAIPIRINHA"), (3) one or more ingredient lines with commas, (4) a standalone numeric price line (e.g. "24", "25").\n` +
        `  Critical: the creative name AND the style subtitle together form ONE drink. Combine them as "Creative — Style" (title-case both, em-dash separator). NEVER emit two separate objects for the same block. NEVER emit a drink named just "Cozumel" — always pair it with its subtitle.\n` +
        `  The ingredient lines (joined into one comma-separated string) go in "producer". The trailing bare numeric line (e.g. "24") IS the price.\n\n` +
        `RULES:\n` +
        `- Output ONLY a JSON array. No prose. No markdown code fences. No leading or trailing text.\n` +
        `- If the menu is empty or unparseable, output [].\n` +
        `- Skip section headers: "WINE LIST", "COCKTAILS", "REDS", "WHITES", "BUBBLES", "BY THE GLASS", country names alone ("FRANCE", "ITALY").\n` +
        `- Skip beer entries entirely (IPA, lager, stout, ale, pilsner, hefeweizen, porter, brewery names).\n` +
        `- Classify Champagne, Prosecco, Cava, Crémant as "wine" (not their own category).\n` +
        `- A drink with 2+ liquid ingredients (gin, vodka, rum, mezcal, whisky, vermouth, bitters, liqueurs) is "cocktail" — even if one ingredient is a spirit name.\n` +
        `- "spirit" is reserved for plain bottle pours like "Macallan 18", "Hennessy VS" — a single brand with no ingredient list.\n` +
        `- VINTAGE vs PRICE: a trailing 2-digit number in a wine context (after region/country) is almost always vintage. A "$N" token, a "X / Y" glass/bottle pair, or a clearly-formatted price is price.\n` +
        `- OCR may have stripped apostrophes. "FRA, 23" likely means "FRA, '23" (vintage 2023). "FRA, 24" → vintage 2024.\n` +
        `- Title-case all name and producer fields. Preserve accents and apostrophes the OCR may have lost (e.g. "Chateau Desclans" → "Château d'Esclans" when reasonable).\n` +
        `- If you cannot tell category, use "unknown" — never guess.\n` +
        `- For wines: name MUST be "Varietal — Producer" format, never just varietal alone, never just producer alone.`;

      aiRaw = await callClaude(
        [{ role: 'user', content: userPrompt }],
        systemPrompt,
        2500,
        EXTRACTION_MODEL
      );

      console.log('[swishd] AI_DIRECT_V1 raw response (first 800 chars):', (aiRaw || '').slice(0, 800));

      // Parse — tolerate markdown fences if the model returns them anyway
      let jsonText = (aiRaw || '').trim();
      jsonText = jsonText.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '');
      const firstBracket = jsonText.indexOf('[');
      const lastBracket = jsonText.lastIndexOf(']');
      if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {
        jsonText = jsonText.slice(firstBracket, lastBracket + 1);
      }

      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) throw new Error('AI response not an array');

      // Normalize fields. Drop anything without a name.
      items = parsed
        .filter(it => it && typeof it.name === 'string' && it.name.trim().length > 0)
        .map(it => ({
          name: String(it.name).trim(),
          category: (it.category && ['wine', 'cocktail', 'spirit', 'unknown'].includes(String(it.category).toLowerCase()))
            ? String(it.category).toLowerCase()
            : 'unknown',
          price: (typeof it.price === 'number' && !Number.isNaN(it.price)) ? it.price : null,
          producer: (typeof it.producer === 'string' && it.producer.trim()) ? it.producer.trim() : '',
          vintage: (typeof it.vintage === 'number' && it.vintage >= 1980 && it.vintage <= 2030) ? it.vintage : null,
          varietal: (typeof it.varietal === 'string' && it.varietal.trim()) ? it.varietal.trim() : null,
        }));
    } catch (e) {
      parseFailed = true;
      console.error('[swishd] AI_DIRECT_V1 extraction failed:', e, 'raw:', (aiRaw || '').slice(0, 300));
    }

    if (items.length === 0) {
      console.warn('[swishd] AI_DIRECT_V1 returned no items.');
      setScanning(false);
      // Show empty ranking state so user sees the scan finished
      setExtractedItems([]);
      saveScan(scanText, JSON.stringify({ phase: 'extracted_empty', ai_raw: aiRaw, parse_failed: parseFailed }), impVenue || '').catch(() => {});
      return;
    }

    // ── ATTACH UI STATE ──────────────────────────────────────────────
    const withDrank = items.map(it => ({
      ...it,
      drank: false,
      notes: '',
      _id: Math.random().toString(36).slice(2, 9),
    }));
    setExtractedItems(withDrank);

    // ── PERSIST EXTRACTION (canonical menu_scans + menu_items) ──────
    (async () => {
      try {
        const scanId = await createMenuScan({
          source: 'menu',
          rawText: cleanedText,
          venueName: impVenue || '',
        });
        if (!scanId) return;
        setCurrentScanId(scanId);

        const itemsForDb = await Promise.all(
          items.map(async it => ({
            name: it.name,
            price: it.price,
            category: it.category,
            producer: it.producer || null,
            vintage: it.vintage,
            normalized_name: normalizeDrinkKey(it.name),
            extraction_hash: await computeExtractionHash({ name: it.name, price: it.price, vintage: it.vintage }),
          }))
        );

        const canonicalRows = await insertMenuItems(scanId, itemsForDb);

        if (canonicalRows.length > 0) {
          const hashToId = {};
          canonicalRows.forEach(r => { hashToId[r.extraction_hash] = r.id; });
          setExtractedItems(prev => prev.map((it, idx) => {
            const h = itemsForDb[idx]?.extraction_hash;
            return h && hashToId[h] ? { ...it, menu_item_id: hashToId[h] } : it;
          }));
        }
      } catch (e) {
        console.error('[swishd] menu_scans/menu_items persist failed:', e);
      }
    })();

    // ── DEBUG: palate vector ───────────────────────────────────────
    try {
      const pv = palateToVector(palate);
      console.log('[swishd] palate vector for current user:', pv);
    } catch (e) { /* non-blocking */ }

    // Legacy debug log
    saveScan(
      scanText,
      JSON.stringify({ phase: 'extracted', ai_raw: aiRaw, parsed: items, parse_failed: parseFailed }),
      impVenue || ''
    ).catch(() => {});

    setScanning(false);

    // ── RANKING PIPELINE ────────────────────────────────────────────
    rankExtractedItems(withDrank).catch(err => console.error('ranking failed', err));
  };

  // ── BOTTLE EXTRACTION (Step: label scan mode) ──────────────────────────
  // Single-bottle path. OCR text is the raw label content. AI returns one object,
  // not an array. No grouping pass, no ranking, no menu snapshot.
  const runBottleExtraction = async () => {
    setScanning(true);
    setScanMenuText(scanText);
    setBottleData(null);
    setExtractedItems([]);
    setCurrentScanId(null);

    // Light cleanup — drop very short/garbage lines but preserve label structure
    const cleanedLabel = scanText
      .split(/\r?\n/)
      .map(l => l.replace(/\s+/g, " ").trim())
      .filter(l => l.length >= 2)
      .join("\n");

    let aiRaw = "";
    let parseFailed = false;
    let parsed = null;
    if (cleanedLabel) {
      try {
        aiRaw = await callClaude(
          [{
            role: "user",
            content:
              `Extract structured fields from this wine/spirit bottle label OCR text.\n\n` +
              `Label text:\n${cleanedLabel}\n\n` +
              `Return a single JSON object with these fields:\n` +
              `- "name": appellation, cuvée, or main product name (e.g. "Sancerre", "La Mercy Dieu", "Old Forester")\n` +
              `- "producer": domaine / château / winery / distillery (e.g. "Domaine Bailly-Reverdy")\n` +
              `- "vintage": year as integer (e.g. 2021), or null if not visible\n` +
              `- "region": appellation or sub-region (e.g. "Loire Valley", "Napa Valley")\n` +
              `- "country": (e.g. "France", "USA")\n` +
              `- "varietal": grape variety if printed (e.g. "Pinot Noir"), or "" if not on label\n` +
              `- "abv": alcohol percentage as a number (e.g. 13.5), or null if not visible\n` +
              `- "category": "wine" | "spirit" — default to "wine" unless clearly otherwise\n` +
              `- "sub_category": for wine "red" | "white" | "rosé" | "sparkling" | "dessert" | ""; for spirit "whiskey" | "gin" | "vodka" | "rum" | "tequila" | "" ; otherwise ""\n\n` +
              `Rules:\n` +
              `- Output ONLY a JSON object. No prose. No markdown fences.\n` +
              `- If a field is not on the label, use null (numbers) or "" (strings). Never guess.\n` +
              `- Producer and name are different. "DOMAINE BAILLY-REVERDY SANCERRE" → producer "Domaine Bailly-Reverdy", name "Sancerre".`
          }],
          `You are a strict bottle label extraction service. You output a single JSON object. No prose. No markdown fences.`,
          800
        );
        const cleaned = aiRaw.trim()
          .replace(/^```(?:json)?\s*/i, "")
          .replace(/```\s*$/, "")
          .trim();
        const obj = JSON.parse(cleaned);
        if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
          parsed = {
            name:         typeof obj.name         === 'string' ? obj.name.trim()         : '',
            producer:     typeof obj.producer     === 'string' ? obj.producer.trim()     : '',
            vintage:      (typeof obj.vintage     === 'number' && isFinite(obj.vintage)) ? obj.vintage : null,
            region:       typeof obj.region       === 'string' ? obj.region.trim()       : '',
            country:      typeof obj.country      === 'string' ? obj.country.trim()      : '',
            varietal:     typeof obj.varietal     === 'string' ? obj.varietal.trim()     : '',
            abv:          (typeof obj.abv         === 'number' && isFinite(obj.abv)) ? obj.abv : null,
            category:     ['wine','spirit'].includes(obj.category) ? obj.category : 'wine',
            sub_category: typeof obj.sub_category === 'string' ? obj.sub_category : '',
          };
        } else {
          parseFailed = true;
        }
      } catch (e) {
        console.error('Bottle AI extraction failed:', e);
        parseFailed = true;
      }
    }

    // Fallback: blank object so the user can fill it in manually rather than hitting an error
    if (!parsed) {
      parsed = {
        name: '', producer: '', vintage: null, region: '', country: '',
        varietal: '', abv: null, category: 'wine', sub_category: '',
      };
    }

    // "I drank this" defaults ON for bottle scans per spec
    setBottleData({ ...parsed, drank: true, notes: '' });

    // Log the extraction for prompt-tuning analysis
    saveScan(
      scanText,
      JSON.stringify({ phase: 'extracted_bottle', ai_raw: aiRaw, parsed, parse_failed: parseFailed }),
      impVenue || ''
    ).catch(() => {});

    setScanning(false);
  };

  // Save the confirmed bottle to swishd_impressions
  const saveBottle = async () => {
    if (!bottleData) return;
    if (!bottleData.name.trim() && !bottleData.producer.trim()) {
      showToast('Add a name or producer before saving', C.coral);
      return;
    }
    if (!bottleData.drank) {
      showToast('Toggle "I drank this" to save', C.coral);
      return;
    }
    setScanSaving(true);
    const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const displayName = bottleData.producer && bottleData.producer.trim()
      ? `${bottleData.name.trim() || 'Wine'} — ${bottleData.producer.trim()}`
      : (bottleData.name.trim() || bottleData.producer.trim());

    // ── Persist bottle as a single-item menu_scan ───────────────────────
    // Bottle = a "menu" of one. Keeps everything in one canonical extraction store.
    let menuItemId = null;
    try {
      const scanId = await createMenuScan({
        source: 'bottle',
        rawText: scanText || '',
        venueName: impVenue || '',
      });
      if (scanId) {
        const itemForDb = {
          name: bottleData.name || (bottleData.producer || 'Bottle'),
          price: null,
          category: bottleData.category || 'wine',
          producer: bottleData.producer || null,
          vintage: bottleData.vintage,
          region: bottleData.region || null,
          country: bottleData.country || null,
          varietal: bottleData.varietal || null,
          abv: bottleData.abv,
          sub_category: bottleData.sub_category || null,
          normalized_name: normalizeDrinkKey(bottleData.name || bottleData.producer || 'bottle'),
          extraction_hash: await computeExtractionHash({
            name: bottleData.name || bottleData.producer || 'bottle',
            price: null,
            vintage: bottleData.vintage,
          }),
        };
        const rows = await insertMenuItems(scanId, [itemForDb]);
        if (rows.length > 0) menuItemId = rows[0].id;
      }
    } catch (e) {
      console.error('bottle scan persist failed:', e);
      // Continue with impression save anyway — link is best-effort
    }

    const entry = {
      id: `imp${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
      wine: displayName,
      venue: impVenue || '',
      date: today,
      with: impWith || '',
      notes: bottleData.notes || '',
      // Dedicated columns on swishd_impressions
      vintage:  bottleData.vintage,
      region:   bottleData.region,
      country:  bottleData.country,
      varietal: bottleData.varietal,
      abv:      bottleData.abv,
      details: '',
      rating: 0,
      menu_item_id: menuItemId,
    };

    const result = await dbSaveImpression(entry);
    if (result) {
      setImpressions(prev => [{ ...entry }, ...prev]);
      showToast('Saved to your story', C.aqua);
      setBottleData(null);
      setScanText('');
      setOcrPreviewURL('');
      setOcrError('');
      setOcrProgress(0);
    } else {
      showToast('Save failed — please try again', C.coral);
    }
    setScanSaving(false);
  };

  // Rank a set of extracted items against the user's palate vector.
  // Mutates extractedItems state with rank, match_score, and (for top match) reason.
  const rankExtractedItems = async (itemsToRank) => {
    if (!itemsToRank || itemsToRank.length === 0) return;
    setRanking(true);

    const palateVec = palateToVector(palate);

    // Step 1: Look up cached flavor vectors for all items at once
    const names = itemsToRank.map(it => it.name);
    let cache = {};
    try { cache = await loadDrinkFlavorsBatch(names); } catch (e) { /* network — proceed with empty cache */ }

    // Step 2: Identify cache misses; ask AI to generate vectors for them
    const misses = itemsToRank.filter(it => !cache[normalizeDrinkKey(it.name)]);
    let aiVectors = {};
    if (misses.length > 0) {
      aiVectors = await aiGenerateFlavorVectors(misses);
      // Persist new vectors back to cache (fire-and-forget)
      for (const it of misses) {
        const v = aiVectors[it.name];
        if (v && typeof v === 'object') {
          saveDrinkFlavor({ name: it.name, category: it.category || 'unknown', vector: v, source: 'ai' }).catch(() => {});
        }
      }
    }

    // Step 3: Score each item
    // Palate match (cosine similarity) and price affordability are blended 80/20.
    // Price affordability is min-max normalized across the items in this scan.
    // Items without price get a neutral 0.5 score (neither rewarded nor punished).
    const prices = itemsToRank.map(it => (typeof it.price === 'number' && isFinite(it.price)) ? it.price : null).filter(p => p !== null);
    const minPrice = prices.length ? Math.min(...prices) : 0;
    const maxPrice = prices.length ? Math.max(...prices) : 0;
    const priceRange = maxPrice - minPrice;
    const priceScore = (p) => {
      if (typeof p !== 'number' || !isFinite(p)) return 0.5; // unknown price → neutral
      if (priceRange === 0) return 0.5;                       // all same price → neutral
      return 1 - (p - minPrice) / priceRange;                 // cheapest = 1, priciest = 0
    };

    const scored = itemsToRank.map(it => {
      const cached = cache[normalizeDrinkKey(it.name)];
      const vector = cached?.vector || aiVectors[it.name] || null;
      const palateMatch = vector ? cosineSimilarity(palateVec, vector) : null;
      const ps = priceScore(it.price);
      // Final blended score — 80% palate, 20% price. Null palate = no overall score.
      const match_score = (palateMatch !== null) ? (0.8 * palateMatch + 0.2 * ps) : null;
      return { ...it, match_score, _palate_match: palateMatch, _price_score: ps, _flavor_vec: vector };
    });

    // Step 4: Sort by score descending. Items without a vector go to bottom (preserve original relative order).
    const withScores = scored.filter(x => typeof x.match_score === 'number').sort((a,b) => b.match_score - a.match_score);
    const withoutScores = scored.filter(x => typeof x.match_score !== 'number');
    const ranked = [...withScores, ...withoutScores].map((it, i) => ({ ...it, rank: i + 1 }));
    setExtractedItems(ranked);

    // Step 5: Generate a one-sentence reason for EVERY drink, in parallel.
    // Uses "your palate" framing (not "the palate") for direct, readable copy.
    const reasonResults = await Promise.allSettled(
        ranked.map(d =>
          callClaude(
            [{
              role: 'user',
              content:
                `Your palate vector: ${JSON.stringify(palateVec)}\n` +
                `Drink: ${d.name} (category: ${d.category})\n\n` +
                `Write ONE short sentence (max 14 words) explaining why this drink suits the reader's palate, addressed to "you" or "your palate".\n\n` +
                `TONE: Plain conversational English. Like a friend describing it, not a sommelier writing a tasting note.\n\n` +
                `PALATE GRAMMAR — critical:\n` +
                `- A palate has PREFERENCES, not flavor properties. The palate doesn't have tartness, acidity, sweetness, smokiness, etc. — those belong to drinks and foods.\n` +
                `- WRONG: "your palate's tartness", "your palate's acidity", "your palate's smokiness", "your palate's dryness".\n` +
                `- RIGHT: "your taste for tart drinks", "your preference for crisp, dry whites", "your liking for smoky, bold reds", "your dry-leaning palate", "matches what you go for".\n` +
                `- The palate is the SUBJECT that prefers things; flavor words modify the DRINK, not the palate.\n\n` +
                `BANNED VOCABULARY — never use these terms:\n` +
                `- umami / umami-rich / savory depth\n` +
                `- acidity / high acidity / acidic\n` +
                `- tannic / tannins / tannic structure\n` +
                `- minerality / mineral / minerally\n` +
                `- terroir / vintage character / appellation\n` +
                `- palate (except in "your palate")\n` +
                `- bright / vibrant (as flavor descriptors — too vague)\n` +
                `- complexity / structure / profile / mouthfeel\n` +
                `- bone-dry, lush, opulent, austere, expressive\n\n` +
                `INSTEAD, use everyday words:\n` +
                `- "tart" / "lemony" / "sour" instead of "acidity"\n` +
                `- "crisp" / "clean" instead of "dry"\n` +
                `- "grippy" / "drying" / "heavy" instead of "tannic"\n` +
                `- "stony" / "chalky" instead of "mineral"\n` +
                `- "smoky" / "earthy" / "spicy" / "fruity" all OK as-is\n\n` +
                `WHAT YOU CAN REFERENCE about the drink:\n` +
                `- General established knowledge of the wine type or cocktail style (e.g. "Sancerre is a crisp white from Loire"). It's OK to bring in commonly-known traits.\n` +
                `- The drink's actual ingredients if listed in the name.\n\n` +
                `DON'T invent specifics that aren't established knowledge: no "old-vine", "barrel-aged for 18 months", "limestone soils" — those are claims that need a source.\n\n` +
                `Examples of good output:\n` +
                `- "Sancerre's tart, lemony kick matches your taste for sour, dry whites."\n` +
                `- "A crisp, clean Chardonnay — easy drinking for your dry, fruit-forward palate."\n` +
                `- "Smoky and warming, plays to your taste for bold, drying reds."\n\n` +
                `FORBIDDEN: "the palate", "the palate's", "the user". Use "your palate" or "you".\n` +
                `No marketing language. No "you'll love". One sentence only. No quotes.`
            }],
            `You write one-sentence drink notes in plain conversational English. Never use sommelier jargon (umami, acidity, tannins, minerality, terroir, etc.). Describe drinks with everyday words a friend would use. General industry knowledge of the wine/cocktail style is OK; specific production claims are not. Always "your palate", never "the palate".`,
            100,
            'claude-sonnet-4-6'
          ).catch(() => null)
        )
      );
      const reasonsById = {};
      reasonResults.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) {
          const cleaned = String(r.value).trim().replace(/^["'`]|["'`]$/g, '');
          if (cleaned) reasonsById[ranked[i]._id] = cleaned;
        }
      });
      setExtractedItems(prev => prev.map(x => reasonsById[x._id] ? { ...x, reason: reasonsById[x._id] } : x));

    setRanking(false);

    // ── PERSIST RANKING (ephemeral but auditable) ──────────────────────
    // Saves a snapshot of this ranking call: scan_id, palate vector at this moment,
    // and a row per item with its score, position, and (eventual) reason.
    // Fires after sort so the user sees rankings immediately; persistence is best-effort.
    if (currentScanId) {
      const rankedRows = ranked
        .filter(it => it.menu_item_id && typeof it.match_score === 'number')
        .map(it => ({
          menu_item_id: it.menu_item_id,
          match_score: it.match_score,
          rank_position: it.rank,
          reason: null, // top-match reason is async; we'll patch later if we want
        }));
      saveRanking({
        scanId: currentScanId,
        foodContext: null,
        palateVector: palateVec,
        rankedItems: rankedRows,
      }).catch(err => console.error('saveRanking failed:', err));
    }
  };

  // Save confirmed items to journal (swishd_impressions, one row per "I drank this" item)
  // Pre-edit (extractedItems at extract time) vs post-edit (current extractedItems) is captured by saveScan above.
  // We log the final confirmed-set here too, for diff analysis.
  const saveScannedItems = async () => {
    const confirmed = extractedItems.filter(it => it.drank && it.name.trim());
    if (confirmed.length === 0) {
      showToast("Toggle 'I drank this' on items to save", C.coral);
      return;
    }
    setScanSaving(true);
    const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    let savedCount = 0;
    for (const it of confirmed) {
      const detailsBlob = JSON.stringify({
        source: "menu_scan",
        producer: it.producer || "",
        category: it.category,
        sub_category: it.sub_category || "",
        price: it.price,
        scanned_at: new Date().toISOString(),
      });
      // Display name: "Sancerre — Domaine Vacheron" when producer is present (wines only).
      const displayName = it.category === 'wine' && it.producer && it.producer.trim()
        ? `${it.name.trim()} — ${it.producer.trim()}`
        : it.name.trim();
      const entry = {
        id: `imp${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
        wine: displayName,
        venue: impVenue || "",
        date: today,
        with: impWith || '',
        mood: (impMood[it._id]||[]).join(", "),
        notes: '',
        details: detailsBlob,
        rating: 0,
        menu_item_id: it.menu_item_id || null,
      };
      const result = await dbSaveImpression(entry);
      if (result) savedCount++;
      // Also reflect in local impressions list
      setImpressions(prev => [{ ...entry }, ...prev]);
    }
    // Log the confirmed set separately so you have edited-vs-extracted diffs in swishd_scans
    saveScan(
      scanRawText || scanText,
      JSON.stringify({ phase: "confirmed", confirmed_items: confirmed }),
      impVenue || ""
    ).catch(() => {});
    setScanSaving(false);
    showToast(`Saved ${savedCount} drink${savedCount === 1 ? "" : "s"} to your story`, C.aqua);
    // Reset back to scan landing
    setExtractedItems([]);
    setScanText("");
    setScanRawText("");
    setOcrPreviewURL("");
    setOcrError("");
    setCurrentScanId(null);
  };


  // ── PER-CHECKBOX SAVE/DELETE (Increment 5: Select model) ─────────────────
  // Tick → save impression immediately, store impression_id on the item.
  // Untick → delete the impression by id, clear the id from the item.
  // First tick of a scan session triggers the food modal.
  // ── INLINE EDIT (My Story cards) ─────────────────────────────────
  // Start editing: prepare a working draft from the impression row.
  // For menu-scan rows, the JSON metadata in `details` is unpacked into
  // top-level draft fields (category, sub_category, price) so the user
  // edits structured fields, not raw JSON.
  const startInlineEdit = (imp) => {
    let draftCategory = '';
    let draftSubCategory = '';
    let draftPrice = '';
    let isMenuScanRow = false;
    if (typeof imp.details === 'string' && imp.details.trim().startsWith('{')) {
      try {
        const p = JSON.parse(imp.details);
        if (p && p.source === 'menu_scan') {
          isMenuScanRow = true;
          draftCategory = p.category || '';
          draftSubCategory = p.sub_category || '';
          if (typeof p.price === 'number') draftPrice = String(p.price);
        }
      } catch {}
    }
    setEditingImpId(imp.id);
    setEditingDraft({
      wine: imp.wine || '',
      venue: imp.venue || '',
      date: imp.date || '',
      with: imp.with || '',
      notes: imp.notes || '',
      rating: imp.rating || 0,
      // Bottle-scan fields
      vintage: imp.vintage == null ? '' : String(imp.vintage),
      region: imp.region || '',
      country: imp.country || '',
      varietal: imp.varietal || '',
      abv: imp.abv == null ? '' : String(imp.abv),
      // Menu-scan structured fields (live in details JSON)
      category: draftCategory,
      sub_category: draftSubCategory,
      price: draftPrice,
      _isMenuScanRow: isMenuScanRow,
      // Keep the original details string as a free-text fallback for non-menu-scan rows
      detailsRaw: isMenuScanRow ? '' : (imp.details || ''),
    });
  };

  const cancelInlineEdit = () => {
    setEditingImpId(null);
    setEditingDraft(null);
  };

  const saveInlineEdit = async () => {
    if (!editingImpId || !editingDraft) return;
    setEditingSaving(true);
    // Re-pack details if this was a menu_scan row
    let detailsToWrite = editingDraft.detailsRaw || '';
    if (editingDraft._isMenuScanRow) {
      const blob = {
        source: 'menu_scan',
        category: editingDraft.category || '',
        sub_category: editingDraft.sub_category || '',
        price: editingDraft.price === '' ? null : Number(editingDraft.price),
        scanned_at: new Date().toISOString(),
      };
      detailsToWrite = JSON.stringify(blob);
    }
    const patched = {
      wine: editingDraft.wine.trim(),
      venue: editingDraft.venue,
      date: editingDraft.date,
      with: editingDraft.with,
      notes: editingDraft.notes,
      rating: editingDraft.rating,
      details: detailsToWrite,
      vintage:  editingDraft.vintage === '' ? null : Number(editingDraft.vintage),
      region:   editingDraft.region   || null,
      country:  editingDraft.country  || null,
      varietal: editingDraft.varietal || null,
      abv:      editingDraft.abv === '' ? null : Number(editingDraft.abv),
    };
    const ok = await dbUpdateImpression(editingImpId, patched);
    if (ok) {
      setImpressions(prev => prev.map(p => p.id === editingImpId ? { ...p, ...patched } : p));
      setEditingImpId(null);
      setEditingDraft(null);
      showToast('Updated', C.aqua);
    } else {
      showToast('Update failed — please retry', C.coral);
    }
    setEditingSaving(false);
  };

  const handleSelectToggle = async (idx, nextChecked) => {
    const it = extractedItems[idx];
    if (!it) return;
    if (nextChecked && (!impMood[it._id] || impMood[it._id].length===0) && !it.impression_id) {
      setMoodModalIdx(it._id);
      return;
    }
    if (nextChecked) {
      // Re-tick of a drink that already has an impression row (e.g. user is post-rerank
      // and re-ticking the same drink they selected pre-rerank). Update the existing
      // entry's timestamp instead of duplicating.
      if (it.impression_id) {
        const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const detailsBlob = JSON.stringify({
          source: 'menu_scan',
          producer: it.producer || '',
          category: it.category,
          sub_category: it.sub_category || '',
          price: it.price,
          scanned_at: new Date().toISOString(),
        });
        // Producer concat only for wines.
        const displayName = it.category === 'wine' && it.producer && it.producer.trim()
          ? `${it.name.trim()} — ${it.producer.trim()}`
          : it.name.trim();
        const patched = {
          wine: displayName,
          venue: impVenue || '',
          date: today,
          with: impWith || '',
          notes: '',
          details: detailsBlob,
          rating: 0,
        };
        const ok = await dbUpdateImpression(it.impression_id, patched);
        if (ok) {
          setExtractedItems(prev => prev.map((x, i) => i === idx ? { ...x, drank: true } : x));
          setImpressions(prev => prev.map(p => p.id === it.impression_id ? { ...p, ...patched } : p));
        } else {
          showToast('Update failed — please retry', C.coral);
        }
        return;
      }

      // First-time tick: save new impression
      const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      // Producer makes sense for wines ("Sancerre — Domaine Vacheron") but not for cocktails or spirits.
      // For cocktails the "producer" field is often the first ingredient/spirit which shouldn't be in the name.
      const displayName = it.category === 'wine' && it.producer && it.producer.trim()
        ? `${it.name.trim()} — ${it.producer.trim()}`
        : it.name.trim();
      const detailsBlob = JSON.stringify({
        source: 'menu_scan',
        producer: it.producer || '',
        category: it.category,
        sub_category: it.sub_category || '',
        price: it.price,
        scanned_at: new Date().toISOString(),
      });
      const entry = {
        id: `imp${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
        wine: displayName,
        venue: impVenue || '',
        date: today,
        with: impWith || '',
        mood: (impMood[it._id]||[]).join(", "),
        notes: '',
        details: detailsBlob,
        rating: 0,
        menu_item_id: it.menu_item_id || null,
      };
      const result = await dbSaveImpression(entry);
      if (result) {
        const impressionId = result.id;
        setExtractedItems(prev => prev.map((x, i) => i === idx ? { ...x, drank: true, impression_id: impressionId } : x));
        setImpressions(prev => [{ ...entry, id: impressionId }, ...prev]);

        // Track this drink in the session's "ever-selected" set, then evaluate redirect.
        // Use the menu_item_id when available (canonical), fall back to local _id.
        const drinkKey = it.menu_item_id || it._id;
        const isFirstTimeForThisDrink = !sessionSelectedIds.has(drinkKey);
        if (isFirstTimeForThisDrink) {
          const newSet = new Set(sessionSelectedIds);
          newSet.add(drinkKey);
          setSessionSelectedIds(newSet);

          // First select of this scan session → transition to Pairing with Food frame
          if (!foodPromptShown) {
            setFoodPromptShown(true);
            setFirstSelectedImpressionId(impressionId);
            setScanPhase('pairing_prompt');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // Signal: pre-food intent. Captures the user's directional preference before food context.
            writeSelectionSignal({
              scanId: currentScanId,
              menuItemId: it.menu_item_id || null,
              signalType: 'pre_food_intent',
            }).catch(()=>{});
          } else if ((foodActive || newSet.size >= 2) && foodModalResolved) {
            // Transition to summary view. Two paths land here:
            //   - Post-rerank tick (foodActive is set): every tick after rerank goes to summary
            //   - No-food path with second-distinct tick: legacy path retained for safety
            // The summary always shows the LATEST tick. On 3+ ticks, summaryDrink updates each time.
            const summaryEntry = {
              impression_id: impressionId,
              wine: entry.wine,
              category: it.category,
              sub_category: it.sub_category || '',
              price: it.price,
              producer: it.producer || '',
              venue: entry.venue,
              date: entry.date,
            };
            setSummaryDrink(summaryEntry);
            setScanPhase('summary');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // Signal: post-food final selection. The authoritative learning signal.
            writeSelectionSignal({
              scanId: currentScanId,
              menuItemId: it.menu_item_id || null,
              foodContext: foodActive || null,
              signalType: 'post_food_final',
            }).then(ok => {
              console.log('[swishd] post_food_final signal write:', ok ? 'OK' : 'FAILED', {
                scanId: currentScanId, menuItemId: it.menu_item_id, foodActive,
              });
            }).catch(err => {
              console.error('[swishd] post_food_final signal threw:', err);
            });
          }
        }
      } else {
        showToast('Save failed — please retry', C.coral);
      }
    } else {
      // Untick → delete by impression_id
      if (it.impression_id) {
        const ok = await dbDeleteImpression(it.impression_id);
        if (ok) {
          setImpressions(prev => prev.filter(p => p.id !== it.impression_id));
        }
      }
      setExtractedItems(prev => prev.map((x, i) => i === idx ? { ...x, drank: false, impression_id: null } : x));
    }
  };

  // ── FOOD OCR (single-dish scan from food menu) ──────────────────────────
  // 1. Tesseract on the photographed menu line
  // 2. Tiny AI call to pull the dish name out of the OCR string
  // 3. Auto-fill foodInput; user reviews and submits
  // ── NO-FOOD PATH FINALIZE ──────────────────────────────────────────────
  // Per spec: if user taps No on the Pairing with Food frame, the first selection is the final.
  // Transition scan tab to summary view with that drink. My Story already has the row
  // (real-time save on tick), so no additional save is needed.
  const handleNoFoodFinalize = () => {
    // Defensive: if nothing's ticked (shouldn't happen in normal flow but guard anyway), warn.
    const ticked = extractedItems.find(x => x.drank && x.impression_id);
    if (!ticked) {
      showToast('Select a drink first', C.coral);
      return;
    }
    setFoodModalResolved(true);
    const summaryEntry = {
      impression_id: ticked.impression_id,
      wine: ticked.producer && ticked.producer.trim()
            ? `${ticked.name.trim()} — ${ticked.producer.trim()}`
            : ticked.name.trim(),
      category: ticked.category,
      sub_category: ticked.sub_category || '',
      price: ticked.price,
      producer: ticked.producer || '',
      venue: impVenue || '',
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    };
    setSummaryDrink(summaryEntry);
    setScanPhase('summary');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Signals: pairing decision + final selection (since first drink IS the final on no-food path)
    writeSessionEvent({
      scanId: currentScanId,
      eventType: 'food_pairing_decided',
      meta: { decision: 'no' },
    }).catch(()=>{});
    writeSelectionSignal({
      scanId: currentScanId,
      menuItemId: ticked.menu_item_id || null,
      foodContext: null,
      signalType: 'post_food_final',
    }).catch(()=>{});
  };

  const runFoodOcr = async (file) => {
    if (!file) return;
    setFoodOcrError("");
    setFoodOcrLoading(true);
    setFoodOcrProgress(0);
    if (!file.type || !file.type.startsWith('image/')) {
      setFoodOcrError("Image only — PDFs aren't supported.");
      setFoodOcrLoading(false);
      return;
    }
    try {
      const Tesseract = (await import("tesseract.js")).default;
      const { data } = await Tesseract.recognize(file, "eng", {
        logger: (m) => {
          if (m.status === "recognizing text" && typeof m.progress === "number") {
            setFoodOcrProgress(Math.round(m.progress * 100));
          }
        },
      });
      const raw = (data?.text || "").trim();
      if (!raw) {
        setFoodOcrError("Couldn't read any text. Try better lighting or a closer shot.");
        setFoodOcrLoading(false);
        return;
      }

      // Extract the dish name from the OCR string. AI call kept minimal.
      let dishName = raw.split(/\r?\n/)[0].trim(); // fallback: first line
      try {
          const aiRaw = await callClaude(
            [{
              role: "user",
              content:
                `Extract just the dish name from this OCR text from a food menu. ` +
                `Strip ingredient lists, prices, and descriptions.\n\n` +
                `OCR text: "${raw.replace(/"/g, "'")}"\n\n` +
                `Output ONLY the dish name as a plain string. No quotes, no JSON, no prose. ` +
                `Examples: "Ribeye Steak", "Tagliatelle Bolognese", "Tuna Tartare".`
            }],
            `You extract dish names from messy food menu OCR. Output a plain string. No prose. No quotes.`,
            60
          );
          const cleaned = (aiRaw || '').trim().replace(/^["'`]|["'`]$/g, '').split(/\r?\n/)[0].trim();
          if (cleaned) dishName = cleaned;
        } catch (e) {
          // Non-fatal — we still have the first-line fallback
          console.error('food OCR dish-name extraction failed', e);
        }
      setFoodInput(dishName);
    } catch (e) {
      console.error('food OCR failed', e);
      setFoodOcrError("Snap failed. You can type the dish instead.");
    } finally {
      setFoodOcrLoading(false);
    }
  };

  // ── FOOD RE-RANK (Increment 5) ──────────────────────────────────────────
  // 1. Load or AI-generate a flavor vector for the food (cached in swishd_food_flavors).
  // 2. Compute pairing fit between food vec and each item's drink vec.
  // 3. Blend match_score with pairing fit + a small term for original score.
  // 4. Re-sort items, persist a new menu_rankings row with food_context set.
  const runFoodRerank = async (foodName) => {
    if (!foodName.trim() || extractedItems.length === 0) return;
    setFoodReranking(true);
    setFoodActive(foodName.trim());
    try {
      // 1. Cache lookup
      let foodVec = NEUTRAL_FOOD_VECTOR;
      const cached = await loadFoodFlavor(foodName);
      if (cached && cached.vector) {
        foodVec = cached.vector;
      } else {
        // 2. AI-generate
          try {
            const aiRaw = await callClaude(
              [{
                role: 'user',
                content:
                  `Return a JSON object with these axes (each 0..1) describing the flavor profile of: "${foodName}".\n` +
                  `Axes: fatty, salty, spicy, umami, sweet, acidic, bitter, grilled.\n` +
                  `"grilled" measures the charred / fire-cooked / smoky-from-cooking quality of the dish.\n` +
                  `Output ONLY the JSON object. No prose, no markdown fences.`,
              }],
              `You are a flavor profiling service. Output JSON only.`,
              200
            );
            const cleaned = aiRaw.trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '').trim();
            const parsed = JSON.parse(cleaned);
            if (parsed && typeof parsed === 'object') {
              foodVec = {
                fatty:   Number(parsed.fatty)   || 0,
                salty:   Number(parsed.salty)   || 0,
                spicy:   Number(parsed.spicy)   || 0,
                umami:   Number(parsed.umami)   || 0,
                sweet:   Number(parsed.sweet)   || 0,
                acidic:  Number(parsed.acidic)  || 0,
                bitter:  Number(parsed.bitter)  || 0,
                grilled: Number(parsed.grilled) || 0,
              };
              saveFoodFlavor({ name: foodName, vector: foodVec, source: 'ai' }).catch(() => {});
            }
          } catch (e) {
            console.error('food vector AI failed:', e);
          }
      }

      // 3. Re-rank — palate-first, food modifies, price tiebreaks.
      // Default blend: 70% palate + 20% pairing + 10% price.
      // Fallback (limited pairing match): drop pairing weight to 0.05, push the remainder into palate.
      // Detection: triggered when max pairing fit < 0.45 OR variance across items < 0.005.
      //
      // First pass: compute pairing fits for all items, decide if fallback applies.
      const pairs = extractedItems.map(it => {
        const drinkVec = it._flavor_vec || null;
        const fit = drinkVec ? pairingFit(foodVec, drinkVec) : 0.5;
        return fit;
      });
      const maxFit = pairs.length ? Math.max(...pairs) : 0;
      const meanFit = pairs.length ? pairs.reduce((a,b)=>a+b,0) / pairs.length : 0;
      const variance = pairs.length
        ? pairs.reduce((acc,p)=>acc + (p-meanFit)*(p-meanFit), 0) / pairs.length
        : 0;
      const isLimited = (maxFit < 0.45) || (variance < 0.005);
      setLimitedPairing(isLimited);

      // Weights — strict palate-first when limited, default otherwise.
      const wPalate = isLimited ? 0.85 : 0.7;
      const wFit    = isLimited ? 0.05 : 0.2;
      const wPrice  = 0.1;

      const reranked = extractedItems.map((it, i) => {
        const palateMatch = typeof it._palate_match === 'number' ? it._palate_match
                          : typeof it.match_score   === 'number' ? it.match_score
                          : 0.5;
        const fit = pairs[i];
        const ps = typeof it._price_score === 'number' ? it._price_score : 0.5;
        const blended = wPalate * palateMatch + wFit * fit + wPrice * ps;
        return { ...it, _pairing_fit: fit, _food_blend: blended };
      });
      reranked.sort((a, b) => (b._food_blend || 0) - (a._food_blend || 0));
      // On food rerank: also clear the first-selected impression from My Story
      // Per Swish'd flow: pairing food = "I'm reconsidering" → first drink is no longer
      // the user's choice. Only the post-rerank selection should land in the journal.
      // Note: the impression_id is wiped from extractedItems too (set to null) so re-ticking
      // the same drink creates a fresh entry, not an update to a now-deleted row.
      const repositioned = reranked.map((it, i) => ({
        ...it,
        rank: i + 1,
        match_score: it._food_blend, // overwrite displayed score with the blended one
        drank: false,
        impression_id: null,
      }));
      setExtractedItems(repositioned);

      // Generate fresh food-aware one-sentence reasons for every drink, in parallel.
      const palateVec = palateToVector(palate);
        const dish = foodName.trim();
        const reasonResults = await Promise.allSettled(
          repositioned.map(d =>
            callClaude(
              [{
                role: 'user',
                content:
                  `Your palate vector: ${JSON.stringify(palateVec)}\n` +
                  `Food: ${dish}\n` +
                  `Food vector (for your reference, do not name these axes): ${JSON.stringify(foodVec)}\n` +
                  `Drink: ${d.name} (category: ${d.category})\n\n` +
                  `Write ONE short sentence (max 16 words) explaining how this drink works with this food, addressed to the reader as "you" or "your palate".\n\n` +
                  `TONE: Plain conversational English. Like a friend explaining at the bar, not a sommelier writing a tasting note.\n\n` +
                  `PALATE GRAMMAR — critical:\n` +
                  `- A palate has PREFERENCES, not flavor properties. The palate doesn't have tartness, acidity, sweetness, smokiness, etc. — those belong to drinks and foods.\n` +
                  `- WRONG: "your palate's tartness", "your palate's acidity", "your palate's smokiness".\n` +
                  `- RIGHT: "your taste for tart drinks", "your preference for crisp whites", "what your palate goes for".\n` +
                  `- A drink CAN cut through richness using ITS OWN tartness/acidity. The palate doesn't do the cutting.\n` +
                  `- WRONG: "your palate's tartness cuts through the richness".\n` +
                  `- RIGHT: "the drink's tartness cuts through the richness — and that's what you go for".\n\n` +
                  `BANNED VOCABULARY — never use these terms, even if accurate:\n` +
                  `- umami / umami-rich / savory depth\n` +
                  `- acidity / high acidity / acidic\n` +
                  `- tannic / tannins / tannic structure\n` +
                  `- minerality / mineral / minerally\n` +
                  `- terroir / vintage / appellation\n` +
                  `- palate (except in "your palate")\n` +
                  `- bright / vibrant (as flavor descriptors — too vague)\n` +
                  `- complexity / structure / profile\n` +
                  `- bone-dry, lush, opulent, austere\n\n` +
                  `INSTEAD, use everyday words people use about food and drink:\n` +
                  `- "tart" / "lemony" instead of "high acidity"\n` +
                  `- "crisp" / "clean" instead of "dry"\n` +
                  `- "salty depth" or "rich savory taste" instead of "umami"\n` +
                  `- "grippy" / "drying" / "heavy" instead of "tannic"\n` +
                  `- "stony" / "chalky" instead of "mineral"\n` +
                  `- "cuts through the richness" instead of "balances fat"\n` +
                  `- "calms the heat" instead of "tames spice"\n\n` +
                  `FOOD CONTENT: You CAN name actual ingredients of the food if obvious from the dish name (e.g. "clams" in Spaghetti Vongole, "egg" in Carbonara, "tomato" in Bolognese). Do NOT invent ingredients or preparations that aren't stated or universally implied by the dish name.\n\n` +
                  `Examples of good output:\n` +
                  `- "The tart citrus cuts through the buttery clams cleanly."\n` +
                  `- "Crisp and clean — won't fight the lemony brightness."\n` +
                  `- "Sweet enough to calm the chili heat."\n` +
                  `- "The grippy red stands up to the rich meat sauce."\n\n` +
                  `FORBIDDEN: "the palate", "the palate's", "the user". Use "your palate" or "you".\n` +
                  `No marketing language. No "you'll love" framing. One sentence only. No quotes.`
              }],
              `You write one-sentence food-pairing notes in plain conversational English. Never use sommelier jargon (umami, acidity, tannins, minerality, terroir, etc.). Describe food and drink with everyday words a friend would use. You may name obvious ingredients of the dish; never invent ones. Always "your palate", never "the palate".`,
              120,
              'claude-sonnet-4-6'
            ).catch(() => null)
          )
        );
        const reasonsById = {};
        reasonResults.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value) {
            const cleaned = String(r.value).trim().replace(/^["'`]|["'`]$/g, '');
            if (cleaned) reasonsById[repositioned[i]._id] = cleaned;
          }
        });
        setExtractedItems(prev => prev.map(x => reasonsById[x._id] ? { ...x, reason: reasonsById[x._id] } : x));

      // Delete the first-selected impression from My Story
      if (firstSelectedImpressionId) {
        dbDeleteImpression(firstSelectedImpressionId).catch(err => console.error('food-rerank delete failed', err));
        setImpressions(prev => prev.filter(p => p.id !== firstSelectedImpressionId));
        setFirstSelectedImpressionId(null);
      }

      // Reset redirect-tracking so the post-rerank "second distinct drink" can transition to summary.
      // foodModalResolved stays true — user already answered, don't re-prompt.
      setRedirectFired(false);
      setSessionSelectedIds(new Set());
      setExpandedReasonIds(new Set());

      // 4. Persist a new ranking snapshot
      if (currentScanId) {
        const palateVec = palateToVector(palate);
        const rankedRows = repositioned
          .filter(it => it.menu_item_id && typeof it.match_score === 'number')
          .map(it => ({
            menu_item_id: it.menu_item_id,
            match_score: it.match_score,
            rank_position: it.rank,
            reason: null,
          }));
        saveRanking({
          scanId: currentScanId,
          foodContext: foodName.trim(),
          palateVector: { palate: palateVec, food: foodVec },
          rankedItems: rankedRows,
        }).catch(err => console.error('saveRanking (food) failed:', err));
      }
    } finally {
      setFoodReranking(false);
    }
  };

  const sendAgentMsg=async(userMsg)=>{
    if(!userMsg.trim())return;
    const newMsgs=[...agentMsgs,{from:"user",text:userMsg}];
    setAgentMsgs(newMsgs);
    setAgentInput("");
    setAgentLoading(true);

    const menu=scanMenuText||scanText||"";
    const msg=userMsg.toLowerCase();
    const dish=food||"";

    // Always try Anthropic API first
    try{
        const profileContext=`User palate: prefers ${palate?.preferredStyle||"medium"} wines, top aromas: ${palate?.topAromas?.join(", ")||"citrus, mineral"}.`;
        const history=newMsgs.map(m=>({role:m.from==="user"?"user":"assistant",content:m.text}));
        const reply=await callClaude(
          history,
          `You are swish'd AI — a silent palate intelligence engine. ${profileContext}\n\nThe snapped menu is:\n${menu}\n\nRules:\n- Rank all drinks on the menu including wines, cocktails, spirits, and beers by match to the user's palate\n- Do not ask the user any questions\n- Do not suggest saving, deciding, or any action — the UI handles all CTAs\n- Output ranked drink list only with a one-sentence palate match reason per drink\n- Include prices from the menu in your responses\n- Never use first person or address the user directly`,
          400
        );
        // Check if user is saving
        const addStory=/(add to|save to|save it|my story|impression|^yes$|^sure$|^ok$|^save$)/i.test(msg);
        if(addStory){
          const lastAi=agentMsgs.filter(m=>m.from==="ai").slice(-1)[0]?.text||"";
          const lines=menu.split("\n").filter(l=>l.trim().length>3);
          const wm=lines.find(l=>lastAi.toLowerCase().includes(l.split(/[—–\-$]/)[0].trim().toLowerCase().substring(0,5)));
          const wn=wm?wm.split(/[—–\-$]/)[0].trim():"";
          if(wn){setImpWine(wn);setImpDate(new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}));if(dish)setImpNotes("Paired with "+dish+".");}
        }
        setAgentMsgs([...newMsgs,{from:"ai",text:reply}]);
        setAgentLoading(false);
        return;
      }catch(e){console.error(e);}

    // Parse multi-line menu — combine wine name + region/vintage + price into single lines
    const rawLines=menu.split("\n").map(l=>l.trim()).filter(l=>l.length>0);
    const parsedLines=[];
    let i=0;
    while(i<rawLines.length){
      const line=rawLines[i];
      const isPrice=/^\$?[\d]+$/.test(line);
      const isPriceRange=/^\$?[\d]+-[\d]+$/.test(line);
      if(isPrice||isPriceRange){
        // Price line — append to previous entry
        if(parsedLines.length>0){
          parsedLines[parsedLines.length-1]+=" — $"+line.replace("$","");
        }
        i++;
      } else if(i+1<rawLines.length&&(/^\$?[\d]+$/.test(rawLines[i+1])||/^\$?[\d]+-[\d]+$/.test(rawLines[i+1]))){
        // Next line is a price — combine
        const nextLine=rawLines[i+1];
        parsedLines.push(line+" — $"+nextLine.replace("$",""));
        i+=2;
      } else if(i+2<rawLines.length&&/^\$?[\d]+$/.test(rawLines[i+2])){
        // Price is two lines down (wine, region/vintage, price)
        parsedLines.push(line+", "+rawLines[i+1]+" — $"+rawLines[i+2].replace("$",""));
        i+=3;
      } else {
        parsedLines.push(line);
        i++;
      }
    }
    const lines=parsedLines.filter(l=>l.length>3);
    const isSp=t=>/(champagne|prosecco|sparkling|brut|veuve|cremant|cava)/i.test(t);
    const isW=t=>/(chardonnay|sancerre|chablis|riesling|sauvignon|blanc|pinot gris|albari|vermentino|viognier|gruner|chenin)/i.test(t);
    const isR=t=>/(cabernet|barolo|malbec|shiraz|pinot noir|merlot|zinfandel|rioja|syrah|sangiovese|chianti|brunello|tempranillo)/i.test(t);
    const isRo=t=>/(ros[eé]|miraval|whispering)/i.test(t);

    const wineMatch=lines.find(l=>msg.split(/\s+/).some(w=>w.length>3&&l.toLowerCase().includes(w)));
    const lastAiText=agentMsgs.filter(m=>m.from==="ai").slice(-1)[0]?.text||"";
    const lastAiLines=lastAiText.split("\n").filter(l=>/^\d\./.test(l.trim()));
    const refersToFirst=/(^first$|^1$|the first|number one)/i.test(msg);
    const refersToSecond=/(^second$|^2$|the second|number two)/i.test(msg);
    const refersToThird=/(^third$|^3$|the third|number three)/i.test(msg);
    const contextWine=refersToFirst?lastAiLines[0]:refersToSecond?lastAiLines[1]:refersToThird?lastAiLines[2]:null;
    const cleanContextWine=contextWine?contextWine.replace(/^\d+\.\s*/,"").split(/[—–\-$]/)[0].trim():null;

    const seafood=/(salmon|tuna|fish|oyster|scallop|shrimp|lobster|crab|sushi)/i.test(msg+dish);
    const meat=/(steak|beef|lamb|duck|burger|ribs|pork|venison)/i.test(msg+dish);
    const pasta=/(pasta|risotto|pizza|truffle|mushroom)/i.test(msg+dish);
    const lastAiAskedMeal=lastAiText.toLowerCase().includes("pairing this with a meal") || lastAiText.toLowerCase().includes("pairing with a meal");
    const userSaidYes=/^(yes|yeah|yep|sure|absolutely|of course|yup|si|oui)$/i.test(msg.trim());
    const userSaidNo=/^(no|nope|nah|not really|no thanks|no thank you)$/i.test(msg.trim());
    const addFood=/(food|pairing|eating|having|dish|meal|change.*mind|kabob|kebab|tonight.*hav|hav.*tonight)/i.test(msg)||(lastAiAskedMeal&&userSaidYes);
    const addStory=/(add to|save to|save it|my story|impression|^yes$|^sure$|^ok$|^save$)/i.test(msg);
    const wantsCrisp=/(crisp|light|refreshing|fresh|mineral|dry|clean|^white$)/i.test(msg);
    const wantsBold=/(bold|full.?bod|rich|heavy|tannic|robust|powerful|^red$)/i.test(msg);
    const wantsSp=/(sparkling|bubbles|bubbly|champagne|prosecco|fizzy)/i.test(msg);
    const wantsRose=/(ros[eé]|rose|pink)/i.test(msg);
    const wantsSpirits=/(whisky|whiskey|bourbon|gin|vodka|rum|tequila|cognac|sake|port|sherry)/i.test(msg);
    const thanking=/(thank|perfect|great|love it|amazing|wonderful|cheers)/i.test(msg);

    // Simple selection — short reply, not a sentence, after "Have you decided?"
    const lastAiAskedDecided=lastAiText.includes("Have you decided?");
    const isSimpleSelection=lastAiAskedDecided&&!addFood&&!addStory&&userMsg.trim().split(/\s+/).length<=5&&!/[.!?]/.test(userMsg)&&msg.length<40;

    let reply="";

    if(isSimpleSelection){
      const matchedLine=lines.find(l=>l.toLowerCase().includes(msg.substring(0,5)));
      const wineName=matchedLine?matchedLine.split(/[—–\-$]/)[0].trim():userMsg.trim();
      const price=matchedLine?matchedLine.match(/\$[\d]+/)?.[0]:"";
      setImpWine(wineName);
      setImpDate(new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}));
      if(dish) setImpNotes("Paired with "+dish+".");
      reply="Great choice — "+wineName+(price?" ("+price+")":"")+". Shall I save it to My Story?";

    } else if(addFood||/(kabob|kebab|chicken|salad|vegetable|cheese|charcuterie)/i.test(msg)){
      if(lastAiAskedMeal&&userSaidYes){
        // User said yes to "Are you pairing with a meal?" — ask what dish
        reply="What are you having? Just tell me the dish and I'll re-rank the menu for you.";
      } else if(lastAiAskedMeal&&userSaidNo){
        reply="No problem — enjoy your drinks! Have you decided on your selection?";
      } else {
        const cleaned=userMsg.replace(/(i changed my mind|i would like to|would like to|add food|i'm having|i am having|having|eating|pairing with|pairing|add)/gi,"").trim();
        const dishText=cleaned.length>1?cleaned:userMsg.trim();
        if(dishText.length>1) setFood(dishText);
        const isMeatDish=/(steak|beef|lamb|duck|burger|ribs|pork|venison|kabob|kebab|chicken|meat)/i.test(dishText+msg);
        const isSeafoodDish=/(salmon|tuna|fish|oyster|scallop|shrimp|lobster|crab|sushi)/i.test(dishText+msg);
        const isPastaDish=/(pasta|risotto|pizza|truffle|mushroom)/i.test(dishText+msg);
        let ranked=lines;
        if(isMeatDish) ranked=[...lines.filter(l=>isR(l)),...lines.filter(l=>!isR(l))];
        else if(isSeafoodDish) ranked=[...lines.filter(l=>isW(l)||isSp(l)),...lines.filter(l=>!isW(l)&&!isSp(l))];
        else if(isPastaDish) ranked=[...lines.filter(l=>/(chianti|sangiovese|pinot noir|barbera|chardonnay)/i.test(l)),...lines.filter(l=>!/(chianti|sangiovese|pinot noir|barbera|chardonnay)/i.test(l))];
        reply="For "+dishText+", here's my re-ranked list:\n\n"+ranked.slice(0,4).map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\n\nHave you decided?";
      }

    } else if(addStory){
      const wm=lines.find(l=>lastAiText.toLowerCase().includes(l.split(/[—–\-$]/)[0].trim().toLowerCase().substring(0,5)));
      const wn=wm?wm.split(/[—–\-$]/)[0].trim():"";
      if(wn){setImpWine(wn);setImpDate(new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}));if(dish)setImpNotes("Paired with "+dish+".");}
      reply="Done! "+(wn?wn+" has been":"Your selection has been")+" added. Fill in any extra details and tap Save to Impressions →";

    } else if(cleanContextWine&&(refersToFirst||refersToSecond||refersToThird)){
      const matchedLine=lines.find(l=>l.toLowerCase().includes(cleanContextWine.toLowerCase().substring(0,5)))||cleanContextWine;
      const price=matchedLine?matchedLine.match(/\$[\d]+/)?.[0]:"";
      const desc=isSp(matchedLine)?"Fine bubbles, crisp acidity, brioche and citrus. Perfect as an aperitif or with oysters.":isW(matchedLine)?"Bright and mineral with lively acidity, citrus, and white flowers. Excellent with seafood and light dishes.":isR(matchedLine)?"Full-bodied with dark fruit, structure, and a long finish. Best with red meat and aged cheese.":isRo(matchedLine)?"Pale, dry, and elegant — strawberry, peach, and dried herbs. Perfect for warm evenings and seafood.":"A well-balanced and expressive wine.";
      reply=cleanContextWine+(price?" — "+price:"")+"\n\n"+desc+"\n\nHave you decided?";

    } else if(wineMatch){
      const name=wineMatch.split(/[—–\-$]/)[0].trim();
      const price=wineMatch.match(/\$[\d]+/)?.[0]||"";
      const desc=isSp(wineMatch)?"Fine bubbles, crisp acidity, brioche and citrus. Perfect as an aperitif or with oysters.":isW(wineMatch)?"Bright and mineral with lively acidity, citrus, and white flowers. Excellent with seafood and light dishes.":isR(wineMatch)?"Full-bodied with dark fruit, structure, and a long finish. Best with red meat and aged cheese.":isRo(wineMatch)?"Pale, dry, and elegant — strawberry, peach, and dried herbs. Perfect for warm evenings and seafood.":"A well-balanced and expressive wine.";
      reply=name+(price?" ("+price+")":"")+"\n\n"+desc+"\n\nHave you decided?";

    } else if(seafood||meat||pasta){
      let ranked=lines;
      if(seafood) ranked=[...lines.filter(l=>isW(l)||isSp(l)),...lines.filter(l=>!isW(l)&&!isSp(l))];
      else if(meat) ranked=[...lines.filter(l=>isR(l)),...lines.filter(l=>!isR(l))];
      else if(pasta) ranked=[...lines.filter(l=>/(chianti|sangiovese|pinot noir|barbera|chardonnay)/i.test(l)),...lines.filter(l=>!/(chianti|sangiovese|pinot noir|barbera|chardonnay)/i.test(l))];
      const label=seafood?"seafood":meat?"red meat":"pasta";
      reply="For "+label+":\n\n"+ranked.slice(0,3).map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\n\nHave you decided?";

    } else if(wantsSp){
      const picks=lines.filter(l=>isSp(l));
      reply=picks.length>0?"Sparkling options:\n\n"+picks.map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\\n\nHave you decided?":"No sparkling on this menu — shall I suggest something crisp and dry instead?";

    } else if(wantsCrisp){
      const picks=lines.filter(l=>isW(l));
      reply=picks.length>0?"Crisp whites:\n\n"+picks.map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\n\nHave you decided?":"For crisp and light, look for Sauvignon Blanc, Sancerre, or Chablis.";

    } else if(wantsBold){
      const picks=lines.filter(l=>isR(l));
      reply=picks.length>0?"Bold reds:\n\n"+picks.map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\n\nHave you decided?":"For bold and full-bodied, look for Cabernet, Barolo, or Malbec.";

    } else if(wantsRose){
      const picks=lines.filter(l=>isRo(l));
      reply=picks.length>0?"Rosé options:\n\n"+picks.map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\n\nHave you decided?":"No rosé on this menu — Provence styles like Miraval are worth asking for.";

    } else if(wantsSpirits){
      reply="For spirits, ask the bartender what they have available. Shall I help you with the wine list instead?";

    } else if(thanking){
      reply="Wonderful! Have you decided? 🍷";

    } else if(seafood){
      const picks=[...lines.filter(l=>isW(l)||isSp(l)),...lines.filter(l=>!isW(l)&&!isSp(l))];
      reply="For seafood:\n\n"+picks.slice(0,3).map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\n\nHave you decided?";

    } else if(meat){
      const picks=[...lines.filter(l=>isR(l)),...lines.filter(l=>!isR(l))];
      reply="For red meat:\n\n"+picks.slice(0,3).map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\n\nHave you decided?";

    } else if(pasta){
      const picks=[...lines.filter(l=>/(chianti|sangiovese|pinot noir|barbera|chardonnay)/i.test(l)),...lines.filter(l=>!/(chianti|sangiovese|pinot noir|barbera|chardonnay)/i.test(l))];
      reply="For pasta:\n\n"+picks.slice(0,3).map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\n\nHave you decided?";

    } else {
      reply="From this menu my top picks:\n\n"+lines.slice(0,3).map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\n\nHave you decided?";
    }

    // Safety net — always respond
    if(!reply) reply="From this menu:\n\n"+lines.slice(0,3).map((l,i)=>(i+1)+". "+l.trim()).join("\n")+"\n\nHave you decided?";

    setAgentMsgs([...newMsgs,{from:"ai",text:reply}]);
    setAgentLoading(false);
  };


  const getAI=async(wine,foodStr)=>{
    setLoadingAI(true);setAiInsight("");
    try {
      const txt=await callClaude(
        [{role:"user",content:`Wine: ${wine.name} (${wine.grape}, ${wine.region}). Food: ${foodStr||"not specified"}. Aromas: ${wine.aromas.join(", ")}.`}],
        "You are a warm, precise sommelier. Write exactly 2 sentences: one about the wine's character, one about why it works with this food. Be specific and sensory.",120);
      setAiInsight(txt);
    }catch{}
    setLoadingAI(false);
  };

  const recommend=()=>{
    if(!palate)return;
    const scored=WINE_DB.map(w=>({...w,score:scoreWine(w,palate,food,wineStyle)})).sort((a,b)=>b.score-a.score);
    setPicks(scored);setScreen("results");
  };

  const saveDiary=()=>{
    if(!addWine)return;
    setHistory(prev=>[{
      id:`h${Date.now()}`,wine:addWine.name,wineId:addWine.id,
      venue:"Tonight's dinner",city:"New York",
      date:new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}),
      with:diaryWith||"Solo",rating:diaryRating,food:food||"",notes:diaryNote
    },...prev]);
    setAddWine(null);setDiaryNote("");setDiaryWith("");setDiaryRating(5);
    showToast("Added to your diary ✓",C.aqua);setScreen("main");
  };

  // GATE_AUDIT_V1_2026_06_01 — discover removed; 4-tab nav: snap, match, diary, profile
  const navItems=[
    {id:"scan",icon:"lens"},
    {id:"match",icon:"glyphs"},
    {id:"diary",icon:"book"},
    {id:"profile",icon:"person"},
  ];

  // BOTTOM NAV — single source of truth so every screen (main + palate results)
  // renders an identical bar. Inactive icon color is C.ink (darkened) per design.
  const renderBottomNav = () => (
    <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:430,background:"rgba(250,247,240,0.97)",backdropFilter:"blur(14px)",borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"space-around",padding:"12px 0 22px",zIndex:50}}>
      {navItems.map(n=>{
        const a=tab===n.id;
        const col=a?C.coral:C.ink;
        const renderIcon=()=>{
          if(n.icon==="glyphs") return (
            <div style={{display:"flex",gap:2,justifyContent:"center",alignItems:"flex-end",height:22}}>
              <WineGlassGlyph capH={15} W={10} sw={1.2} color={col}/>
              <WineGlassGlyph capH={15} W={10} sw={1.2} color={col}/>
            </div>
          );
          if(n.icon==="compass") return (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill={a?C.coral:"none"} stroke={col} strokeWidth="1.3"/>
            </svg>
          );
          if(n.icon==="lens") return (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7h3l2-2h8l2 2h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z"/>
              <circle cx="12" cy="13" r="3.5" fill={col}/>
            </svg>
          );
          if(n.icon==="book") return (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              <line x1="8" y1="7" x2="16" y2="7"/>
              <line x1="8" y1="11" x2="13" y2="11"/>
            </svg>
          );
          if(n.icon==="person") return (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          );
        };
        return (
          // CTA_STD_V1_2026_06_10
          <IconBtn key={n.id} size={48} style={{padding:"4px 8px"}} onClick={()=>{
            // camera tab always routes through handleCameraNav
            if(n.id==="scan"){ setTab('scan');setScreen('main'); } else { setTab(n.id);setScreen("main"); }
          }}>
            {renderIcon()}
          </IconBtn>
        );
      })}
    </div>
  );

  // CTA_FINAL_V1_2026_06_10 — camera nav: always lands on a fresh Snap entry,
  // prompting to discard an in-progress ranking first
const requirePalateQuiz = async () => {
    try {
      const { data:{ session } } = await supabase.auth.getSession();
      if (!session?.user) return false;
      const { data } = await supabase
        .from('swishd_profiles')
        .select('palate_fingerprint')
        .eq('user_id', session.user.id)
        .single();
      if (!data?.palate_fingerprint) {
        setPalateIntroCancelled(false);
        setScreen('palate-intro');
        return false;
      }
      return true;
    } catch(e) {
      setPalateIntroCancelled(false);
      setScreen('palate-intro');
      return false;
    }
  };

const handleCameraNav = async () => {
    if (!cameraPermission) { setShowCameraPrompt(true); return; }
    const hasQuiz = await requirePalateQuiz();
    if (!hasQuiz) return;
    if (extractedItems.length > 0) {
      if (!window.confirm('Discard current ranking and snap a new menu?')) return;
      setExtractedItems([]);setScanText("");setScanResult(null);setOcrPreviewURL("");setOcrError("");setOcrProgress(0);setScanRawText("");setCurrentScanId(null);
      setFoodModalOpen(false);setFoodPromptShown(false);setFoodInput("");setFoodActive("");setFoodReranking(false);setFoodModalStage('ask');
      setSessionSelectedIds(new Set());setFoodModalResolved(false);setRedirectFired(false);
      setScanPhase('ranking');setSummaryDrink(null);setFirstSelectedImpressionId(null);setLimitedPairing(false);
      setBottleData(null);
    }
    setTab('scan');setScreen('main');
  };

  // ── PALATE QUIZ option handling (multi-select aware) ──────────
  const finalizeQuiz = (scoresObj, selectionsObj) => {
    const profileKey = quizDetermineProfile(selectionsObj);
    const subtype = quizDetermineSubtype(profileKey, scoresObj);
    const profile = QUIZ_PROFILES[profileKey];
    const fingerprint = quizToFingerprint(scoresObj, profileKey);
    clearQuizProgress();
    setQuizResult({ profileKey, profile, subtype, fingerprint, scores: scoresObj, selections: selectionsObj });
  };

  const handleQuizOption = (i) => {
    const q = QUIZ_QUESTIONS[quizStep];
    const qId = q.id;
    const prev = quizSelections[qId] || [];
    let nextSel;
    if (q.multi) {
      const opt = q.options[i];
      if (opt.exclusive) {
        // Selecting the exclusive option clears all others (toggle off if it's the only one).
        nextSel = (prev.length === 1 && prev[0] === i) ? [] : [i];
      } else {
        // Toggle this option; selecting any non-exclusive option clears the exclusive one.
        const base = prev.filter(idx => !q.options[idx]?.exclusive);
        nextSel = base.includes(i) ? base.filter(idx => idx !== i) : [...base, i];
      }
    } else {
      // Single-select: tapping replaces the prior selection.
      nextSel = [i];
    }
    const newSelections = { ...quizSelections, [qId]: nextSel };
    setQuizResumeOffer(null); // answering Q1 instead of choosing implies starting over
    setQuizSelections(newSelections);
    const updated = computeQuizScores(newSelections);
    setQuizScores(updated);
    if (!q.multi) {
      if (quizStep < QUIZ_QUESTIONS.length - 1) setQuizStep(s => s + 1);
      else finalizeQuiz(updated, newSelections);
    }
    // Multi-select questions do not auto-advance — the Next button handles it.
  };

  const handleQuizNext = () => {
    const updated = computeQuizScores(quizSelections);
    if (quizStep < QUIZ_QUESTIONS.length - 1) setQuizStep(s => s + 1);
    else finalizeQuiz(updated, quizSelections);
  };

  // ── SITE GATE (SITE_GATE_V1_2026_09_24) ──────────────────────
  // Renders alone, ahead of every other screen — nothing below this point mounts until unlocked.
  if(!siteUnlocked) return (
    <div style={{minHeight:"100vh",background:"#EFEDE3",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",paddingTop:0,paddingLeft:24,paddingRight:24,paddingBottom:18,maxWidth:393,margin:"0 auto",boxSizing:"border-box"}}>
      <style>{FONTS+RESET}</style>
      <img
        src={logoImage}
        alt="Swish'd"
        style={{width:"100%",maxWidth:320,height:"auto",display:"block"}}
      />
      <div style={{maxWidth:340,marginTop:24,textAlign:"center",fontSize:17,lineHeight:1.5,color:"#1f2226"}}>
        <span style={{fontFamily:"'Jost',sans-serif",fontWeight:500,letterSpacing:2,color:"#c23b0e"}}>DEF'</span>
        {" "}
        <span style={{fontFamily:"'Jost',sans-serif",fontWeight:400,color:"#9aa0a6"}}>(past tense)</span>
        {" "}
        <span style={{fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic",fontWeight:500}}>To have been moved by something — a first pour that made you stay a little longer.</span>
      </div>
      <div style={{marginTop:36,display:"flex",flexDirection:"column",gap:12,alignItems:"center",width:"100%",maxWidth:320}}>
        <input
          type="password"
          value={gateInput}
          onChange={e=>{setGateInput(e.target.value);if(gateError)setGateError('');}}
          onKeyDown={e=>{if(e.key==='Enter')submitGate(e);}}
          placeholder="Password"
          autoComplete="current-password"
          style={{width:'100%',height:52,borderRadius:26,border:'1.5px solid #d4cfc7',background:'#f5f1ea',fontFamily:"'Jost',sans-serif",fontSize:15,color:'#1f2226',padding:'0 20px',boxSizing:'border-box',outline:'none'}}
        />
        {gateError&&<p style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:'#c23b0e',margin:0,textAlign:'center'}}>{gateError}</p>}
        {/* CTA_STD_V1_2026_06_10 */}
        <Btn variant="primary" type="button" onClick={submitGate} disabled={gateChecking} style={{width:240}}>
          {gateChecking?"checking…":"enter"}
        </Btn>
      </div>
      <p style={{fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic",fontWeight:500,fontSize:22,color:"#1f2226",textAlign:"center",margin:"28px 0 0",maxWidth:300,lineHeight:1.4}}>
        In the works — not long now.
      </p>
    </div>
  );

  if(authLoading) return (
    <div style={{minHeight:'100vh',background:'#EFEDE3'}}><style>{FONTS+RESET}</style></div>
  );

  // ── LANDING SCREEN (LANDING_FIX_V2_2026_05_26) ───────────────
  // LANDING_TRUE_CENTER_V1_2026_05_28 — flex center, no magic paddingTop
  if(screen==="palate-intro") return (
    <div style={{minHeight:"100vh",background:"#F5F0E4",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px",maxWidth:430,margin:"0 auto",boxSizing:"border-box"}}>
      <style>{FONTS+RESET}</style>
      <WineGlassGlyph capH={60} W={36} sw={2}/>
      <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic",fontSize:28,color:C.ink,textAlign:"center",marginTop:24,marginBottom:8,lineHeight:1.3}}>
        Mapping your taste DNA
      </div>
      <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.ink,fontStyle:"italic",letterSpacing:1,textAlign:"center",marginBottom:40,lineHeight:1.7}}>
        Required so the magic can happen. It only takes a minute.
      </div>
      {!palateIntroCancelled ? (
        <div style={{display:"flex",flexDirection:"column",gap:12,width:"100%"}}>
          <button
            onClick={()=>{
              setPalateIntroCancelled(false);
              setQuizStep(0);
              setQuizScores({});
              setQuizSelections({});
              setQuizResult(null);
              setScreen('palate-quiz');
            }}
            style={{width:"50%",margin:"0 auto",padding:"14px",borderRadius:40,background:C.coral,color:"#fff",fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:"none",cursor:"pointer"}}
          >Let's go!</button>
          <button
            onClick={()=>setPalateIntroCancelled(true)}
            style={{width:"100%",padding:"14px",borderRadius:40,background:"transparent",color:C.ink,fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:`1.5px solid ${C.border}`,cursor:"pointer"}}
          >Cancel</button>
        </div>
      ) : (
        <div style={{width:"100%"}}>
          <div style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:C.coral,textAlign:"center",marginBottom:24,lineHeight:1.7}}>
            Unable to complete your sign up without a palate fingerprint.
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <button
              onClick={()=>setPalateIntroCancelled(false)}
              style={{width:"100%",padding:"14px",borderRadius:40,background:C.coral,color:"#fff",fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:"none",cursor:"pointer"}}
            >Set Up Now</button>
            <button
              onClick={()=>{ setPalateIntroCancelled(false); setScreen('main'); setTab('scan'); }}
              style={{width:"100%",padding:"14px",borderRadius:40,background:"transparent",color:C.mist,fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:`1.5px solid ${C.border}`,cursor:"pointer"}}
            >Skip for Now</button>
          </div>
        </div>
      )}
    </div>
  );
if(screen==="palate-quiz") return (
  <div style={{minHeight:"100vh",background:"#F5F0E4",display:"flex",flexDirection:"column",maxWidth:430,margin:"0 auto",boxSizing:"border-box"}}>
    <style>{FONTS+RESET}</style>
    {!quizResult ? (
      <div style={{flex:1,display:"flex",flexDirection:"column",padding:"40px 24px 32px"}}>
        {quizResumeOffer&&quizStep===0&&(
          <div style={{background:"#fff",border:`1.5px solid ${C.coral}`,borderRadius:12,padding:"18px",marginBottom:24}}>
            <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic",fontSize:22,color:C.ink,lineHeight:1.3,marginBottom:4}}>Pick up where you left off?</div>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:C.mist,marginBottom:14}}>Question {quizResumeOffer.step+1} of {QUIZ_QUESTIONS.length}</div>
            <div style={{display:"flex",gap:10}}>
              <button
                onClick={()=>{ const p=quizResumeOffer; setQuizSelections(p.selections); setQuizScores(computeQuizScores(p.selections)); setQuizStep(p.step); setQuizResumeOffer(null); }}
                style={{flex:1,padding:"12px",borderRadius:40,background:C.coral,color:"#fff",fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:"none",cursor:"pointer"}}
              >Resume</button>
              <button
                onClick={()=>{ clearQuizProgress(); setQuizStep(0); setQuizScores({}); setQuizSelections({}); setQuizResumeOffer(null); }}
                style={{flex:1,padding:"12px",borderRadius:40,background:"transparent",color:C.ink,fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:`1.5px solid ${C.border}`,cursor:"pointer"}}
              >Start over</button>
            </div>
          </div>
        )}
        <div style={{height:3,background:C.border,borderRadius:4,marginBottom:32}}>
          <div style={{height:3,width:`${((quizStep+1)/QUIZ_QUESTIONS.length)*100}%`,background:C.coral,borderRadius:4,transition:"width 0.3s ease"}}/>
        </div>
        <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,letterSpacing:2,color:C.mist,marginBottom:16,textTransform:"uppercase"}}>
          Question {quizStep+1} of {QUIZ_QUESTIONS.length}
        </div>
        <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic",fontSize:26,color:C.ink,lineHeight:1.3,marginBottom:24}}>
          {QUIZ_QUESTIONS[quizStep].text}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {QUIZ_QUESTIONS[quizStep].options.map((opt,i)=>{
            const selected=(quizSelections[QUIZ_QUESTIONS[quizStep].id]||[]).includes(i);
            return (
            <button
              key={i}
              onClick={()=>handleQuizOption(i)}
              style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 18px",borderRadius:12,border:`1.5px solid ${selected?C.coral:C.border}`,background:selected?"#FFF4EF":"#fff",color:C.ink,fontFamily:"'Jost',sans-serif",fontSize:13,cursor:"pointer",textAlign:"left",transition:"border-color 0.15s"}}
            >
              <span style={{flex:1,lineHeight:1.4}}>{opt.label}</span>
              {selected&&<span style={{color:C.coral,fontWeight:700,marginLeft:8,flexShrink:0}}>✓</span>}
              <span style={{fontSize:22,marginLeft:12,flexShrink:0}}>{opt.emoji}</span>
            </button>
            );
          })}
          {QUIZ_QUESTIONS[quizStep].multi&&(()=>{
            const hasSel=(quizSelections[QUIZ_QUESTIONS[quizStep].id]||[]).length>0;
            return (
              <button
                disabled={!hasSel}
                onClick={handleQuizNext}
                style={{marginTop:8,padding:"15px",borderRadius:40,background:C.coral,color:"#fff",fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:"none",cursor:hasSel?"pointer":"not-allowed",opacity:hasSel?1:0.45}}
              >{quizStep<QUIZ_QUESTIONS.length-1?"Next":"See my results"}</button>
            );
          })()}
        </div>
      </div>
    ) : (
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",padding:"48px 24px 110px",overflowY:"auto"}}>
        <div style={{fontSize:56,marginBottom:12}}>{quizResult.profile.emoji}</div>
        <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,letterSpacing:2,textTransform:"uppercase",color:C.mist,marginBottom:8}}>Your palate profile</div>
        <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:32,fontWeight:400,color:C.ink,marginBottom:4,textAlign:"center"}}>{quizResult.profile.title}</div>
        <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.coral,letterSpacing:1,marginBottom:20,textTransform:"uppercase"}}>{quizResult.subtype}</div>
        <div style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:C.mist,lineHeight:1.7,textAlign:"center",marginBottom:32,maxWidth:300}}>{quizResult.profile.description}</div>
        <PalateRadar data={radarValuesFromScores(quizResult.scores)} style={{marginBottom:24}}/>
        <div style={{display:"flex",gap:12,width:"100%",marginBottom:14}}>
          <button
            disabled={quizSaving}
            onClick={()=>requireAuth(async()=>{
              setQuizSaving(true);
              try {
                const {data:{session}}=await supabase.auth.getSession();
                if(session?.user){
                  // DB WRITE: persists raw per-question selections alongside the fingerprint.
                  // NOTE: `quiz_selections` must exist as a JSONB column on swishd_profiles.
                  // If the column does not exist yet, this upsert will fail — add the column
                  // (one JSONB column) before relying on this write.
                  await supabase
                    .from('swishd_profiles')
                    .upsert({user_id:session.user.id,palate_fingerprint:quizResult.fingerprint,quiz_selections:quizResult.selections},{onConflict:'user_id'});
                }
              } catch(e){console.error('[quiz] fingerprint save failed',e);}
              setQuizSaving(false);
              setTab('profile');
              setScreen('main');
            },'save your palate to your account')}
            style={{flex:1,padding:"15px",borderRadius:40,background:C.coral,color:"#fff",fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:"none",cursor:quizSaving?"not-allowed":"pointer",opacity:quizSaving?0.7:1}}
          >{quizSaving?"Saving...":"Save to account"}</button>
          <button
            onClick={()=>{ setQuizStep(0); setQuizScores({}); setQuizSelections({}); setQuizResult(null); }}
            style={{flex:1,padding:"15px",borderRadius:40,background:"transparent",color:C.ink,fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:`1.5px solid ${C.coral}`,cursor:"pointer"}}
          >Retake quiz</button>
        </div>
        <div
          onClick={()=>setScreen('main')}
          style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:C.mist,textAlign:"center",cursor:"pointer",lineHeight:1.6}}
        >Skip it for now, save the magic for later.</div>
      </div>
    )}
    {quizResult&&renderBottomNav()}
  </div>
);
if(screen==="welcome") return (
    <div style={{minHeight:"100vh",background:"#EFEDE3",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",paddingTop:0,paddingLeft:24,paddingRight:24,paddingBottom:18,maxWidth:393,margin:"0 auto",boxSizing:"border-box"}}>
      <style>{FONTS+RESET}</style>
      <img
        src={logoImage}
        alt="Swish'd"
        style={{width:"100%",maxWidth:320,height:"auto",display:"block"}}
      />
      <div style={{maxWidth:340,marginTop:24,textAlign:"center",fontSize:17,lineHeight:1.5,color:"#1f2226"}}>
        <span style={{fontFamily:"'Jost',sans-serif",fontWeight:500,letterSpacing:2,color:"#c23b0e"}}>DEF'</span>
        {" "}
        <span style={{fontFamily:"'Jost',sans-serif",fontWeight:400,color:"#9aa0a6"}}>(past tense)</span>
        {" "}
        <span style={{fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic",fontWeight:500}}>To have been moved by something — a first pour that made you stay a little longer.</span>
      </div>
      <div style={{marginTop:36,display:"flex",flexDirection:"column",gap:12,alignItems:"center"}}>
        <Btn
          variant="primary"
          onClick={()=>{setScreen('age_gate');}}
          style={{width:240}}
        >
          get swish'd
        </Btn>
      </div>
      <div style={{marginTop:40,fontFamily:"'Jost',sans-serif",fontSize:16,fontWeight:500,color:"#1f2226",letterSpacing:1}}>
        ★ ★ ★ ★ ★ &middot; 4,200 Swishers
      </div>
    </div>
  );
  // AGE_GATE_V1_2026_05_27 — second step after Landing, confirms 21+
  if(screen==="age_gate") return (
    <div style={{minHeight:"100vh",background:"#EFEDE3",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",paddingLeft:24,paddingRight:24,maxWidth:393,margin:"0 auto",boxSizing:"border-box",position:"relative"}}>
      <style>{FONTS+RESET}</style>
      <img src={logoImage} alt="Swish'd" style={{position:"absolute",top:20,left:20,width:90,height:"auto"}}/>
      <p style={{fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic",fontWeight:500,fontSize:24,color:"#1f2226",textAlign:"center",margin:"0 0 10px",lineHeight:1.4}}>Are you 21?</p>
      <p style={{fontFamily:"'Jost',sans-serif",fontWeight:400,fontSize:14,color:"#9aa0a6",textAlign:"center",margin:"0 0 48px",maxWidth:300,lineHeight:1.6}}>Swish'd is for those of legal drinking age. By continuing, you confirm you're 21 or older.</p>
      {/* SIGNUP_GATE_V1_2026_06_01 — age confirmed → guest browse, signup modal on first action */}
      {under21 ? (
        <p style={{fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic",fontWeight:500,fontSize:22,color:"#1f2226",textAlign:"center",margin:0,maxWidth:300,lineHeight:1.4}}>Sorry, you must be 21 to use this app.</p>
      ) : (
        <>
          {/* CTA_STD_V1_2026_06_10 */}
          <Btn variant="primary" type="button" onClick={()=>{localStorage.setItem('swishd_age_confirmed','1');setScreen('main');}} style={{width:240}}>confirm you're 21</Btn>
          <button type="button" onClick={()=>setUnder21(true)} style={{marginTop:16,background:"none",border:"none",cursor:"pointer",fontFamily:"'Jost',sans-serif",fontWeight:400,fontSize:13,color:"#9aa0a6",textDecoration:"underline",letterSpacing:0.5}}>I'm under 21</button>
        </>
      )}
    </div>
  );

  // ── SIGNUP SCREEN ─────────────────────────────────────────────
  if(screen==="signup") return <SignupScreen mode={authMode} />;

  // ── CHAT ──────────────────────────────────────────────
  if(chatProfile) return (
    <div style={{maxWidth:430,margin:"0 auto"}}>
      <style>{FONTS+RESET}</style>
      <ChatScreen profile={chatProfile} onBack={()=>setChatProfile(null)} palate={palate}/>
    </div>
  );

  // ── INNER APP ─────────────────────────────────────────
  const isDetail=["wine-detail","results"].includes(screen)||screen.startsWith("payment-");

  return (
    <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",background:"#EFEDE3",minHeight:"100vh",color:C.ink,overflowX:"hidden",maxWidth:430,margin:"0 auto"}}>
      <style>{FONTS+RESET}</style>

      {/* HEADER */}
      {!isDetail&&(
        <div style={{padding:"14px 20px 10px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`1px solid ${C.border}`,position:"sticky",top:0,background:"rgba(250,247,240,0.96)",backdropFilter:"blur(12px)",zIndex:50}}>
          <SwishdLogo size="sm"/>
          {/* CTA_STD_V1_2026_06_10 */}
          <Btn variant="inline" onClick={()=>{setTab("events");setScreen("main");}} style={{fontSize:10,letterSpacing:"1.5px",textTransform:"uppercase"}}>Events</Btn>
        </div>
      )}

      {/* BACK HEADER */}
      {isDetail&&(
        <div style={{padding:"14px 20px 12px",display:"flex",alignItems:"center",gap:14,borderBottom:`1px solid ${C.border}`,position:"sticky",top:0,background:"rgba(250,247,240,0.96)",backdropFilter:"blur(12px)",zIndex:50}}>
          <button onClick={()=>setScreen(screen==="wine-detail"?"results":"main")} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"uppercase"}}>← Back</button>
          {screen==="wine-detail"&&selectedWine&&<span style={{fontSize:15,fontWeight:500,fontStyle:"italic"}}>{selectedWine.name}</span>}
        </div>
      )}

      <div style={{paddingBottom:84}}>

        {/* ══ SCAN ══════════════════════════════════════ */}
        {screen==="main"&&tab==="scan"&&(
          <div className="fade" style={{maxWidth:393,margin:"0 auto",paddingLeft:24,paddingRight:24,paddingTop:24,paddingBottom:20,boxSizing:"border-box"}}>
            <h2 style={{fontSize:26,fontWeight:400,fontStyle:"italic",marginBottom:18,textAlign:"center"}}>SNAP</h2>

            {/* MODE TOGGLE — pill segmented control */}
            {extractedItems.length===0 && !bottleData && (
              <div style={{display:"flex",justifyContent:"center",marginBottom:22}}>
                <div style={{display:"inline-flex",padding:3,background:"#f3efe5",borderRadius:999,gap:0}}>
                  {['menu','bottle'].map(m=>{
                    const active = scanMode===m;
                    return (
                      <button
                        key={m}
                        onClick={()=>{
                          if (active) return;
                          setScanMode(m);
                          // Clear inputs when switching modes
                          setScanText("");
                          setOcrPreviewURL("");
                          setOcrError("");
                          setOcrProgress(0);
                          setExtractedItems([]);
                          setBottleData(null);
                          setCurrentScanId(null);
                          setSessionSelectedIds(new Set());
                          setFoodModalResolved(false);
                          setRedirectFired(false);
                          setFoodPromptShown(false);
                          setFoodActive("");
                          setScanPhase('ranking');
                          setSummaryDrink(null);
                          setFirstSelectedImpressionId(null);
                          setLimitedPairing(false);
                        }}
                        style={{
                          padding:"8px 18px",
                          fontFamily:"'Jost',sans-serif",
                          fontSize:11,
                          letterSpacing:1.5,
                          textTransform:"uppercase",
                          borderRadius:999,
                          border:"none",
                          cursor:"pointer",
                          fontWeight: active ? 600 : 400,
                          color: active ? C.coral : C.mist,
                          background: active ? "#ffffff" : "transparent",
                          boxShadow: active ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                          transition:"all 0.15s",
                        }}
                      >
                        {m==='menu' ? 'Menu' : 'Bottle'}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* PASTE AREA — only show before extraction (either flow) */}
            {extractedItems.length===0 && !bottleData && (
              <>
                {/* ── CAMERA + UPLOAD (OCR) ───────────────────────────── */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  capture="environment"
                  style={{display:"none"}}
                  onChange={(e)=>{const f=e.target.files?.[0];if(f)runOCR(f);e.target.value="";}}
                />
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  style={{display:"none"}}
                  onChange={(e)=>{const f=e.target.files?.[0];if(f)runOCR(f);e.target.value="";}}
                />
                {/* GATE_AUDIT_V1_2026_06_01 — snap actions gated */}
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                  {/* CTA_STD_V1_2026_06_10 */}
                  <IconBtn
                    size={150}
                    label="Take photo"
                    onClick={()=>{ requireAuth(async()=>{ const ok=await requirePalateQuiz(); if(!ok) return; cameraInputRef.current?.click(); },'snap a menu'); }}
                    disabled={ocrLoading||scanning}
                    style={{flex:1,width:"auto",height:120,border:`1.5px solid ${C.border}`,borderRadius:8}}
                  >
                    <svg width="90" height="70" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {/* White camera body */}
                      <rect x="1" y="4" width="20" height="13" rx="1.5" fill="#fff" stroke="#1c1f26" strokeWidth="1"/>
                      {/* Top viewfinder hump */}
                      <rect x="7" y="1.5" width="8" height="3" rx="0.5" fill="#fff" stroke="#1c1f26" strokeWidth="1"/>
                      {/* Lens — black outline ring */}
                      <circle cx="11" cy="10.5" r="3.8" fill="#fff" stroke="#1c1f26" strokeWidth="1.4"/>
                      <circle cx="11" cy="10.5" r="1.6" fill="#1c1f26"/>
                    </svg>
                  </IconBtn>
                  {/* CTA_STD_V1_2026_06_10 */}
                  <Btn
                    variant="primary"
                    onClick={()=>{ requireAuth(async()=>{ const ok=await requirePalateQuiz(); if(!ok) return; galleryInputRef.current?.click(); },'snap a menu'); }}
                    disabled={ocrLoading||scanning}
                    style={{flex:1,gap:8}}
                  >
                    <span style={{fontSize:16}}>↑</span> Upload image
                  </Btn>
                </div>

                {ocrPreviewURL&&(
                  <div style={{marginBottom:12,position:"relative"}}>
                    <img src={ocrPreviewURL} alt="menu preview" style={{width:"100%",maxHeight:180,objectFit:"cover",borderRadius:6,border:`1px solid ${C.border}`,opacity:ocrLoading?0.55:1}}/>
                    {!ocrLoading&&(
                      <button
                        onClick={()=>{setOcrPreviewURL("");setScanText("");setOcrError("");}}
                        style={{position:"absolute",top:8,right:8,background:"rgba(28,31,38,0.75)",color:"white",border:"none",borderRadius:"50%",width:28,height:28,cursor:"pointer",fontSize:14}}
                        aria-label="remove image"
                      >×</button>
                    )}
                  </div>
                )}

                {ocrLoading&&(
                  <div style={{marginBottom:12,fontFamily:"'Jost',sans-serif",fontSize:11,color:C.aqua,letterSpacing:3,textTransform:"uppercase",textAlign:"center"}}>
                    {scanMode==='bottle' ? 'Reading the label…' : 'Reading the menu…'} {ocrProgress}%
                    <div style={{marginTop:6,height:2,background:C.border,borderRadius:1,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${ocrProgress}%`,background:C.aqua,transition:"width 0.2s"}}/>
                    </div>
                  </div>
                )}

                {ocrError&&!ocrLoading&&(
                  <div style={{
                    marginBottom:14,
                    padding:"16px 16px 14px",
                    background:"rgba(204,34,0,0.06)",
                    border:`1px solid rgba(204,34,0,0.25)`,
                    borderRadius:8,
                  }}>
                    <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:18,fontStyle:"italic",color:C.ink,marginBottom:6}}>
                      {ocrError === 'NOT_IMAGE' ? 'Image only' : 'Unable to process — retake'}
                    </div>
                    <p style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:C.mist,lineHeight:1.6,marginBottom:12}}>
                      {ocrError === 'NOT_IMAGE'
                        ? "Only photos work — PDFs and other formats can't be snapped. Take a photo of the menu, or upload a JPG/PNG."
                        : ocrError === 'EMPTY'
                        ? "No readable text detected. Hold the camera steady and frame the menu directly."
                        : ocrError === 'LOW_CONFIDENCE'
                        ? "The photo wasn't sharp enough to read well. Move closer, hold steady, and try again."
                        : ocrError === 'GARBAGE'
                        ? "Text came back unclear. Avoid glare, low light, and angled shots."
                        : "The snap didn't complete. Try again with even lighting and a flat menu."}
                    </p>
                    <div style={{display:"flex",gap:8}}>
                      <button
                        style={{...ss.btn,...ss.btnCoral,flex:1,padding:"12px",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}
                        onClick={()=>{
                          // Clear error + preview, reopen camera
                          setOcrError("");setOcrPreviewURL("");setOcrProgress(0);setScanText("");
                          cameraInputRef.current?.click();
                        }}
                      >
                        <span style={{display:"inline-flex",alignItems:"center",marginRight:2}}>
                          <svg width="18" height="18" viewBox="0 0 22 18" aria-hidden="true">
                            <rect x="1" y="4" width="20" height="13" rx="1.5" fill="#fff" stroke="#1c1f26" strokeWidth="1"/>
                            <rect x="7" y="1.5" width="8" height="3" rx="0.5" fill="#fff" stroke="#1c1f26" strokeWidth="1"/>
                            <circle cx="11" cy="10.5" r="3.8" fill="#fff" stroke="#1c1f26" strokeWidth="1.4"/>
                            <circle cx="11" cy="10.5" r="1.6" fill="#1c1f26"/>
                          </svg>
                        </span> Retake photo
                      </button>
                      <button
                        style={{...ss.btn,...ss.btnOut,flex:1,padding:"12px",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}
                        onClick={()=>{
                          setOcrError("");setOcrPreviewURL("");setOcrProgress(0);setScanText("");
                          galleryInputRef.current?.click();
                        }}
                      >
                        <span style={{fontSize:14}}>↑</span> Upload different
                      </button>
                    </div>
                  </div>
                )}

                {/* Divider — paste fallback */}
                <div style={{display:"flex",alignItems:"center",gap:10,margin:"8px 0 12px"}}>
                  <div style={{flex:1,height:1,background:C.border}}/>
                  <span style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:2,textTransform:"uppercase"}}>or paste / edit</span>
                  <div style={{flex:1,height:1,background:C.border}}/>
                </div>

                <textarea
                  placeholder={scanMode==='bottle'
                    ? "Paste bottle label text here…\n\nDOMAINE BAILLY-REVERDY\nSANCERRE\nLa Mercy Dieu\n2021\n13.5% vol"
                    : "Paste menu text here…\n\n· Sancerre, Loire Valley — $22\n· Barolo DOCG, Piedmont — $32\n· Champagne Brut NV — $28"}
                  value={scanText}
                  onChange={e=>setScanText(e.target.value)}
                  style={{marginBottom:12}}
                />
                {/* SIGNUP_GATE_V1_2026_06_01 — GATE_AUDIT_V1_2026_06_01 coral pill */}
                {/* CTA_STD_V1_2026_06_10 */}
                <Btn variant="primary" style={{width:240,display:"block",margin:"0 auto 8px",opacity:(scanning||ocrLoading||!scanText.trim())?0.5:1}} onClick={()=>requireAuth(runScan,'snap a menu')} disabled={scanning||ocrLoading||!scanText.trim()}>
                  {scanning
                    ? (scanMode==='bottle' ? <>Reading the label<span className="dots"><i>.</i><i>.</i><i>.</i></span></> : <>Reading the menu<span className="dots"><i>.</i><i>.</i><i>.</i></span></>)
                    : (scanMode==='bottle' ? "View Label Details" : "View Drinks Selection")}
                </Btn>
                </>
            )}

            {/* BOTTLE CONFIRMATION SCREEN — single editable card */}
            {bottleData && (
              <div className="fade">
                <div style={{marginBottom:14}}>
                  <span style={ss.sl}>Bottle details</span>
                  <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.mist,marginTop:6,lineHeight:1.6}}>
                    Edit anything OCR misread. Empty fields are fine.
                  </p>
                </div>

                <div style={{...ss.card, padding:"16px 16px 14px", marginBottom:12}}>
                  {/* Name */}
                  <div style={{marginBottom:12}}>
                    <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Name / appellation</div>
                    <input
                      value={bottleData.name}
                      onChange={e=>setBottleData(b=>({...b,name:e.target.value}))}
                      placeholder="e.g. Sancerre"
                      style={{...ss.input,padding:"8px 10px",fontSize:15,fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic",width:"100%"}}
                    />
                  </div>

                  {/* Producer */}
                  <div style={{marginBottom:12}}>
                    <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>
                      {bottleData.category==='wine' ? 'Producer / vineyard / château' : 'Distillery / brand'}
                    </div>
                    <input
                      value={bottleData.producer}
                      onChange={e=>setBottleData(b=>({...b,producer:e.target.value}))}
                      placeholder="e.g. Domaine Bailly-Reverdy"
                      style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                    />
                  </div>

                  {/* Vintage + ABV */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                    <div>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Vintage</div>
                      <input
                        type="number"
                        value={bottleData.vintage==null?"":bottleData.vintage}
                        onChange={e=>{
                          const v=e.target.value;
                          setBottleData(b=>({...b,vintage:v===""?null:Number(v)}));
                        }}
                        placeholder="2021"
                        style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                      />
                    </div>
                    <div>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>ABV %</div>
                      <input
                        type="number"
                        step="0.1"
                        value={bottleData.abv==null?"":bottleData.abv}
                        onChange={e=>{
                          const v=e.target.value;
                          setBottleData(b=>({...b,abv:v===""?null:Number(v)}));
                        }}
                        placeholder="13.5"
                        style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                      />
                    </div>
                  </div>

                  {/* Region + Country */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                    <div>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Region</div>
                      <input
                        value={bottleData.region}
                        onChange={e=>setBottleData(b=>({...b,region:e.target.value}))}
                        placeholder="Loire Valley"
                        style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                      />
                    </div>
                    <div>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Country</div>
                      <input
                        value={bottleData.country}
                        onChange={e=>setBottleData(b=>({...b,country:e.target.value}))}
                        placeholder="France"
                        style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                      />
                    </div>
                  </div>

                  {/* Varietal + Category */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                    <div>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Varietal / grape</div>
                      <input
                        value={bottleData.varietal}
                        onChange={e=>setBottleData(b=>({...b,varietal:e.target.value}))}
                        placeholder={bottleData.category==='wine' ? 'Sauvignon Blanc' : ''}
                        style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                      />
                    </div>
                    <div>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Category</div>
                      <select
                        value={bottleData.category}
                        onChange={e=>setBottleData(b=>({...b,category:e.target.value}))}
                        style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%",background:"#fff"}}
                      >
                        <option value="wine">Wine</option>
                        <option value="spirit">Spirit</option>
                      </select>
                    </div>
                  </div>

                  {/* I drank this toggle */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",paddingTop:8,borderTop:`1px dashed ${C.border}`,marginBottom:bottleData.drank?12:0}}>
                    <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none"}}>
                      <span style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:bottleData.drank?C.coral:C.mist,letterSpacing:1.5,textTransform:"uppercase",fontWeight:bottleData.drank?600:400}}>I drank this</span>
                      <span style={{position:"relative",display:"inline-block",width:36,height:20}}>
                        <input
                          type="checkbox"
                          checked={!!bottleData.drank}
                          onChange={e=>setBottleData(b=>({...b,drank:e.target.checked}))}
                          style={{opacity:0,width:0,height:0,position:"absolute"}}
                        />
                        <span style={{position:"absolute",inset:0,background:bottleData.drank?C.coral:C.border,borderRadius:20,transition:"background 0.2s"}}/>
                        <span style={{position:"absolute",top:2,left:bottleData.drank?18:2,width:16,height:16,background:"#fff",borderRadius:"50%",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
                      </span>
                    </label>
                  </div>

                  {/* Notes — only when drank toggled on */}
                  {bottleData.drank && (
                    <div>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Notes</div>
                      <textarea
                        value={bottleData.notes||""}
                        onChange={e=>setBottleData(b=>({...b,notes:e.target.value}))}
                        placeholder="What made this pour unforgettable…"
                        spellCheck={true}
                        style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%",minHeight:60,fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic"}}
                      />
                    </div>
                  )}
                </div>

                {/* Optional venue / paired-with */}
                <div style={{...ss.card,padding:"12px 14px",marginBottom:14,background:C.linen}}>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:"#1f2226",letterSpacing:2,textTransform:"none",marginBottom:6}}>Optional context</div>
                  <input
                    value={impVenue}
                    onChange={e=>setImpVenue(e.target.value)}
                    placeholder="Venue (e.g. Gary Danko)"
                    style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%",marginBottom:6}}
                  />
                  <input
                    value={impWith}
                    onChange={e=>setImpWith(e.target.value)}
                    placeholder="Paired swisher (optional)"
                    style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                  />
                </div>

                {/* SIGNUP_GATE_V1_2026_06_01 — GATE_AUDIT_V1_2026_06_01 coral pill */}
                {/* CTA_STD_V1_2026_06_10 */}
                <Btn
                  variant="primary"
                  style={{width:240,display:"block",margin:"0 auto 8px",opacity:(scanSaving||!bottleData.drank||(!bottleData.name.trim()&&!bottleData.producer.trim()))?0.5:1}}
                  onClick={()=>requireAuth(saveBottle,'save to My Story')}
                  disabled={scanSaving || !bottleData.drank || (!bottleData.name.trim() && !bottleData.producer.trim())}
                >
                  {scanSaving ? "saving…" : "save to my story"}
                </Btn>

                <button
                  style={{...ss.btn,...ss.btnOut,fontSize:13,padding:"10px",marginBottom:20}}
                  onClick={()=>{setBottleData(null);setScanText("");setOcrPreviewURL("");setOcrError("");setOcrProgress(0);setCurrentScanId(null);}}
                >
                  ← Snap a new bottle
                </button>
              </div>
            )}

            {/* SUMMARY SCREEN — shows after second-distinct-tick (post-modal-resolved). */}
            {/* Replaces the auto-redirect-to-My-Story behavior. Shows the latest tick + paired food. */}
            {scanPhase === 'summary' && summaryDrink && (
              <div className="fade">
                <div style={{textAlign:"center",marginBottom:20,paddingTop:8}}>
                  <span style={{...ss.sl,color:C.aquaD}}>{foodActive ? "Paired with" : "Tonight's pick"}</span>
                  {foodActive && (
                    <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:24,fontStyle:"italic",color:C.ink,marginTop:6,lineHeight:1.2}}>
                      {foodActive}
                    </div>
                  )}
                </div>

                <div style={{...ss.card,padding:"20px 20px 18px",marginBottom:16,border:`1.5px solid ${C.coral}`,position:"relative"}}>
                  <div style={{position:"absolute",top:-9,left:18,background:C.coral,color:"#fff",fontFamily:"'Jost',sans-serif",fontSize:9,letterSpacing:2,textTransform:"uppercase",padding:"2px 9px",borderRadius:4,fontWeight:600}}>Saved to My Story</div>

                  <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:22,color:C.ink,lineHeight:1.25,marginTop:4,marginBottom:6}}>
                    {summaryDrink.wine}
                  </div>

                  <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
                    {summaryDrink.category && (
                      <span style={{...ss.tag,fontSize:10,padding:"3px 9px",background:"rgba(0,180,200,0.08)",color:C.aquaD,border:`1px solid rgba(0,180,200,0.2)`,textTransform:"capitalize"}}>{summaryDrink.category}</span>
                    )}
                    {summaryDrink.sub_category && (
                      <span style={{...ss.tag,fontSize:10,padding:"3px 9px",background:"rgba(0,180,200,0.08)",color:C.aquaD,border:`1px solid rgba(0,180,200,0.2)`,textTransform:"capitalize"}}>{summaryDrink.sub_category}</span>
                    )}
                    {typeof summaryDrink.price === 'number' && (
                      <span style={{...ss.tag,fontSize:10,padding:"3px 9px",background:"rgba(0,180,200,0.08)",color:C.aquaD,border:`1px solid rgba(0,180,200,0.2)`}}>${summaryDrink.price}</span>
                    )}
                  </div>

                  <div style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:11,color:C.mist,lineHeight:1.8}}>
                    {summaryDrink.venue && <div>📍 {summaryDrink.venue}</div>}
                    {summaryDrink.date && <div>📅 {summaryDrink.date}</div>}
                  </div>
                </div>

                <button
                  style={{...ss.btn,...ss.btnCoral,fontSize:13,padding:"12px",marginBottom:10}}
                  onClick={()=>{
                    // Return to the same scan's ranked list to pick another drink.
                    // Keep summaryDrink so we remember; flip phase to 'ranking'.
                    setScanPhase('ranking');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Another round?
                </button>

                {/* CTA_STD_V1_2026_06_10 */}
                <IconBtn
                  size={64}
                  label="Snap a new menu"
                  onClick={()=>{
                    setExtractedItems([]);setScanText("");setScanResult(null);setOcrPreviewURL("");setOcrError("");setOcrProgress(0);setScanRawText("");setCurrentScanId(null);
                    setFoodModalOpen(false);setFoodPromptShown(false);setFoodInput("");setFoodActive("");setFoodReranking(false);setFoodModalStage('ask');
                    setSessionSelectedIds(new Set());setFoodModalResolved(false);setRedirectFired(false);
                    setScanPhase('ranking');setSummaryDrink(null);setFirstSelectedImpressionId(null);setLimitedPairing(false);
                  }}
                  style={{width:"auto",height:"auto",margin:"0 auto 20px",color:C.ink}}
                >
                  <span style={{fontSize:20,lineHeight:1}}>←</span>
                </IconBtn>
              </div>
            )}

            {/* M / Ranking / Pairing with Food — full-screen frame, replaces old modal */}
            {scanPhase === 'pairing_prompt' && (
              <div className="fade" style={{padding:"60px 20px 40px",textAlign:"center"}}>
                <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:32,fontStyle:"italic",color:C.ink,lineHeight:1.2,marginBottom:14}}>
                  Pair with food?
                </div>
                <p style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:C.mist,lineHeight:1.7,marginBottom:36,maxWidth:280,marginLeft:"auto",marginRight:"auto"}}>
                  We'll re-rank these drinks for the meal if you are.
                </p>
                <div style={{display:"flex",flexDirection:"row",gap:12,maxWidth:340,margin:"0 auto"}}>
                  {/* CTA_STD_V1_2026_06_10 */}
                  <Btn
                    variant="secondary"
                  style={{flex:1}}
                    onClick={()=>{
                      setFoodModalResolved(true);
                      setScanPhase('food_input');
                      setFoodInput("");
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      writeSessionEvent({
                        scanId: currentScanId,
                        eventType: 'food_pairing_decided',
                        meta: { decision: 'yes' },
                      }).catch(()=>{});
                    }}
                  >
                    Yes
                  </Btn>
                  {/* CTA_STD_V1_2026_06_10 */}
                  <Btn variant="secondary" style={{flex:1,borderColor:"#1C1F26",color:"#1C1F26"}} onClick={handleNoFoodFinalize}>
                    No
                  </Btn>
                </div>
              </div>
            )}

            {/* M / Food / Add Food — full-screen frame for typing/scanning the dish */}
            {scanPhase === 'food_input' && (
              <div className="fade" style={{padding:"40px 20px"}}>
                <div style={{textAlign:"center",marginBottom:22}}>
                  <span style={ss.sl}>What are you having?</span>
                  <p style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:C.mist,marginTop:8,lineHeight:1.6,maxWidth:280,marginLeft:"auto",marginRight:"auto"}}>
                    Enter the dish you're having, or snap it from the menu.
                  </p>
                </div>

                {/* Hidden inputs for food-dish OCR */}
                <input
                  ref={foodCameraRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  capture="environment"
                  style={{display:"none"}}
                  onChange={(e)=>{const f=e.target.files?.[0];if(f)runFoodOcr(f);e.target.value="";}}
                />
                <input
                  ref={foodGalleryRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  style={{display:"none"}}
                  onChange={(e)=>{const f=e.target.files?.[0];if(f)runFoodOcr(f);e.target.value="";}}
                />

                <input
                  autoFocus
                  value={foodInput}
                  onChange={e=>setFoodInput(e.target.value)}
                  onKeyDown={e=>{
                    if (e.key === 'Enter' && foodInput.trim()) {
                      setScanPhase('apply_confirm');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  placeholder="e.g. ribeye steak, oysters, pad thai"
                  style={{...ss.input,padding:"12px 14px",fontSize:15,width:"100%",marginBottom:14}}
                />

                {/* Scan / upload row */}
                <div style={{display:"flex",gap:8,marginBottom:14}}>
                  <button
                    style={{...ss.btn,...ss.btnOut,flex:1,padding:"11px",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}
                    onClick={()=>foodCameraRef.current?.click()}
                    disabled={foodOcrLoading}
                  >
                    <span style={{display:"inline-flex",alignItems:"center"}}>
                      <svg width="15" height="15" viewBox="0 0 22 18" aria-hidden="true">
                        <rect x="1" y="4" width="20" height="13" rx="1.5" fill="#fff" stroke="#1c1f26" strokeWidth="1"/>
                        <rect x="7" y="1.5" width="8" height="3" rx="0.5" fill="#fff" stroke="#1c1f26" strokeWidth="1"/>
                        <circle cx="11" cy="10.5" r="3.8" fill="#fff" stroke="#1c1f26" strokeWidth="1.4"/>
                        <circle cx="11" cy="10.5" r="1.6" fill="#1c1f26"/>
                      </svg>
                    </span> Snap from menu
                  </button>
                  <button
                    style={{...ss.btn,...ss.btnOut,flex:1,padding:"11px",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}
                    onClick={()=>foodGalleryRef.current?.click()}
                    disabled={foodOcrLoading}
                  >
                    <span style={{fontSize:14}}>↑</span> Upload photo
                  </button>
                </div>

                {foodOcrLoading && (
                  <div style={{marginBottom:14,fontFamily:"'Jost',sans-serif",fontSize:11,color:C.aqua,letterSpacing:2,textTransform:"uppercase",textAlign:"center"}}>
                    Reading the dish… {foodOcrProgress}%
                    <div style={{marginTop:6,height:2,background:C.border,borderRadius:1,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${foodOcrProgress}%`,background:C.aqua,transition:"width 0.2s"}}/>
                    </div>
                  </div>
                )}
                {foodOcrError && !foodOcrLoading && (
                  <div style={{marginBottom:14,fontFamily:"'Jost',sans-serif",fontSize:11,color:C.coral,textAlign:"center"}}>{foodOcrError}</div>
                )}

                <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:8}}>
                  {/* CTA_STD_V1_2026_06_10 */}
                  <Btn
                    variant="secondary"
                    style={{flex:1}}
                    disabled={!foodInput.trim() || foodOcrLoading}
                    onClick={()=>{
                      setScanPhase('apply_confirm');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Continue
                  </Btn>
                  <button
                    style={{...ss.btn,...ss.btnOut,padding:"12px",fontSize:12,color:C.mist}}
                    onClick={()=>{
                      // Back to ranking; first drink is still ticked, food not paired
                      setFoodInput("");
                      setFoodOcrError("");
                      setScanPhase('ranking');
                    }}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {/* M / Ranking / Update Ranking — confirm to apply food context */}
            {scanPhase === 'apply_confirm' && (
              <div className="fade" style={{padding:"60px 20px 40px",textAlign:"center"}}>
                <span style={ss.sl}>Pairing with</span>
                <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:28,fontStyle:"italic",color:C.ink,lineHeight:1.2,margin:"10px 0 30px"}}>
                  {foodInput.trim()}
                </div>
                <p style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:C.mist,lineHeight:1.7,marginBottom:36,maxWidth:300,marginLeft:"auto",marginRight:"auto"}}>
                  Refreshing your drink lineup.
                </p>
                <div style={{display:"flex",flexDirection:"row",gap:12,maxWidth:340,margin:"0 auto"}}>
                  {/* CTA_STD_V1_2026_06_10 */}
                  <Btn
                    variant="primary"
                    style={{width:"100%"}}
                    disabled={foodReranking}
                    onClick={async ()=>{
                      const f = foodInput.trim();
                      if (!f) return;
                      // Signal: food context added (fired before rerank, so it's recorded even if rerank errors)
                      writeSessionEvent({
                        scanId: currentScanId,
                        eventType: 'food_context_added',
                        meta: { food: f },
                      }).catch(()=>{});
                      // Run rerank (it sets foodActive, deletes first drink, resets checkboxes).
                      // After rerank completes, we want the user on the ranked list.
                      await runFoodRerank(f);
                      setScanPhase('ranking');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    {foodReranking ? <>Re-ranking<span className="dots"><i>.</i><i>.</i><i>.</i></span></> : "Re-rank"}
                  </Btn>
                  <button
                    style={{...ss.btn,...ss.btnOut,padding:"12px",fontSize:12,color:C.mist}}
                    disabled={foodReranking}
                    onClick={()=>{ setScanPhase('food_input'); }}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {/* CONFIRMATION SCREEN — editable extracted items, rank-ordered */}
            {extractedItems.length>0 && scanPhase === 'ranking' && (
              <div className="fade" style={{maxWidth:393,margin:"0 auto",paddingLeft:24,paddingRight:24,boxSizing:"border-box"}}>
                <div style={{marginBottom:14}}>
                  {/* UI_POLISH_V1_2026_06_01 — darker ranking subheadline */}
                  <span style={{...ss.sl,color:"#1f2226"}}>
                    {ranking ? "Tailoring to your taste" :
                     foodReranking ? "Re-ranking for your meal…" :
                     foodActive ? `Final ranking paired with ${foodActive}` :
                     `Top picks · ${extractedItems.length} drink${extractedItems.length===1?"":"s"}`}
                  </span>
                  {limitedPairing && foodActive && !foodReranking && (
                    <p style={{fontFamily:"'Jost',sans-serif",fontWeight:400,fontSize:11,color:C.coral,marginTop:4,fontStyle:"italic",lineHeight:1.5}}>
                      Perfect pairings, your way
                    </p>
                  )}
                  {foodActive && !foodReranking && (
                    /* CTA_STD_V1_2026_06_10 */
                    <Btn
                      variant="primary"
                      onClick={()=>{ setFoodInput(""); setScanPhase('food_input'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      style={{marginTop:8,marginRight:8,background:C.coral,color:"#fff",fontFamily:"'Jost',sans-serif",fontSize:9,letterSpacing:2,textTransform:"uppercase",padding:"2px 9px",borderRadius:4,fontWeight:600,height:"auto",width:"auto"}}
                    >
                      Change food
                    </Btn>
                  )}
                </div>

                {/* CARD_DENSITY_V1_2026_05_27 — tighter cards, 2-col rows, all reasons collapsed */}
                {extractedItems.map((it, idx) => (
                  <div
                    key={it._id}
                    style={{
                      ...ss.card,
                      padding:"12px 12px 10px",
                      marginBottom:8,
                      ...(idx===0 && typeof it.match_score==='number' ? {border:`1.5px solid ${C.coral}`,boxShadow:"0 2px 12px rgba(204,34,0,0.08)"} : {}),
                    }}
                  >
                    {/* Top match label — sits above header on card #1 */}
                    {idx===0 && typeof it.match_score==='number' && (
                      <div style={{marginBottom:4}}>
                        <span style={{fontFamily:"'Jost',sans-serif",fontSize:9,letterSpacing:1.5,textTransform:"uppercase",color:C.coral,fontWeight:600}}>Top match</span>
                      </div>
                    )}

                    {/* Header: rank + price + delete */}
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                      {typeof it.rank==='number' && (
                        <span style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:2,fontWeight:500}}>#{it.rank}</span>
                      )}
                      <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}}>
                        {typeof it.price==='number' && (
                          <span style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:15,fontStyle:"italic",color:C.ink}}>${it.price}</span>
                        )}
                        {/* CTA_STD_V1_2026_06_10 */}
                        <IconBtn
                          size={28}
                          onClick={()=>setExtractedItems(prev=>prev.filter((_,i)=>i!==idx))}
                          style={{border:`1px solid ${C.border}`,color:C.mist,borderRadius:"50%",fontSize:15,flexShrink:0,lineHeight:1}}
                          aria-label="Remove item"
                        >×</IconBtn>
                      </div>
                    </div>

                    {/* Name + Category pill — CARD_RESTRUCTURE_V1_2026_06_01 */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:10,marginBottom:8,alignItems:"center"}}>
                      <input
                        value={it.name}
                        onChange={e=>setExtractedItems(prev=>prev.map((x,i)=>i===idx?{...x,name:e.target.value}:x))}
                        placeholder="Drink name"
                        style={{...ss.input,fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:14,fontStyle:"italic",padding:"6px 8px",height:36}}
                      />
                      {/* UI_POLISH_V1_2026_06_01 — category dropdown */}
                      <select
                        value={it.category||'unknown'}
                        onChange={e=>setExtractedItems(prev=>prev.map((x,i)=>i===idx?{...x,category:e.target.value}:x))}
                        style={{display:"inline-block",fontFamily:"'Jost',sans-serif",fontSize:10,letterSpacing:1.5,textTransform:"uppercase",background:"rgba(31,34,38,0.06)",color:"#1f2226",padding:"4px 24px 4px 10px",borderRadius:12,border:"none",cursor:"pointer",appearance:"none",WebkitAppearance:"none",MozAppearance:"none",backgroundImage:"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24'><path fill='none' stroke='%231f2226' stroke-width='2' d='M6 9l6 6 6-6'/></svg>\")",backgroundRepeat:"no-repeat",backgroundPosition:"right 8px center",backgroundSize:"8px"}}
                      >
                        <option value="wine">WINE</option>
                        <option value="cocktail">COCKTAIL</option>
                        <option value="spirit">SPIRIT</option>
                        <option value="beer">BEER</option>
                      </select>
                    </div>

                    {/* Reason — always visible — CARD_RESTRUCTURE_V1_2026_06_01 */}
                    {it.reason && (
                      <div style={{fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic",fontSize:14,lineHeight:1.4,color:"#6b7178",marginBottom:10}}>
                        {it.reason}
                      </div>
                    )}

                    {/* Producer — full width */}
                    {(it.category==="cocktail"||it.category==="unknown") ? (
                      <textarea
                        value={it.producer||""}
                        onChange={e=>setExtractedItems(prev=>prev.map((x,i)=>i===idx?{...x,producer:e.target.value}:x))}
                        placeholder={it.category==="cocktail"?"e.g. tequila, lime":"e.g. ingredients"}
                        rows={1}
                        style={{...ss.input,padding:"6px 8px",fontSize:12,width:"100%",fontFamily:"inherit",resize:"none",minHeight:36,height:36,marginBottom:8}}
                      />
                    ) : (
                      <input
                        value={it.producer||""}
                        onChange={e=>setExtractedItems(prev=>prev.map((x,i)=>i===idx?{...x,producer:e.target.value}:x))}
                        placeholder={it.category==="wine"?"Producer / château":""}
                        style={{...ss.input,padding:"6px 8px",fontSize:12,width:"100%",height:36,marginBottom:8}}
                      />
                    )}

                    {/* Who (paired swisher) + Where (venue) on one row (50/50) */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:8}}>
                      <select
                        value={impWith}
                        onChange={e=>setImpWith(e.target.value)}
                        style={{...ss.input,padding:"6px 8px",fontSize:12,width:"100%",height:36}}
                      >
                        <option value="">Who are you with?</option>
                        <option value="solo">Solo</option>
                        <option value="friends">Friends</option>
                        <option value="date">Date</option>
                        <option value="group">Group</option>
                      </select>
                      <input
                        value={impVenue}
                        onChange={e=>setImpVenue(e.target.value)}
                        placeholder="Where are you?"
                        style={{...ss.input,padding:"6px 8px",fontSize:12,width:"100%",height:36}}
                      />
                    </div>
                    {/* Saved mood display */}
                    {(impMood[it._id]||[]).length>0&&(
                      <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:8}}>
                        {(impMood[it._id]||[]).map(m=>(
                          <span key={m} style={{fontFamily:"'Jost',sans-serif",fontSize:9,letterSpacing:1,textTransform:"capitalize",color:C.aqua}}>{m}</span>
                        ))}
                      </div>
                    )}
                    {/* Footer: select toggle */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:10}}>
                      {/* CTA_STD_V1_2026_06_10 */}
                      <Btn
                        variant="inline"
                        onClick={()=>handleSelectToggle(idx, !it.drank)}
                        style={{display:"flex",alignItems:"center",gap:8,userSelect:"none",color:it.drank?C.coral:C.mist}}
                      >
                        <span style={{fontFamily:"'Jost',sans-serif",fontSize:11,letterSpacing:1.5,textTransform:"none",fontWeight:it.drank?600:400}}>{it.drank ? "Saved to My Story" : "Save"}</span>
                        <span style={{
                          position:"relative",
                          display:"inline-flex",
                          alignItems:"center",
                          justifyContent:"center",
                          width:22,
                          height:22,
                          borderRadius:5,
                          border:`1.5px solid ${it.drank?C.coral:C.border}`,
                          background:it.drank?C.coral:"#fff",
                          transition:"all 0.15s",
                        }}>
                          {it.drank && (
                            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                              <path d="M3 8.5l3 3 7-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </span>
                      </Btn>
                    </div>
                  </div>
                ))}

                {/* Add a missed item */}
                {/* CTA_STD_V1_2026_06_10 */}
                <Btn
                  variant="inline"
                  onClick={()=>setExtractedItems(prev=>[...prev,{name:"",producer:"",category:"wine",sub_category:"",price:null,drank:true,notes:"",_id:Math.random().toString(36).slice(2,9)}])}
                  style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,width:"100%",marginBottom:14}}
                >
                  + Add a drink we missed
                </Btn>

                {/* Optional venue / paired-with for the whole batch */}
                <div style={{...ss.card,padding:"12px 14px",marginBottom:14,background:C.linen}}>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:"#1f2226",letterSpacing:2,textTransform:"none",marginBottom:6}}>Optional context</div>
                  <input
                    value={impVenue}
                    onChange={e=>setImpVenue(e.target.value)}
                    placeholder="Venue (e.g. Gary Danko)"
                    style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%",marginBottom:6}}
                  />
                  <input
                    value={impWith}
                    onChange={e=>setImpWith(e.target.value)}
                    placeholder="Paired swisher (optional)"
                    style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                  />
                </div>

                {/* CTA_STD_V1_2026_06_10 */}
                <IconBtn
                  size={64}
                  label="Snap a new menu"
                  onClick={()=>{
                    setExtractedItems([]);setScanText("");setScanResult(null);setOcrPreviewURL("");setOcrError("");setOcrProgress(0);setScanRawText("");setCurrentScanId(null);
                    setFoodModalOpen(false);setFoodPromptShown(false);setFoodInput("");setFoodActive("");setFoodReranking(false);setFoodModalStage('ask');
                    setSessionSelectedIds(new Set());setFoodModalResolved(false);setRedirectFired(false);
                    setScanPhase('ranking');setSummaryDrink(null);setFirstSelectedImpressionId(null);setLimitedPairing(false);
                  }}
                  style={{width:"auto",height:"auto",margin:"0 auto 20px",color:C.ink}}
                >
                  <span style={{fontSize:20,lineHeight:1}}>←</span>
                </IconBtn>
              </div>
            )}

          </div>
        )}

        {/* ══ RESULTS ═══════════════════════════════════ */}
        {screen==="results"&&(
          <div className="fade" style={{padding:"24px 20px"}}>
            <span style={ss.sl}>Tailoring to your taste.</span>
            <h2 style={{fontSize:24,fontWeight:400,fontStyle:"italic",marginBottom:4}}>Tonight's perfect pours.</h2>
            <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.mist,marginBottom:18}}>{food?`Paired with ${food}. `:""}By your taste history.</p>
            {picks.map((w,i)=>{
              const max=picks[0]?.score||1;
              const pct=Math.max(6,Math.round((w.score/max)*100));
              return (
                <div key={w.id} style={{...(i===0?ss.cardHi:ss.card),padding:"15px 17px",marginBottom:10,cursor:"pointer"}} onClick={()=>{setSelectedWine(w);setScreen("wine-detail");getAI(w,food);}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                        {i===0&&<span style={{...ss.tag,background:"rgba(204,34,0,0.07)",color:C.coral,border:"1px solid rgba(204,34,0,0.2)"}}>Top Pick</span>}
                        <span style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:1}}>#{i+1}</span>
                      </div>
                      <div style={{fontSize:i===0?18:15,fontWeight:400,marginBottom:2}}>{w.name}</div>
                      <div style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:11,color:C.mist,marginBottom:7}}>{w.grape} · {w.region}</div>
                      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                        {w.aromas.slice(0,3).map(a=><span key={a} style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,background:"rgba(237,224,204,0.5)",padding:"2px 8px",borderRadius:20}}>{a}</span>)}
                      </div>
                    </div>
                    <div style={{textAlign:"right",marginLeft:12}}>
                      <div style={{fontSize:19,color:i===0?C.coral:C.aqua,fontWeight:400}}>${w.price}</div>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,marginTop:2}}>/glass</div>
                    </div>
                  </div>
                  <div style={{height:2,background:C.border,marginTop:10,borderRadius:4}}>
                    <div style={{height:2,width:`${pct}%`,background:i===0?C.coral:C.aqua,borderRadius:4}}/>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ══ WINE DETAIL ═══════════════════════════════ */}
        {screen==="wine-detail"&&selectedWine&&(
          <div className="fade" style={{padding:"24px 20px"}}>
            <div style={{background:"linear-gradient(135deg,#F0ECE0,#FAF7F0)",border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px",marginBottom:14}}>
              <span style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"uppercase",marginBottom:3,display:"block"}}>{selectedWine.region}</span>
              <div style={{fontSize:24,fontWeight:400,marginBottom:2}}>{selectedWine.name}</div>
              <div style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:13,color:C.mist,marginBottom:8}}>{selectedWine.grape}</div>
              <div style={{fontStyle:"italic",fontSize:15,color:C.ink,opacity:0.7}}>{selectedWine.desc}</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
              {[["Body",selectedWine.body],["Style",selectedWine.style],["Price",`$${selectedWine.price}`],["Glass","By the glass"]].map(([k,v])=>(
                <div key={k} style={{...ss.card,padding:"13px"}}>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,marginBottom:3,textTransform:"uppercase"}}>{k}</div>
                  <div style={{fontSize:14,textTransform:"capitalize",color:k==="Price"?C.coral:C.ink}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{...ss.card,padding:"15px",marginBottom:10}}>
              <span style={ss.sl}>Aroma profile</span>
              <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                {selectedWine.aromas.map(a=><span key={a} style={{border:"1.5px solid rgba(0,180,200,0.27)",color:C.aquaD,padding:"6px 12px",fontSize:12,fontFamily:"'Jost',sans-serif",borderRadius:40}}>{a}</span>)}
              </div>
            </div>
            <div style={{...ss.card,padding:"15px",marginBottom:10}}>
              <span style={ss.sl}>Pairs with</span>
              <div style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:13,color:C.mist,lineHeight:1.9}}>{selectedWine.foods.join("  ·  ")}</div>
            </div>
            <div style={{...ss.card,padding:"15px",marginBottom:16,border:`1px solid rgba(232,184,75,0.27)`,background:"rgba(232,184,75,0.04)"}}>
              <span style={ss.sl}>AI sommelier</span>
              {loadingAI?<div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.mist,letterSpacing:2}}>Consulting the cellar…</div>
              :aiInsight?<div style={{fontStyle:"italic",fontSize:16,color:C.ink,lineHeight:1.6}}>{aiInsight}</div>
              :<div style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:C.mist}}>{food?"Tap to get a pairing note.":"Select food above to get AI insight."}</div>}
              {!aiInsight&&!loadingAI&&food&&(
                <button style={{...ss.btn,background:C.gold,color:C.ink,padding:"9px",fontSize:13,fontStyle:"italic",borderRadius:3,marginTop:8}} onClick={()=>getAI(selectedWine,food)}>Get pairing note</button>
              )}
            </div>
            <button style={{...ss.btn,...ss.btnCoral,marginBottom:10}} onClick={()=>setAddWine(selectedWine)}>Add to my diary</button>
            <button style={{...ss.btn,...ss.btnDark}} onClick={()=>{setTab("events");setScreen("main");}}>Find events with this wine</button>
          </div>
        )}

        {/* ══ MATCH ═════════════════════════════════════ */}
        {screen==="main"&&tab==="match"&&(()=>{
          const p=PROFILES[profIdx%PROFILES.length];
          return (
            <div className="fade" style={{padding:"24px 20px"}}>
              <span style={{...ss.sl,color:"#1C1F26"}}>Find {lookingFor==="woman"?"Female":"Male"} Swishers Near You</span>
              <h2 style={{fontFamily:"'Jost',sans-serif",fontSize:20,fontWeight:600,fontStyle:"normal",textTransform:"uppercase",letterSpacing:2,marginBottom:4}}>{p.compatibility}% swish'd match</h2>
              <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.aqua,marginBottom:18}}>Where Tastes Mingle</p>
              <div style={{...ss.card,marginBottom:18}}>
                <div style={{padding:"22px 18px 14px",borderBottom:`1px solid ${C.border}`,display:"flex",gap:14,alignItems:"center"}}>
                  <div style={{width:60,height:60,borderRadius:"50%",background:C.border,display:"flex",alignItems:"center",justifyContent:"center",fontSize:30}}>{p.photo}</div>
                  <div>
                    <div style={{fontSize:20,fontWeight:400}}>{p.name}, {p.age}</div>
                    <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.mist}}>{p.job} · {p.city}</div>
                    <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.coral,marginTop:2}}>{p.winePersonality}</div>
                  </div>
                </div>
                <div style={{padding:"14px 18px",borderBottom:`1px solid ${C.border}`,background:"rgba(0,180,200,0.04)"}}>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:"#1f2226",letterSpacing:2,textTransform:"none",marginBottom:5}}>Last poured</div>
                  <div style={{fontSize:15,fontWeight:400}}>{p.favoriteWine}</div>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.mist}}>{p.lastPour}</div>
                </div>
                {p.prompts.map((pr,i)=>(
                  <div key={i} style={{padding:"14px 18px",borderBottom:i<p.prompts.length-1?`1px solid ${C.border}`:"none"}}>
                    <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:"#1f2226",letterSpacing:1,marginBottom:5}}>{pr.q}</div>
                    <div style={{fontStyle:"italic",fontSize:16,color:C.ink,lineHeight:1.4}}>{pr.a}</div>
                  </div>
                ))}
                <div style={{padding:"12px 18px",background:"rgba(237,232,223,0.33)"}}>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:"#1f2226",letterSpacing:2,textTransform:"none",marginBottom:5}}>Shared aromas</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {p.aromas.map(a=><span key={a} style={{...ss.tag,background:"rgba(204,34,0,0.07)",color:C.coral,border:"1px solid rgba(204,34,0,0.2)"}}>{a}</span>)}
                  </div>
                </div>
              </div>
              {/* GATE_AUDIT_V1_2026_06_01 — match actions gated */}
              <div style={{display:"flex",gap:12,marginBottom:24}}>
                {/* CTA_STD_V1_2026_06_10 */}
                <Btn variant="secondary" style={{flex:1,gap:6}} onClick={()=>requireAuth(()=>setProfIdx(x=>x+1),'pass')}>
  <span style={{fontSize:18,fontWeight:300,color:C.ink,lineHeight:1}}>✕</span>
  <WineGlassGlyph capH={13} W={8} sw={1.0} color={C.ink}/>
</Btn>
               {/* SIGNUP_GATE_V1_2026_06_01 */}
                {/* CTA_STD_V1_2026_06_10 */}
                <Btn variant="primary" style={{flex:1,gap:6}}
                  onClick={()=>requireAuth(()=>{
                    const now=Date.now();
                    setConnections(prev=>{
                      const exists=prev.find(c=>c.profile.id===p.id);
                      if(exists) return prev;
                      return [{profile:p,connectedAt:now,lastInteraction:now},...prev];
                    });
                    setChatProfile(p);
                    showToast("swish'd!",C.coral);
                    setProfIdx(x=>x+1);
                  },'swish another swisher')}>
                  <WineGlassGlyph capH={13} W={8} sw={1.0} color="white"/>
                  <WineGlassGlyph capH={13} W={8} sw={1.0} color="white"/>
                  {/* UI_POLISH_V1_2026_06_01 — matching Landing CTA font */}
                  {/* CTA_FINAL_V1_2026_06_10 — text only; T-T glyphs unchanged */}
                  <span style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:17,letterSpacing:1}}>swish'd</span>
                </Btn>
              </div>

              {/* ── SWISH'D CONNECTIONS ── */}
              {(()=>{
                const now=Date.now();
                const FOURTEEN_DAYS=14*24*60*60*1000;
                const active=connections.filter(c=>now-c.lastInteraction<FOURTEEN_DAYS).slice(0,7);
                const inactive=connections.filter(c=>now-c.lastInteraction>=FOURTEEN_DAYS);
                // Get impressions that have a "With" field — those are story connections
                const storyConnections=impressions.filter(h=>h.with&&h.with.trim().length>0);
                return (
                  <div style={{marginTop:4}}>
                    <span style={{...ss.sl,color:"#1f2226"}}>Matched Swishers Nearby You</span>
                    {active.length===0&&storyConnections.length===0&&(
                      <div style={{...ss.card,padding:"16px 18px",textAlign:"center"}}>
                        <div style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:C.mist,lineHeight:1.8}}>
                          Swish'd someone to start a connection.<br/>
                          <em style={{fontSize:11}}>Up to 7 active connections shown.</em>
                        </div>
                      </div>
                    )}
                    {active.map((c,i)=>{
                      const daysAgo=Math.floor((now-c.lastInteraction)/(24*60*60*1000));
                      const isRecent=daysAgo===0;
                      return (
                        <div key={c.profile.id} style={{...ss.card,padding:"14px 16px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",gap:14,border:isRecent?`1px solid rgba(204,34,0,0.2)`:`1px solid ${C.border}`}}
                          onClick={()=>{
                            setConnections(prev=>prev.map(x=>x.profile.id===c.profile.id?{...x,lastInteraction:Date.now()}:x));
                            setChatProfile(c.profile);
                          }}>
                          <div style={{width:46,height:46,borderRadius:"50%",background:C.border,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>{c.profile.photo}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:16,fontWeight:400,marginBottom:2}}>{c.profile.name}, {c.profile.age}</div>
                            <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist}}>{c.profile.winePersonality}</div>
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:isRecent?C.coral:C.mist,letterSpacing:"0.5px"}}>{isRecent?"Today":`${daysAgo}d ago`}</div>
                            <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,marginTop:3}}>{c.profile.compatibility}% match</div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Story connections from My Impressions */}
                    {storyConnections.length>0&&(
                      <>
                        <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"uppercase",marginTop:16,marginBottom:8}}>From My Story</div>
                        {storyConnections.slice(0,7).map((h,i)=>(
                          <div key={h.id||i} style={{...ss.card,padding:"14px 16px",marginBottom:10,display:"flex",alignItems:"center",gap:14,border:`1px solid ${C.border}`}}>
                            <div style={{width:46,height:46,borderRadius:"50%",background:C.border,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>👤</div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:16,fontWeight:400,marginBottom:2}}>{h.with}</div>
                              <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist}}>{h.wine||"A shared pour"} · {h.venue||""}</div>
                              {h.notes&&<div style={{fontStyle:"italic",fontSize:12,color:C.ink,opacity:0.7,marginTop:3}}>"{h.notes.substring(0,60)}{h.notes.length>60?"…":""}"</div>}
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              {h.date&&<div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist}}>{h.date}</div>}
                              {h.rating>0&&<div style={{color:C.coral,fontSize:11,marginTop:3}}>{"★".repeat(h.rating)}</div>}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    {inactive.length>0&&(
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,textAlign:"center",marginTop:8,letterSpacing:"0.5px"}}>
                        {inactive.length} inactive connection{inactive.length>1?"s":""} · no activity in 14+ days
                      </div>
                    )}
                  </div>
                );
              })()}

            </div>
          );
        })()}

        {/* ══ DIARY ═════════════════════════════════════ */}
        {screen==="main"&&tab==="diary"&&(
          <div className="fade" style={{padding:"24px 20px"}}>
            <h2 style={{fontSize:26,fontWeight:400,fontStyle:"italic",marginBottom:2}}>My Story.</h2>
            <p style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.aqua,marginBottom:18,fontStyle:"italic",letterSpacing:"0.04em"}}>Notes & Details</p>

            {/* ADD NEW ENTRY */}
            <div style={{...ss.card,padding:"16px 18px",marginBottom:20,border:"1px solid rgba(204,34,0,0.12)"}}>
              <span style={ss.sl}>Add an entry</span>
              {[
                {label:"Wine / Cocktails / Spirits",placeholder:"e.g. Sancerre 2022, Old Fashioned, Mezcal",val:impWine,set:setImpWine},
                {label:"Venue",placeholder:"e.g. Gary Danko, San Francisco",val:impVenue,set:setImpVenue},
                {label:"With",placeholder:"e.g. Isabelle M.",val:impWith,set:setImpWith},
              ].map(({label,placeholder,val,set})=>(
                <div key={label} style={{marginBottom:10}}>
                  <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>{label}</div>
                  <input style={{...ss.input}} placeholder={placeholder} value={val} onChange={e=>set(e.target.value)}/>
                </div>
              ))}
              <div style={{marginBottom:10}}>
                <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>Date</div>
                <input type="date" style={{...ss.input,color:C.ink}} value={impDate} onChange={e=>setImpDate(e.target.value)}/>
              </div>
              <div style={{marginBottom:10}}>
                <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>Notes</div>
                <textarea placeholder="What made this pour unforgettable…" style={{minHeight:80}} value={impNotes} onChange={e=>setImpNotes(e.target.value)} spellCheck={true} autoCorrect="on" autoCapitalize="sentences"/>
              </div>
              <div style={{marginBottom:12}}>
                <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"none",marginBottom:5}}>Details</div>
                <textarea placeholder="Aroma, body, finish — any tasting notes worth remembering…" style={{minHeight:70}} value={impDetails} onChange={e=>setImpDetails(e.target.value)} spellCheck={true} autoCorrect="on" autoCapitalize="sentences"/>
              </div>
              <div style={{marginBottom:14}}>
                <div style={{fontFamily:"'Jost',sans-serif",fontSize:10,color:C.mist,letterSpacing:"2px",textTransform:"uppercase",marginBottom:8}}>Rating</div>
                <div style={{display:"flex",gap:8}}>
                  {[1,2,3,4,5].map(n=><span key={n} onClick={()=>setImpRating(n)} style={{fontSize:22,cursor:"pointer",color:n<=impRating?C.coral:C.border}}>★</span>)}
                </div>
              </div>
              {/* SIGNUP_GATE_V1_2026_06_01 — GATE_AUDIT_V1_2026_06_01 */}
              {/* CTA_STD_V1_2026_06_10 */}
              <Btn variant="primary" style={{width:"100%"}} onClick={()=>requireAuth(saveImpression,'save to My Story')}>Save to My Story</Btn>
            </div>

            {/* IMPRESSIONS LIST */}
            {impressions.length===0&&history.length===0&&(
              <div style={{textAlign:"center",padding:"28px 0",fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic",fontSize:15,color:C.mist}}>
                No impressions yet — add your first pour above.
              </div>
            )}
            {impressions.map((h,idx)=>(
              <div key={h.id} style={{...ss.card,padding:"15px 17px",marginBottom:10}}>
                {editingImpId === h.id && editingDraft ? (
                  // ── INLINE EDIT PANEL ───────────────────────────────────────
                  <div className="fade">
                    <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>Edit impression</div>

                    {/* Drink name */}
                    <div style={{marginBottom:10}}>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Drink</div>
                      <input
                        value={editingDraft.wine}
                        onChange={e=>setEditingDraft(d=>({...d,wine:e.target.value}))}
                        placeholder="e.g. Sancerre — Domaine Vacheron"
                        style={{...ss.input,padding:"8px 10px",fontSize:14,width:"100%",fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic"}}
                      />
                    </div>

                    {/* Venue + Date */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                      <div>
                        <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Venue</div>
                        <input
                          value={editingDraft.venue}
                          onChange={e=>setEditingDraft(d=>({...d,venue:e.target.value}))}
                          placeholder="Restaurant or bar"
                          style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                        />
                      </div>
                      <div>
                        <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Date</div>
                        <input
                          value={editingDraft.date}
                          onChange={e=>setEditingDraft(d=>({...d,date:e.target.value}))}
                          placeholder="May 2, 2026"
                          style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                        />
                      </div>
                    </div>

                    {/* With whom */}
                    <div style={{marginBottom:10}}>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Paired swisher</div>
                      <input
                        value={editingDraft.with}
                        onChange={e=>setEditingDraft(d=>({...d,with:e.target.value}))}
                        placeholder="Optional"
                        style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                      />
                    </div>

                    {/* Rating */}
                    <div style={{marginBottom:10}}>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"uppercase",marginBottom:6}}>Rating</div>
                      <div style={{display:"flex",gap:8}}>
                        {[1,2,3,4,5].map(n=>(
                          <span key={n} onClick={()=>setEditingDraft(d=>({...d,rating:n===d.rating?0:n}))} style={{fontSize:22,cursor:"pointer",color:n<=editingDraft.rating?C.coral:C.border}}>★</span>
                        ))}
                      </div>
                    </div>

                    {/* Notes */}
                    <div style={{marginBottom:10}}>
                      <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"none",marginBottom:4}}>Notes</div>
                      <textarea
                        value={editingDraft.notes}
                        onChange={e=>setEditingDraft(d=>({...d,notes:e.target.value}))}
                        placeholder="What made this pour unforgettable…"
                        spellCheck={true}
                        autoCorrect="on"
                        autoCapitalize="sentences"
                        style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%",minHeight:60,fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic"}}
                      />
                    </div>

                    {/* Menu-scan structured fields */}
                    {editingDraft._isMenuScanRow && (
                      <div style={{marginBottom:10,paddingTop:10,borderTop:`1px dashed ${C.border}`}}>
                        <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Drink details</div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 80px",gap:8}}>
                          <select
                            value={editingDraft.category}
                            onChange={e=>setEditingDraft(d=>({...d,category:e.target.value}))}
                            style={{...ss.input,padding:"8px 10px",fontSize:13,background:"#fff"}}
                          >
                            <option value="">Category</option>
                            <option value="wine">Wine</option>
                            <option value="cocktail">Cocktail</option>
                            <option value="spirit">Spirit</option>
                            <option value="unknown">Unknown</option>
                          </select>
                          <input
                            value={editingDraft.sub_category}
                            onChange={e=>setEditingDraft(d=>({...d,sub_category:e.target.value}))}
                            placeholder="red / sparkling / mezcal / etc."
                            style={{...ss.input,padding:"8px 10px",fontSize:13}}
                          />
                          <input
                            type="number"
                            value={editingDraft.price}
                            onChange={e=>setEditingDraft(d=>({...d,price:e.target.value}))}
                            placeholder="$"
                            style={{...ss.input,padding:"8px 10px",fontSize:13}}
                          />
                        </div>
                      </div>
                    )}

                    {/* Bottle-scan structured fields — show whenever any are set OR user wants to add */}
                    {(editingDraft.vintage || editingDraft.region || editingDraft.country || editingDraft.varietal || editingDraft.abv || h.vintage != null || h.region || h.country || h.varietal || h.abv != null) && (
                      <div style={{marginBottom:10,paddingTop:10,borderTop:`1px dashed ${C.border}`}}>
                        <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Bottle details</div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                          <input
                            type="number"
                            value={editingDraft.vintage}
                            onChange={e=>setEditingDraft(d=>({...d,vintage:e.target.value}))}
                            placeholder="Vintage (2021)"
                            style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                          />
                          <input
                            type="number"
                            step="0.1"
                            value={editingDraft.abv}
                            onChange={e=>setEditingDraft(d=>({...d,abv:e.target.value}))}
                            placeholder="ABV (13.5)"
                            style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                          />
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                          <input
                            value={editingDraft.region}
                            onChange={e=>setEditingDraft(d=>({...d,region:e.target.value}))}
                            placeholder="Region"
                            style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                          />
                          <input
                            value={editingDraft.country}
                            onChange={e=>setEditingDraft(d=>({...d,country:e.target.value}))}
                            placeholder="Country"
                            style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                          />
                        </div>
                        <input
                          value={editingDraft.varietal}
                          onChange={e=>setEditingDraft(d=>({...d,varietal:e.target.value}))}
                          placeholder="Varietal / grape"
                          style={{...ss.input,padding:"8px 10px",fontSize:13,width:"100%"}}
                        />
                      </div>
                    )}

                    {/* Save / Cancel */}
                    <div style={{display:"flex",gap:8,marginTop:14}}>
                      {/* GATE_AUDIT_V1_2026_06_01 */}
                      <button
                        style={{...ss.btn,...ss.btnCoral,flex:1,padding:"10px",fontSize:12}}
                        onClick={()=>requireAuth(saveInlineEdit,'save your impression')}
                        disabled={editingSaving}
                      >
                        {editingSaving ? 'Saving…' : 'Save changes'}
                      </button>
                      <button
                        style={{...ss.btn,...ss.btnOut,padding:"10px 14px",fontSize:12}}
                        onClick={cancelInlineEdit}
                        disabled={editingSaving}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:16,fontWeight:400,marginBottom:6}}>{h.wine||"Untitled pour"}</div>
                    <div style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.mist,lineHeight:2}}>
                      {h.venue&&<div>📍 {h.venue}</div>}
                      {h.date&&<div>📅 {h.date}</div>}
                      {h.with&&<div>👤 {h.with}</div>}
                    </div>
                    {h.notes&&<div style={{fontStyle:"italic",fontSize:14,color:C.ink,opacity:0.75,marginTop:7,lineHeight:1.5}}>"{h.notes}"</div>}
                    {(()=>{
                      // Order of precedence:
                      // 1. Bottle scan: any of the dedicated columns (vintage/region/varietal/abv/country) is set → render those as chips
                      // 2. Menu scan: details is a JSON object with source=menu_scan → parse and render chips
                      // 3. Otherwise: details as free text (manual entries)
                      const hasBottleCols = (h.vintage!=null) || (h.region) || (h.country) || (h.varietal) || (h.abv!=null);
                      const chips = [];
                      if (hasBottleCols) {
                        if (h.vintage)   chips.push(String(h.vintage));
                        if (h.varietal)  chips.push(h.varietal);
                        if (h.region)    chips.push(h.region);
                        else if (h.country) chips.push(h.country);
                        if (h.abv!=null) chips.push(`${h.abv}% ABV`);
                      } else if (h.details) {
                        let parsed=null;
                        if(typeof h.details==="string" && h.details.trim().startsWith("{")){
                          try{ parsed=JSON.parse(h.details); }catch{ parsed=null; }
                        }
                        if(parsed && parsed.source==="menu_scan"){
                          if(parsed.category) chips.push(parsed.category);
                          if(parsed.sub_category) chips.push(parsed.sub_category);
                          if(typeof parsed.price==="number") chips.push("$"+parsed.price);
                        } else {
                          return <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,color:C.mist,marginTop:5,lineHeight:1.6}}>{h.details}</div>;
                        }
                      }
                      if (chips.length === 0) return null;
                      return (
                        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
                          {chips.map((c,ci)=>(
                            <span key={ci} style={{...ss.tag,fontSize:10,padding:"3px 9px",background:"rgba(0,180,200,0.08)",color:C.aquaD,border:`1px solid rgba(0,180,200,0.2)`,textTransform:"capitalize"}}>{c}</span>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6,marginLeft:8,flexShrink:0}}>
                    {h.rating>0&&<div style={{color:C.coral,fontSize:12,letterSpacing:1}}>{"★".repeat(h.rating)}</div>}
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                      {/* GATE_AUDIT_V1_2026_06_01 — impression mutations gated */}
                      {/* CTA_STD_V1_2026_06_10 */}
                      <Btn variant="inline" onClick={()=>requireAuth(()=>startInlineEdit(h),'edit your impression')}>Edit</Btn>
                      <Btn variant="inline" onClick={()=>requireAuth(()=>{setImpressions(prev=>prev.filter((_,i)=>i!==idx));dbDeleteImpression(h.id);},'delete your impression')}>Delete</Btn>
                    </div>
                  </div>
                </div>
                )}
              </div>
            ))}
            {history.map(h=>(
              <div key={h.id} style={{...ss.card,padding:"15px 17px",marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:16,fontWeight:400,marginBottom:6}}>{h.wine}</div>
                    <div style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:12,color:C.mist,lineHeight:2}}>
                      <div>📍 {h.venue}</div>
                      <div>📅 {h.date}</div>
                      <div>👤 {h.with}</div>
                      {h.food&&<div>🍽 {h.food}</div>}
                    </div>
                    {h.notes&&<div style={{fontStyle:"italic",fontSize:14,color:C.ink,opacity:0.65,marginTop:7,lineHeight:1.5}}>"{h.notes}"</div>}
                  </div>
                  <div style={{color:C.coral,fontSize:12,letterSpacing:1,flexShrink:0,marginLeft:8}}>{"★".repeat(h.rating)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ══ EVENTS ════════════════════════════════════ */}
        {screen==="main"&&tab==="events"&&(
          <div className="fade" style={{padding:"24px 20px"}}>
            <span style={ss.sl}>swish'd events</span>
            <h2 style={{fontSize:26,fontWeight:400,fontStyle:"italic",lineHeight:1.1,marginBottom:18}}>A Shared Pour - <em style={{color:C.aqua}}>your swisher awaits</em></h2>
            {EVENTS.map(ev=>(
              <div key={ev.id} style={{...ss.card,marginBottom:14,overflow:"hidden"}}>
                <div style={{height:3,background:`linear-gradient(90deg,${C.coral},${C.aqua})`}}/>
                <div style={{padding:"18px 18px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:9}}>
                    <span style={{...ss.tag,background:"rgba(0,180,200,0.08)",color:C.aquaD,border:"1px solid rgba(0,180,200,0.2)"}}>{ev.theme}</span>
                    <span style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.mist,letterSpacing:1}}>{ev.spots}/{ev.maxSpots} left</span>
                  </div>
                  <div style={{fontSize:18,fontWeight:400,marginBottom:2}}>{ev.title}</div>
                  <div style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:11,color:C.mist,marginBottom:4}}>{ev.venue} · {ev.city}</div>
                  <div style={{fontFamily:"'Jost',sans-serif",fontWeight:300,fontSize:11,color:C.mist,marginBottom:10}}>{ev.date} · {ev.time}</div>
                  <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:12}}>
                    {ev.wines.map(w=><span key={w} style={{fontFamily:"'Jost',sans-serif",fontSize:10,background:C.border,color:C.ink,padding:"3px 7px",borderRadius:3}}>{w}</span>)}
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:22,color:C.coral,fontWeight:400}}>${ev.price}</span>
                    {/* SIGNUP_GATE_V1_2026_06_01 — GATE_AUDIT_V1_2026_06_01 coral pill */}
                    {/* CTA_STD_V1_2026_06_10 */}
                    <Btn variant="primary" style={{background:C.coral,color:"#fff",fontFamily:"'Jost',sans-serif",fontSize:9,letterSpacing:2,textTransform:"uppercase",padding:"2px 9px",borderRadius:4,fontWeight:600,height:"auto",width:"auto"}}
                      onClick={()=>requireAuth(()=>setScreen(`payment-${ev.id}`),'reserve a spot')}>
                      I'm In
                    </Btn>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ══ PROFILE ═══════════════════════════════════ */}
        {/* SIGNUP_GATE_V1_2026_06_01 — profile always visible; Save button gated */}
        {screen==="main"&&tab==="profile"&&(
          <ProfileTab history={history} palate={palate} ss={ss} C={C} lookingFor={lookingFor} setLookingFor={setLookingFor} profileData={profileData} setProfileData={setProfileData} profileSaved={profileSaved} saveProfile={async()=>{ if(!(await hasPalateFingerprint())){ setQuizRequiredOpen(true); return false; } requireAuth(saveProfile,'save your profile'); return true; }} quizResult={quizResult} startQuiz={startQuizFresh}/>
        )}
        {/* ══ PAYMENT ═══════════════════════════════════ */}
        {screen&&screen.startsWith("payment-")&&(()=>{
          const evId=screen.replace("payment-","");
          const ev=EVENTS.find(e=>e.id===evId)||EVENTS[0];
          return <PaymentScreen ev={ev} onBack={()=>setScreen("main")} onDone={()=>{setScreen("main");setTab("events");}} ss={ss} C={C} WineGlassGlyph={WineGlassGlyph}/>;
        })()}

      </div>

      {/* ADD TO DIARY OVERLAY */}
      {addWine&&(
        <div style={{position:"fixed",inset:0,background:"rgba(28,31,26,0.72)",zIndex:200,display:"flex",alignItems:"flex-end"}}>
          <div style={{background:C.linen,borderRadius:"20px 20px 0 0",padding:"26px 22px",width:"100%",maxWidth:430,margin:"0 auto"}}>
            <div style={{fontSize:19,fontWeight:400,marginBottom:3}}>Add to diary</div>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:C.mist,marginBottom:18}}>{addWine.name}</div>
            <span style={ss.sl}>Rating</span>
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              {[1,2,3,4,5].map(n=><span key={n} onClick={()=>setDiaryRating(n)} style={{fontSize:22,cursor:"pointer",color:n<=diaryRating?C.coral:C.border}}>★</span>)}
            </div>
            <span style={ss.sl}>Who were you with?</span>
            <input style={{...ss.input,marginBottom:12}} placeholder="Solo, friends, date…" value={diaryWith} onChange={e=>setDiaryWith(e.target.value)}/>
            <span style={ss.sl}>Notes</span>
            <textarea placeholder="What did you think?" value={diaryNote} onChange={e=>setDiaryNote(e.target.value)} style={{marginBottom:14}}/>
            {/* SIGNUP_GATE_V1_2026_06_01 */}
            {/* CTA_FINAL_V1_2026_06_10 — letterSpacing only; layout unchanged */}
            <button style={{...ss.btn,...ss.btnCoral,marginBottom:9,letterSpacing:1}} onClick={()=>requireAuth(saveDiary,'save to your diary')}>Save to diary</button>
            <button style={{...ss.btn,...ss.btnOut}} onClick={()=>setAddWine(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* BOTTOM NAV */}
      {(!isDetail||screen.startsWith("payment-"))&&renderBottomNav()}

      {/* MOOD MODAL */}
      {moodModalIdx!==null&&(
        <div style={{position:"fixed",inset:0,background:"rgba(28,31,38,0.55)",zIndex:1000,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={()=>setMoodModalIdx(null)}>
          <div style={{background:"#fff",borderRadius:"20px 20px 0 0",padding:"24px 20px 32px",width:"100%",maxWidth:430}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:11,letterSpacing:1.5,textTransform:"uppercase",color:C.ink,marginBottom:4}}>What does this moment feel like?</div>
            <div style={{fontFamily:"'Jost',sans-serif",fontStyle:"italic",fontSize:9,color:C.aqua,letterSpacing:1,marginBottom:16}}>tap all that apply</div>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:9,color:C.aqua,letterSpacing:1,marginBottom:16}}>tap all that apply</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:20}}>
              {[
                {label:"romantic",display:"romantic 💕"},
                {label:"social",display:"social 🍸"},
                {label:"celebratory",display:"celebratory 🎊"},
                {label:"relaxed",display:"relaxed 😌"},
                {label:"casual",display:"casual"},
                {label:"exploring",display:"exploring 🧭"},
                {label:"treating myself",display:"treating myself 👑"},
                {label:"unwinding",display:"unwinding 🎶"},
              ].map(({label,display})=>{
                const id=moodModalIdx;
                const sel=(impMood[id]||[]).includes(label);
                return (
                  <button key={label}
                    onClick={()=>setImpMood(prev=>{
                      const arr=Array.isArray(prev[id])?prev[id]:[];
                      return {...prev,[id]:arr.includes(label)?arr.filter(m=>m!==label):[...arr,label]};
                    })}
                    style={{
                      fontFamily:"'Jost',sans-serif",
                      fontSize:10,
                      letterSpacing:0.5,
                      textTransform:"capitalize",
                      fontWeight:sel?600:400,
                      padding:"8px 4px",
                      borderRadius:20,
                      border:`1px solid ${sel?C.coral:C.border}`,
                      background:sel?"rgba(204,34,0,0.06)":"transparent",
                      color:C.ink,
                      cursor:"pointer",
                      transition:"all 0.15s",
                      textAlign:"center",
                    }}
                  >{display}{label==="casual"&&<WineGlassGlyph capH={10} W={7} sw={1}/>}</button>
                );
              })}
              {/* Other pill */}
              <button
                onClick={()=>setMoodOtherOpen(o=>!o)}
                style={{
                  fontFamily:"'Jost',sans-serif",
                  fontSize:10,
                  letterSpacing:0.5,
                  textTransform:"capitalize",
                  fontWeight:moodOtherOpen?600:400,
                  padding:"8px 4px",
                  borderRadius:20,
                  border:`1px solid ${moodOtherOpen?C.coral:C.border}`,
                  background:moodOtherOpen?"rgba(204,34,0,0.06)":"transparent",
                  color:C.ink,
                  cursor:"pointer",
                  transition:"all 0.15s",
                  textAlign:"center",
                }}
              >other ✏️</button>
            </div>
            {/* Other input */}
            {moodOtherOpen&&(
              <div style={{display:"flex",gap:6,marginBottom:12,marginTop:-8}}>
                <input
                  value={moodOther}
                  onChange={e=>setMoodOther(e.target.value)}
                  placeholder="Describe your mood..."
                  style={{...ss.input,flex:1,padding:"6px 10px",fontSize:12}}
                />
                <button
                  onClick={()=>{
                    if(!moodOther.trim()) return;
                    const id=moodModalIdx;
                    setImpMood(prev=>{
                      const arr=Array.isArray(prev[id])?prev[id]:[];
                      return arr.includes(moodOther.trim())?prev:{...prev,[id]:[...arr,moodOther.trim()]};
                    });
                    setMoodOther("");
                    setMoodOtherOpen(false);
                  }}
                  style={{padding:"6px 12px",borderRadius:20,background:C.coral,color:"#fff",border:"none",fontFamily:"'Jost',sans-serif",fontSize:11,cursor:"pointer"}}
                >Add</button>
              </div>
            )}
            <div style={{display:"flex",justifyContent:"center",marginTop:4}}>
              <button
                onClick={()=>{
                  if((impMood[moodModalIdx]||[]).length===0) return;
                  setMoodModalIdx(null);
                  setMoodOtherOpen(false);
                  setMoodOther("");
                  handleSelectToggle(extractedItems.findIndex(x=>x._id===moodModalIdx),true);
                }}
                style={{width:40,height:40,borderRadius:4,background:"transparent",border:`2px solid ${C.coral}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M3 9.5l4 4 8-8" stroke="#1C1F26" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
{/* CAMERA PERMISSION PROMPT */}
      {showCameraPrompt&&(
        <div style={{position:"fixed",inset:0,background:"rgba(28,31,38,0.6)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 24px"}}>
          <div style={{background:"#fff",borderRadius:20,padding:"32px 24px",maxWidth:360,width:"100%",textAlign:"center"}}>
            <div style={{fontSize:40,marginBottom:16}}>📷</div>
            <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic",fontSize:22,color:C.ink,marginBottom:8}}>Snap a menu, discover your pour</div>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:C.mist,lineHeight:1.6,marginBottom:24}}>Swish'd uses your camera to read menus and find drinks tailored to your taste.</div>
            <button
              onClick={async()=>{
                try {
                  const stream=await navigator.mediaDevices.getUserMedia({video:true});
                  stream.getTracks().forEach(t=>t.stop());
                  localStorage.setItem("swishd_camera_ok","true");
                  setCameraPermission(true);
                  setShowCameraPrompt(false);
                  setTab('scan');setScreen('main');
                } catch(e) {
      console.warn('[quiz gate] profile check failed, allowing through', e);
      return true;
    }
              }}
              style={{width:"100%",padding:"13px",borderRadius:40,background:C.coral,color:"#fff",fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:"none",cursor:"pointer",marginBottom:10}}
            >Allow Camera Access</button>
            <button
              onClick={()=>setShowCameraPrompt(false)}
              style={{width:"100%",padding:"13px",borderRadius:40,background:"transparent",color:C.mist,fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:"uppercase",border:`1px solid ${C.border}`,cursor:"pointer"}}
            >Not Now</button>
          </div>
        </div>
      )}
      {/* TOAST */}
      {toast&&(
        <div style={{position:"fixed",bottom:90,left:"50%",transform:"translateX(-50%)",background:toast.col,color:C.linen,padding:"12px 24px",fontFamily:"'Jost',sans-serif",fontSize:13,zIndex:999,whiteSpace:"nowrap",borderRadius:40,boxShadow:`0 6px 24px ${toast.col}55`}}>
          {toast.msg}
        </div>
      )}

      {/* Quiz-required modal — shown instead of the signup modal when saving a profile with no completed quiz */}
      {quizRequiredOpen&&(
        <div style={{position:'fixed',inset:0,background:'rgba(31,34,38,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:24}}>
          <div style={{background:'#EFEDE3',borderRadius:16,maxWidth:360,width:'100%',padding:24,position:'relative',boxShadow:'0 10px 40px rgba(0,0,0,0.15)'}}>
            <IconBtn size={32} type="button" onClick={()=>setQuizRequiredOpen(false)} style={{position:'absolute',top:12,right:12,fontSize:22,color:'#1f2226',lineHeight:1}}>×</IconBtn>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontStyle:'italic',fontWeight:500,fontSize:24,color:'#1f2226',margin:'0 0 8px',textAlign:'center'}}>Complete your palate quiz</h2>
            <p style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:'#1f2226',textAlign:'center',margin:'0 0 24px'}}>Complete your palate quiz to save your profile.</p>
            <Btn variant="primary" type="button" onClick={openQuizFromGate} style={{width:240,display:'block',margin:'0 auto'}}>
              {loadQuizProgress()?'Resume quiz':'Take the quiz'}
            </Btn>
          </div>
        </div>
      )}

      {/* SIGNUP_GATE_V1_2026_06_01 — action-triggered signup modal — GATE_AUDIT_V1_2026_06_01 coral pill CTAs */}
      {signupModalOpen&&(
        <div style={{position:'fixed',inset:0,background:'rgba(31,34,38,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:24}}>
          <div style={{background:'#EFEDE3',borderRadius:16,maxWidth:360,width:'100%',padding:24,position:'relative',boxShadow:'0 10px 40px rgba(0,0,0,0.15)'}}>
            {/* CTA_STD_V1_2026_06_10 */}
            <IconBtn size={32} type="button" onClick={()=>{setSignupModalOpen(false);setPendingAction(null);}} style={{position:'absolute',top:12,right:12,fontSize:22,color:'#1f2226',lineHeight:1}}>×</IconBtn>
            {signupStage==='email'&&(
              <>
                <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontStyle:'italic',fontWeight:500,fontSize:24,color:'#1f2226',margin:'0 0 8px',textAlign:'center'}}>Sign up to continue</h2>
                <p style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:'#1f2226',textAlign:'center',margin:'0 0 24px'}}>
                  {pendingAction?.label?`Sign up to ${pendingAction.label}.`:'Sign up to use this feature.'}
                </p>
                <input type="email" value={signupEmail} onChange={e=>setSignupEmail(e.target.value)} placeholder="your@email.com" style={{width:'100%',boxSizing:'border-box',padding:'12px 14px',border:'1px solid #1f2226',borderRadius:8,background:'#fff',fontFamily:"'Jost',sans-serif",fontSize:14,color:'#1f2226',marginBottom:12}} autoFocus/>
                {signupError&&<p style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:'#c23b0e',margin:'0 0 12px',textAlign:'center'}}>{signupError}</p>}
                {/* CTA_STD_V1_2026_06_10 */}
                <Btn variant="secondary" type="button" disabled={signupLoading||!signupEmail.includes('@')}
                  onClick={async()=>{
                    setSignupLoading(true);setSignupError('');
                    try{
                      const {error}=await supabase.auth.signInWithOtp({email:signupEmail,options:{shouldCreateUser:true}});
                      if(error)throw error;
                      setSignupStage('code');
                    }catch(e){setSignupError(e.message||'Could not send code. Try again.');}
                    finally{setSignupLoading(false);}
                  }}
                  style={{width:240,display:'block',margin:'0 auto',cursor:signupLoading?'default':'pointer',opacity:signupLoading?0.6:1}}
                >{signupLoading?'sending…':'send code'}</Btn>
              </>
            )}
            {signupStage==='code'&&(
              <>
                <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontStyle:'italic',fontWeight:500,fontSize:24,color:'#1f2226',margin:'0 0 8px',textAlign:'center'}}>Check your email</h2>
                <p style={{fontFamily:"'Jost',sans-serif",fontSize:13,color:'#1f2226',textAlign:'center',margin:'0 0 24px'}}>We sent a 6-digit code to {signupEmail}.</p>
                <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={signupCode} onChange={e=>setSignupCode(e.target.value.replace(/\D/g,''))} placeholder="000000" style={{width:'100%',boxSizing:'border-box',padding:'14px',border:'1px solid #1f2226',borderRadius:8,background:'#fff',fontFamily:'monospace',fontSize:26,letterSpacing:12,color:'#1f2226',textAlign:'center',marginBottom:12}} autoFocus/>
                {signupError&&<p style={{fontFamily:"'Jost',sans-serif",fontSize:12,color:'#c23b0e',margin:'0 0 12px',textAlign:'center'}}>{signupError}</p>}
                {/* CTA_STD_V1_2026_06_10 */}
                <Btn variant="secondary" type="button" disabled={signupLoading||signupCode.length!==6}
                  onClick={async()=>{
                    setSignupLoading(true);setSignupError('');
                    try{
                      const {data,error}=await supabase.auth.verifyOtp({email:signupEmail,token:signupCode,type:'email'});
                      if(error)throw error;
                      if(!data.session)throw new Error('No session returned.');
                      setSignupModalOpen(false);
                      const action=pendingAction?.run;
                      setPendingAction(null);setSignupCode('');setSignupEmail('');
                      // Post-verification palate check: an in-state quiz result is saved to the
                      // account; otherwise a profile with no fingerprint is routed to the quiz.
                      const uid=data.session.user.id;
                      if(quizResult){
                        try{
                          await supabase.from('swishd_profiles')
                            .upsert({user_id:uid,palate_fingerprint:quizResult.fingerprint,quiz_selections:quizResult.selections},{onConflict:'user_id'});
                        }catch(e){console.error('[quiz] fingerprint save failed',e);}
                      } else {
                        let hasFp=true;
                        try{
                          const {data:prof}=await supabase.from('swishd_profiles').select('palate_fingerprint').eq('user_id',uid).maybeSingle();
                          hasFp=!!prof?.palate_fingerprint;
                        }catch(e){console.warn('[quiz gate] post-verify profile check failed, allowing through',e);}
                        if(!hasFp){ startQuizFresh(); return; }
                      }
                      if(action)setTimeout(()=>action(),100);
                    }catch(e){setSignupError(e.message||'Invalid code. Try again.');}
                    finally{setSignupLoading(false);}
                  }}
                  style={{width:240,display:'block',margin:'0 auto',cursor:signupLoading?'default':'pointer',opacity:signupLoading?0.6:1}}
                >{signupLoading?'verifying…':'verify'}</Btn>
                {/* CTA_STD_V1_2026_06_10 */}
                <Btn variant="inline" tone="gray" type="button"
                  onClick={async()=>{
                    setSignupLoading(true);setSignupError('');
                    try{await supabase.auth.signInWithOtp({email:signupEmail,options:{shouldCreateUser:true}});}
                    catch(e){setSignupError('Could not resend.');}
                    finally{setSignupLoading(false);}
                  }}
                  style={{margin:'12px auto 0',display:'block'}}
                >resend code</Btn>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
